/* eslint-disable */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
  useContext,
  useMemo,
} from "react";
import {
  FaStepBackward,
  FaStepForward,
  FaArrowLeft,
  FaArrowRight,
  FaPlay,
  FaPause,
  FaTimes,
  FaUndoAlt,
  FaTh,
} from "react-icons/fa";
import "./Worship.css"; // Import your CSS file here
import SEO from "./components/SEO";
import ActiveSongContext from "./contexts/ActiveSongContext";
import {
  exportCurrentViewAsImage,
  exportCurrentViewAsPpt,
  exportSongsToDocx,
  exportSongsToPdf,
  exportSongsToBookPdf,
  buildSongPayload,
  normaliseParagraph,
  captureElement,
  exportImageCollectionFromDataUrls,
  exportPptFromDataUrls,
} from "./utils/exporters";
import useTouchNavigation from "./utils/useTouchNavigation";

const MAX_ENTIRE_COLUMNS = 12;
const MIN_ENTIRE_FONT_SIZE = 10;
const MIN_ENTIRE_COLUMN_WIDTH = 260;
const MIN_FLOW_COLUMN_WIDTH = 360;
const getDefaultColumnsForMode = (mode) => {
  switch (mode) {
    case "entire":
    case "newsletter":
    case "music":
      return 3;
    case "book":
      return 2;
    default:
      return 3;
  }
};

const THEME_PRESETS = [
  { id: "classic", label: "Classic", background: "#000000", text: "#ffffff" },
  { id: "ocean", label: "Ocean", background: "#0a1f44", text: "#f2f6ff" },
  { id: "sunrise", label: "Sunrise", background: "#fbe6d4", text: "#4a3127" },
  { id: "evergreen", label: "Evergreen", background: "#103a2d", text: "#e6fff4" },
  {
    id: "aurora",
    label: "Aurora Borealis",
    background: "#0f2027",
    text: "#f8f9fa",
    gradient: "linear-gradient(135deg, #0f2027 0%, #203a43 55%, #2c5364 100%)",
  },
  {
    id: "sunset",
    label: "Golden Sunset",
    background: "#ff7e5f",
    text: "#fffaf3",
    gradient: "linear-gradient(140deg, #ff7e5f 0%, #feb47b 100%)",
  },
  {
    id: "royal",
    label: "Royal Velvet",
    background: "#1f1c2c",
    text: "#f0e9ff",
    gradient: "linear-gradient(145deg, #1f1c2c 0%, #928dab 100%)",
  },
  {
    id: "dawn",
    label: "Forest Dawn",
    background: "#102a1f",
    text: "#defce5",
    gradient: "linear-gradient(140deg, #0f1e13 0%, #1f5337 60%, #4caf50 100%)",
  },
  {
    id: "deep-space",
    label: "Deep Space",
    background: "#000000",
    text: "#ffffff",
    gradient: "linear-gradient(135deg, #000000 0%, #434343 100%)",
  },
  {
    id: "sweet-morning",
    label: "Sweet Morning",
    background: "#FFC371",
    text: "#4a3127",
    gradient: "linear-gradient(135deg, #FFC371 0%, #FF5F6D 100%)",
  },
  {
    id: "lavender",
    label: "Lavender",
    background: "#E6E6FA",
    text: "#333333",
    gradient: "linear-gradient(135deg, #E6E6FA 0%, #D8BFD8 100%)",
  },
  {
    id: "mint-chocolate",
    label: "Mint Chocolate",
    background: "#536976",
    text: "#E6E6FA",
    gradient: "linear-gradient(135deg, #536976 0%, #292E49 100%)",
  },
  {
    id: "rose-water",
    label: "Rose Water",
    background: "#E55D87",
    text: "#F0E9FF",
    gradient: "linear-gradient(135deg, #E55D87 0%, #5FC3E4 100%)",
  },
  {
    id: "blue-lagoon",
    label: "Blue Lagoon",
    background: "#43C6AC",
    text: "#F8F9FA",
    gradient: "linear-gradient(135deg, #43C6AC 0%, #191654 100%)",
  },
  {
    id: "pacific-glow",
    label: "Pacific Glow",
    background: "#021427",
    text: "#e4f7ff",
    gradient: "linear-gradient(180deg, #021427 0%, #07406e 55%, #0a7ca2 100%)",
    waveLayers: [
      {
        id: "pacific-glow-layer-1",
        top: "48%",
        height: "60%",
        pattern: "radial-gradient(circle at 27% 60%, rgba(143, 212, 255, 0.48) 0%, rgba(143, 212, 255, 0) 67%)",
        backgroundSize: "150% 140%",
        duration: 38,
        delay: 0,
        opacity: 0.45,
        blur: 42,
      },
      {
        id: "pacific-glow-layer-2",
        top: "54%",
        height: "60%",
        pattern: "radial-gradient(circle at 65% 55%, rgba(65, 170, 255, 0.35) 0%, rgba(65, 170, 255, 0) 70%)",
        backgroundSize: "140% 130%",
        duration: 48,
        delay: 8,
        opacity: 0.35,
        blur: 56,
      },
      {
        id: "pacific-glow-layer-3",
        top: "38%",
        height: "80%",
        pattern: "radial-gradient(circle at 45% 45%, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 65%)",
        backgroundSize: "160% 150%",
        duration: 66,
        delay: 14,
        opacity: 0.25,
        blur: 80,
      },
    ],
  },
  {
    id: "coral-surf",
    label: "Coral Surf",
    background: "#1d0328",
    text: "#fff2eb",
    gradient: "linear-gradient(160deg, #1d0328 0%, #4f0c4a 65%, #f05a5a 100%)",
    waveLayers: [
      {
        id: "coral-surf-layer-1",
        top: "60%",
        height: "46%",
        pattern: "radial-gradient(circle at 20% 70%, rgba(255, 143, 128, 0.45) 0%, rgba(255, 143, 128, 0) 70%)",
        backgroundSize: "150% 120%",
        duration: 36,
        delay: 0,
        opacity: 0.5,
        blur: 36,
      },
      {
        id: "coral-surf-layer-2",
        top: "52%",
        height: "55%",
        pattern: "radial-gradient(circle at 60% 60%, rgba(255, 201, 160, 0.35) 0%, rgba(255, 201, 160, 0) 70%)",
        backgroundSize: "155% 130%",
        duration: 50,
        delay: 6,
        opacity: 0.35,
        blur: 48,
      },
      {
        id: "coral-surf-layer-3",
        top: "40%",
        height: "70%",
        pattern: "radial-gradient(circle at 40% 45%, rgba(255, 255, 255, 0.28) 0%, rgba(255, 255, 255, 0) 70%)",
        backgroundSize: "170% 150%",
        duration: 62,
        delay: 12,
        opacity: 0.22,
        blur: 72,
      },
    ],
  },
  {
    id: "aurora-tide",
    label: "Aurora Tide",
    background: "#01050a",
    text: "#c6ffe4",
    gradient: "linear-gradient(180deg, #01050a 0%, #052c46 45%, #1a6a90 80%, #39e2d1 100%)",
    waveLayers: [
      {
        id: "aurora-tide-layer-1",
        top: "50%",
        height: "60%",
        pattern: "radial-gradient(circle at 30% 60%, rgba(63, 255, 213, 0.32) 0%, rgba(63, 255, 213, 0) 60%)",
        backgroundSize: "150% 130%",
        duration: 34,
        delay: 1,
        opacity: 0.38,
        blur: 50,
      },
      {
        id: "aurora-tide-layer-2",
        top: "55%",
        height: "55%",
        pattern: "radial-gradient(circle at 65% 55%, rgba(94, 214, 255, 0.3) 0%, rgba(94, 214, 255, 0) 65%)",
        backgroundSize: "145% 120%",
        duration: 46,
        delay: 7,
        opacity: 0.28,
        blur: 60,
      },
      {
        id: "aurora-tide-layer-3",
        top: "35%",
        height: "75%",
        pattern: "radial-gradient(circle at 45% 35%, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0) 70%)",
        backgroundSize: "165% 150%",
        duration: 58,
        delay: 13,
        opacity: 0.2,
        blur: 70,
      },
    ],
  },
  {
    id: "saffron-surf",
    label: "Saffron Surf",
    background: "#211217",
    text: "#ffe9cf",
    gradient: "linear-gradient(160deg, #211217 0%, #4e1b2c 60%, #f39c60 100%)",
    waveLayers: [
      {
        id: "saffron-surf-layer-1",
        top: "60%",
        height: "45%",
        pattern: "radial-gradient(circle at 30% 70%, rgba(255, 179, 102, 0.35) 0%, rgba(255, 179, 102, 0) 70%)",
        backgroundSize: "150% 120%",
        duration: 36,
        delay: 2,
        opacity: 0.5,
        blur: 34,
      },
      {
        id: "saffron-surf-layer-2",
        top: "55%",
        height: "65%",
        pattern: "radial-gradient(circle at 70% 65%, rgba(255, 130, 75, 0.3) 0%, rgba(255, 130, 75, 0) 75%)",
        backgroundSize: "145% 130%",
        duration: 44,
        delay: 8,
        opacity: 0.35,
        blur: 48,
      },
      {
        id: "saffron-surf-layer-3",
        top: "40%",
        height: "70%",
        pattern: "radial-gradient(circle at 45% 40%, rgba(255, 215, 179, 0.25) 0%, rgba(255, 215, 179, 0) 70%)",
        backgroundSize: "165% 150%",
        duration: 60,
        delay: 14,
        opacity: 0.25,
        blur: 70,
      },
    ],
  },
  {
    id: "midnight-reef",
    label: "Midnight Reef",
    background: "#010b16",
    text: "#b4d5ff",
    gradient: "linear-gradient(180deg, #010b16 0%, #071b32 55%, #1b3a6b 100%)",
    waveLayers: [
      {
        id: "midnight-reef-layer-1",
        top: "55%",
        height: "60%",
        pattern: "radial-gradient(circle at 25% 65%, rgba(130, 197, 255, 0.35) 0%, rgba(130, 197, 255, 0) 70%)",
        backgroundSize: "150% 130%",
        duration: 38,
        delay: 0,
        opacity: 0.45,
        blur: 44,
      },
      {
        id: "midnight-reef-layer-2",
        top: "48%",
        height: "65%",
        pattern: "radial-gradient(circle at 60% 60%, rgba(72, 140, 255, 0.28) 0%, rgba(72, 140, 255, 0) 70%)",
        backgroundSize: "145% 125%",
        duration: 46,
        delay: 6,
        opacity: 0.32,
        blur: 52,
      },
      {
        id: "midnight-reef-layer-3",
        top: "35%",
        height: "80%",
        pattern: "radial-gradient(circle at 50% 40%, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0) 70%)",
        backgroundSize: "170% 150%",
        duration: 60,
        delay: 12,
        opacity: 0.22,
        blur: 80,
      },
    ],
  },
];

const AnimatedWaveBackdrop = ({ layers }) => {
  if (!layers || !layers.length) return null;
  return (
    <div className="worship-wave-backdrop" aria-hidden="true">
      {layers.map((layer) => (
        <div
          key={layer.id}
          className="worship-wave-layer"
          style={{
            top: layer.top || "50%",
            height: layer.height || "55%",
            opacity: layer.opacity ?? 0.35,
            backgroundImage: layer.pattern,
            backgroundSize: layer.backgroundSize || "130% 120%",
            backgroundRepeat: layer.backgroundRepeat || "no-repeat",
            animationDuration: `${layer.duration || 40}s`,
            animationDelay: `${layer.delay || 0}s`,
            mixBlendMode: layer.mixBlendMode || "screen",
            filter: layer.blur ? `blur(${layer.blur}px)` : undefined,
          }}
        />
      ))}
    </div>
  );
};

const FONT_OPTIONS = [
  { id: "poppins", label: "Poppins (Default)", value: "'Poppins', 'Helvetica Neue', Arial, sans-serif" },
  { id: "noto-sans", label: "Noto Sans", value: "'Noto Sans', 'Helvetica Neue', Arial, sans-serif" },
  { id: "noto-sans-tamil", label: "Noto Sans Tamil", value: "'Noto Sans Tamil', 'Latha', 'Arial Unicode MS', sans-serif" },
  { id: "noto-sans-malayalam", label: "Noto Sans Malayalam", value: "'Noto Sans Malayalam', 'Karthika', 'Arial Unicode MS', sans-serif" },
  { id: "playfair", label: "Playfair Display", value: "'Playfair Display', 'Georgia', serif" },
  { id: "roboto", label: "Roboto", value: "'Roboto', sans-serif" },
  { id: "lato", label: "Lato", value: "'Lato', sans-serif" },
  { id: "montserrat", label: "Montserrat", value: "'Montserrat', sans-serif" },
  { id: "merriweather", label: "Merriweather", value: "'Merriweather', serif" },
  { id: "source-sans-pro", label: "Source Sans Pro", value: "'Source Sans Pro', sans-serif" },
];

const EMBED_MEDIA_MESSAGE = "gcc-worship-media";
const EMBED_CONTROL_MESSAGE = "gcc-worship-control";
const REMOTE_SLIDE_NEXT_KEYS = new Set([
  "MediaTrackNext",
  "NextTrack",
  "AudioTrackNext",
  "MediaNextTrack",
  "Next",
]);
const REMOTE_SLIDE_PREV_KEYS = new Set([
  "MediaTrackPrevious",
  "PreviousTrack",
  "AudioTrackPrevious",
  "MediaPreviousTrack",
  "Previous",
]);
const SESSION_FOCUS_MESSAGE = "gcc-session-focus";
const SESSION_NAVIGATION_MESSAGE = "gcc-session-navigation";
const SESSION_NAVIGATION_RESPONSE = "gcc-session-navigation-response";

const ALL_CATEGORIES_OPTION = "All Categories";
const ALL_CATALOGS_VALUE = "__all__";
const DEFAULT_CATALOG_DOMAIN = "gcc-catalog.blogspot.com";
const CUSTOM_CATEGORY_OPTION = "Custom Selection";
const CUSTOM_SONGS_PARAM = "customSongs";
const DOMAIN_EVENT = "gcc-domain-change";
const DEFAULT_FONT_SIZE = 50;

const normalizeCatalogDomain = (value) => {
  const trimmed = (value || "").trim();
  if (!trimmed) return DEFAULT_CATALOG_DOMAIN;
  if (/\.blogspot\.com$/i.test(trimmed)) {
    return trimmed.toLowerCase();
  }
  const slug = trimmed
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (!slug) return DEFAULT_CATALOG_DOMAIN;
  return `${slug}.blogspot.com`;
};

const isAllCatalogsParam = (value) =>
  value === ALL_CATALOGS_VALUE || (typeof value === "string" && value.trim().toLowerCase() === "all");

const resolveCatalogDomainParam = (value) => {
  if (isAllCatalogsParam(value)) return ALL_CATALOGS_VALUE;
  if (!value) return null;
  return normalizeCatalogDomain(value);
};

const toShortDomainParam = (value) => {
  if (!value) return "";
  if (value === ALL_CATALOGS_VALUE) return "all";
  return normalizeCatalogDomain(value).replace(/\.blogspot\.com$/i, "");
};

const formatDomainOptionLabel = (domain) => {
  if (domain === ALL_CATALOGS_VALUE) return "All Catalogs";
  const trimmed = (domain || "").trim();
  if (!trimmed) return "Custom Catalog";
  return trimmed.replace(/\.blogspot\.com$/i, "") || trimmed;
};

const normaliseHex = (hex) => {
  if (!hex || typeof hex !== "string") return null;
  const trimmed = hex.trim();
  if (!/^#?[0-9a-f]{3,6}$/i.test(trimmed)) return null;
  const normalized = trimmed.startsWith("#") ? trimmed.slice(1) : trimmed;
  if (normalized.length === 3) {
    return `#${normalized[0]}${normalized[0]}${normalized[1]}${normalized[1]}${normalized[2]}${normalized[2]}`;
  }
  if (normalized.length === 6) {
    return `#${normalized}`;
  }
  return null;
};

const hexToRgb = (hex) => {
  const normalized = normaliseHex(hex);
  if (!normalized) return null;
  const value = Number.parseInt(normalized.slice(1), 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
};

const rgbToHex = (r, g, b) => {
  const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${clamp(r).toString(16).padStart(2, "0")}${clamp(g).toString(16).padStart(2, "0")}${clamp(b)
    .toString(16)
    .padStart(2, "0")}`;
};

const blendHexColors = (base, mix, weight = 0.5) => {
  const baseRgb = hexToRgb(base);
  const mixRgb = hexToRgb(mix);
  if (!baseRgb || !mixRgb) return base;
  const t = Math.max(0, Math.min(1, weight));
  const r = baseRgb.r * (1 - t) + mixRgb.r * t;
  const g = baseRgb.g * (1 - t) + mixRgb.g * t;
  const b = baseRgb.b * (1 - t) + mixRgb.b * t;
  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
};

const lightenHexColor = (hex, amount = 0.2) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const factor = Math.max(0, Math.min(1, amount));
  const r = rgb.r + (255 - rgb.r) * factor;
  const g = rgb.g + (255 - rgb.g) * factor;
  const b = rgb.b + (255 - rgb.b) * factor;
  return rgbToHex(r, g, b);
};

const BOOK_LINES_PER_PAGE = 18;
const BOOK_FONT_SIZE = 25;

const getBookPageStart = (slidesLength, targetIndex = 0, pagesPerView = 2) => {
  if (!slidesLength) return 0;
  const pageSpan = Math.max(1, pagesPerView);
  const totalPages = Math.ceil(slidesLength / pageSpan);
  const desiredPage = Math.floor(targetIndex / pageSpan);
  const boundedPage = Math.max(0, Math.min(Math.max(totalPages - 1, 0), desiredPage));
  return boundedPage * pageSpan;
};

const getBookLastPageStart = (slidesLength, pagesPerView = 2) =>
  getBookPageStart(slidesLength, slidesLength - 1, pagesPerView);

const normalizeSearchText = (value) => {
  if (value === null || value === undefined) return "";
  const str = typeof value === "string" ? value : String(value);
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

const parseCustomSongsParam = (value) => {
  if (!value) return [];
  const entries = value
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
  const seen = new Set();
  return entries.filter((entry) => {
    if (seen.has(entry)) return false;
    seen.add(entry);
    return true;
  });
};

const hexToRgbaString = (hex, alpha = 1) => {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return `rgba(0, 0, 0, ${alpha})`;
  }
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

const toColorWithAlpha = (value, alpha = 1) => {
  if (!value) {
    return `rgba(0, 0, 0, ${alpha})`;
  }
  const trimmed = String(value).trim();
  const rgbMatch = trimmed.match(/^rgba?\(([^)]+)\)$/i);
  if (rgbMatch) {
    const components = rgbMatch[1].split(",").map((segment) => segment.trim()).slice(0, 3);
    if (components.length === 3) {
      const [r, g, b] = components;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }
  return hexToRgbaString(trimmed, alpha);
};

function LyricsSlideshow({ watermarkText = "" }) {
  const defaultTheme = THEME_PRESETS[0] || { id: "classic", label: "Classic", background: "#000000", text: "#ffffff" };
  const initialQueryRef = useRef(null);
  if (!initialQueryRef.current) {
    const searchString = typeof window !== "undefined" ? window.location.search || "" : "";
    const params = new URLSearchParams(searchString);
    const parseNumberParam = (value, fallback) => {
      if (value === null || value === undefined || value === "") return fallback;
      const parsed = Number.parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : fallback;
    };
    const parseBooleanParam = (value, fallback = false) => {
      if (value === null || value === undefined) return fallback;
      const trimmed = String(value).trim().toLowerCase();
      return trimmed === "1" || trimmed === "true" || trimmed === "yes";
    };
    const categoryParam = params.get("category");
    const searchParam = params.get("search");
    const displayParam = params.get("display");
    const themeParam = params.get("theme");
    const songViewModeParam = params.get("songViewMode");
    const customSongsParam = params.get(CUSTOM_SONGS_PARAM);
    const columnsParam = params.get("columns");
    const autoNextParam = params.get("autonext");
    const customSongs = parseCustomSongsParam(customSongsParam);
    const resolvedCategory = customSongs.length
      ? CUSTOM_CATEGORY_OPTION
      : categoryParam
      ? categoryParam.trim()
      : "";
    const themeExists = THEME_PRESETS.some((preset) => preset.id === themeParam);
    const resolvedThemeId =
      themeParam === "custom" || themeExists
        ? themeParam
        : defaultTheme.id;
    const lockSongParam = params.get("lockSong") ?? params.get("staySong") ?? params.get("stay");
    initialQueryRef.current = {
      domain: params.get("domain") || "",
      category: resolvedCategory,
      search: searchParam ? searchParam.trim() : "",
      display: displayParam ? displayParam.toLowerCase() : "slide",
      lines: parseNumberParam(params.get("lines"), 0),
      font: parseNumberParam(params.get("font"), DEFAULT_FONT_SIZE),
      auto: parseNumberParam(params.get("auto"), 0),
      theme: resolvedThemeId,
      bg: params.get("bg") || "",
      text: params.get("text") || "",
      song: params.get("song") || "",
      slide: parseNumberParam(params.get("slide"), 0),
      songViewMode: songViewModeParam || "buttons",
      stayOnSong: parseBooleanParam(lockSongParam, false),
      fontFamily: (() => {
        const requested = params.get("fontFamily");
        if (!requested) return "";
        return FONT_OPTIONS.some((font) => font.id === requested) ? requested : "";
      })(),
      bold: parseBooleanParam(params.get("bold"), false),
      columns: columnsParam ? parseNumberParam(columnsParam, null) : null,
      gradient: parseBooleanParam(params.get("gradient"), false),
      accent: params.get("accent") || "",
      admin: parseBooleanParam(params.get("admin"), false),
      customSongs,
      autoNext: parseBooleanParam(autoNextParam, false),
    };
  }

  const parentOriginRef = useRef("*");
  const isEmbeddedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const embedded = window.parent && window.parent !== window;
    isEmbeddedRef.current = Boolean(embedded);
    if (!embedded) {
      return undefined;
    }
    if (typeof document !== "undefined" && document.referrer) {
      try {
        const referrerOrigin = new URL(document.referrer).origin;
        if (referrerOrigin) {
          parentOriginRef.current = referrerOrigin;
        }
      } catch (error) {
        parentOriginRef.current = "*";
      }
    }
    return undefined;
  }, []);

  const notifyParentMediaState = useCallback((state) => {
    if (!isEmbeddedRef.current || typeof window === "undefined") {
      return;
    }
    const targetOrigin = parentOriginRef.current || "*";
    try {
      window.parent.postMessage({ type: EMBED_MEDIA_MESSAGE, state }, targetOrigin);
    } catch (error) {
      // Ignore cross-origin messaging failures
    }
  }, []);

  useEffect(() => {
    if (!isEmbeddedRef.current) {
      return undefined;
    }
    notifyParentMediaState("waiting");
    return undefined;
  }, [notifyParentMediaState]);
  const sendSessionNavigationResponse = useCallback((requestId, action, handled) => {
    if (!isEmbeddedRef.current || typeof window === "undefined") {
      return;
    }
    const targetOrigin = parentOriginRef.current || "*";
    try {
      window.parent.postMessage(
        {
          type: SESSION_NAVIGATION_RESPONSE,
          payload: { requestId, action, handled },
        },
        targetOrigin,
      );
    } catch (error) {
      // Ignore cross-origin issues
    }
  }, []);
  const initialQuery = initialQueryRef.current;

  const sendSessionFocusRequest = useCallback((target) => {
    if (!isEmbeddedRef.current || typeof window === "undefined") {
      return;
    }
    const targetOrigin = parentOriginRef.current || "*";
    try {
      window.parent.postMessage(
        {
          type: SESSION_FOCUS_MESSAGE,
          payload: { target },
        },
        targetOrigin,
      );
    } catch (error) {
      // ignore
    }
  }, []);

  /**
   * 1) All React States
   */
  const [songs, setSongs] = useState([]);
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [currentSong, setCurrentSong] = useState(null);
  const [customSong, setCustomSong] = useState(null);

  // Blogger feed, categories, searching
  const initialDomainValue = (() => {
    const fallback =
      typeof window !== "undefined"
        ? window.__GCC_DOMAIN__ || DEFAULT_CATALOG_DOMAIN
        : DEFAULT_CATALOG_DOMAIN;
    return (
      resolveCatalogDomainParam(initialQuery.domain) ||
      resolveCatalogDomainParam(fallback) ||
      DEFAULT_CATALOG_DOMAIN
    );
  })();
  const [selectedDomain, setSelectedDomain] = useState(initialDomainValue);
  const [organizations, setOrganizations] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState(initialQuery.search || "");
  const [typeAheadSearching, setTypeAheadSearching] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(initialQuery.category || ALL_CATEGORIES_OPTION);
  const [customSelectedSlugs, setCustomSelectedSlugs] = useState(initialQuery.customSongs || []);
  const [categories, setCategories] = useState([]);
  const [onDemandText, setOnDemandText] = useState("");
  const [onDemandError, setOnDemandError] = useState("");
  const [onDemandLoading, setOnDemandLoading] = useState(false);
  const [onDemandPopupOpen, setOnDemandPopupOpen] = useState(false);
  const [tilesPanelOpen, setTilesPanelOpen] = useState(false);
  const [tilesPanelSearch, setTilesPanelSearch] = useState("");
  const [tilesPanelWidth, setTilesPanelWidth] = useState(420);
  const [tilesPanelPinned, setTilesPanelPinned] = useState(false);
  const [tilesPanelMinimized, setTilesPanelMinimized] = useState(false);
  const [isResizingTilesPanel, setIsResizingTilesPanel] = useState(false);
  const tilesPanelResizeStartX = useRef(0);
  const tilesPanelResizeStartWidth = useRef(tilesPanelWidth);
  const onDemandInputRef = useRef(null);

  // Current song indices & slides
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [paragraphSlides, setParagraphSlides] = useState([]);
  const [lineSlides, setLineSlides] = useState([]);
  const [activeSlides, setActiveSlides] = useState([]);
  const [linesPerSlide, setLinesPerSlide] = useState(Math.max(0, initialQuery.lines || 0));
  const initialLinesPerSlideRef = useRef(Math.max(0, initialQuery.lines || 0));
  const [flowColumnGap, setFlowColumnGap] = useState(32);
  const handleDomainSelect = useCallback((value) => {
    if (value === ALL_CATALOGS_VALUE) {
      setSelectedDomain(value);
      return;
    }
    const normalized = normalizeCatalogDomain(value);
    setSelectedDomain(normalized);
    if (typeof window !== "undefined") {
      const shortValue = toShortDomainParam(normalized);
      window.__GCC_DOMAIN__ = shortValue;
      window.dispatchEvent(new CustomEvent(DOMAIN_EVENT, { detail: shortValue }));
    }
  }, []);

  // YouTube / Musescore
  const [youtubeLinks, setYoutubeLinks] = useState([]);
  const [selectedYouTubeLink, setSelectedYouTubeLink] = useState(null);
  const [musescoreLink, setMusescoreLink] = useState(null);
  const [isYouTubePlaying, setIsYouTubePlaying] = useState(false);

  const CAMERA_SLIDES_MODE = "camera-slides";
  const CAMERA_PRESENT_MODE = "camera-present";
  const CAMERA_VIDEO_MODE = "camera-video";
  // Display & playback
  const validDisplayModes = useMemo(
    () => [
      "slide",
      CAMERA_SLIDES_MODE,
      CAMERA_PRESENT_MODE,
      "video",
      "videoslide",
      "music",
      "presenter",
      "entire",
      "newsletter",
      "book",
    ],
    []
  );
  const normalizedDisplayQuery =
    initialQuery.display === "live" ? CAMERA_SLIDES_MODE : initialQuery.display;
  const initialDisplayMode = validDisplayModes.includes(normalizedDisplayQuery)
    ? normalizedDisplayQuery
    : "slide";
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const preservedSettingsRef = useRef({
    displayMode: initialDisplayMode,
    linesPerSlide: Math.max(0, initialQuery.lines || 0),
    globalFontSize: clamp(initialQuery.font, 10, 150),
    autoAdvanceInterval: Math.max(0, initialQuery.auto || 0),
    selectedCategory: initialQuery.category || ALL_CATEGORIES_OPTION,
  });
  const [displayMode, setDisplayMode] = useState(initialDisplayMode);
  const [globalFontSize, setGlobalFontSize] = useState(clamp(initialQuery.font, 10, 150));
  const [responsiveSlideFontSize, setResponsiveSlideFontSize] = useState(clamp(initialQuery.font, 10, 150));
  const hasColumnQuery = Number.isFinite(initialQuery.columns);
  const [columnPreferenceSource, setColumnPreferenceSource] = useState(
    hasColumnQuery ? "user" : "default",
  );
  const [entireColumnCount, setEntireColumnCount] = useState(() => {
    const initialColumns = hasColumnQuery
      ? clamp(initialQuery.columns, 1, MAX_ENTIRE_COLUMNS)
      : clamp(getDefaultColumnsForMode(initialDisplayMode), 1, MAX_ENTIRE_COLUMNS);
    return initialColumns;
  });
  const [entireColumnsLocked] = useState(true);
  const [entireFontSize, setEntireFontSize] = useState(clamp(initialQuery.font, 10, 150));
  const [newsletterFontSize, setNewsletterFontSize] = useState(() =>
    Math.max(10, Math.round(clamp(initialQuery.font, 10, 150) / 3))
  );
  const [autoEntireColumnCount, setAutoEntireColumnCount] = useState(() => {
    const initialColumns = hasColumnQuery
      ? clamp(initialQuery.columns, 1, MAX_ENTIRE_COLUMNS)
      : clamp(getDefaultColumnsForMode(initialDisplayMode), 1, MAX_ENTIRE_COLUMNS);
    return initialColumns;
  });
  const [currentPlaybackRate, setCurrentPlaybackRate] = useState(1);
  const [exporting, setExporting] = useState(false);
  const playbackRates = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

  // Controls & tile lighting
  const [controlsVisible, setControlsVisible] = useState(false);
  const [topControlsDismissed, setTopControlsDismissed] = useState(false);
  const [bottomNavHidden, setBottomNavHidden] = useState(false);
  const [tileLightModeActive, setTileLightModeActive] = useState(false);
  const SONG_VIEW_MODES = [
    { id: "buttons", label: "Buttons", symbol: "B" },
    { id: "grid", label: "Grid", symbol: "G" },
    { id: "tile", label: "Wide Tile", symbol: "T" },
    { id: "cards", label: "Card", symbol: "C" },
    { id: "list", label: "List", symbol: "L" },
    { id: "carousel", label: "Carousel", symbol: "Co" },
  ];
  const [songViewMode, setSongViewMode] = useState(initialQuery.songViewMode);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const carouselTouchStartRef = useRef({ x: 0, y: 0 });
  const isCustomCategory = selectedCategory === CUSTOM_CATEGORY_OPTION;
  const customSelectedSet = useMemo(() => new Set(customSelectedSlugs), [customSelectedSlugs]);
  const customSelectedSongs = useMemo(() => {
    if (!songs.length || customSelectedSlugs.length === 0) return [];
    const songMap = new Map(songs.map((song) => [slugify(song.title), song]));
    return customSelectedSlugs
      .map((slug) => songMap.get(slug))
      .filter(Boolean);
  }, [songs, customSelectedSlugs]);
  const matchesSearchTokens = useCallback((song, tokens) => {
    if (!tokens.length) return true;
    return tokens.every((token) => {
      const title = song.normalizedTitle || normalizeSearchText(song.title);
      const lyrics = song.normalizedLyrics || normalizeSearchText(song.lyrics.join(" "));
      const categories = song.normalizedCategories || normalizeSearchText(song.categories.join(" "));
      return title.includes(token) || lyrics.includes(token) || categories.includes(token);
    });
  }, []);
  const selectionDisplaySongs = useMemo(() => {
    if (!isCustomCategory) return filteredSongs;
    if (!songs.length) return [];
    const normalizedQuery = normalizeSearchText(searchKeyword);
    const tokens = normalizedQuery.split(/\s+/).filter(Boolean);
    if (!tokens.length) return songs;
    return songs.filter((song) => matchesSearchTokens(song, tokens));
  }, [filteredSongs, isCustomCategory, matchesSearchTokens, searchKeyword, songs]);
  const tileSongs = useMemo(
    () => (isCustomCategory ? selectionDisplaySongs : filteredSongs),
    [filteredSongs, isCustomCategory, selectionDisplaySongs],
  );

  // Auto-advance
  const [autoAdvanceInterval, setAutoAdvanceInterval] = useState(Math.max(0, initialQuery.auto || 0));
  const [lockSongNavigation, setLockSongNavigation] = useState(Boolean(initialQuery.stayOnSong));
  const [autoPlayNext, setAutoPlayNext] = useState(Boolean(initialQuery.autoNext));
  const [randomPlayNext, setRandomPlayNext] = useState(false);
  const autoAdvanceTimerRef = useRef(null);
  const initialSongAppliedRef = useRef(false);
  const pendingSlideRef = useRef(
    Number.isFinite(initialQuery.slide) && initialQuery.slide >= 0 ? initialQuery.slide : null
  );
  const initialDomainRef = useRef(
    resolveCatalogDomainParam(initialQuery.domain) ||
      (typeof window !== "undefined"
        ? resolveCatalogDomainParam(window.__GCC_DOMAIN__ || DEFAULT_CATALOG_DOMAIN)
        : DEFAULT_CATALOG_DOMAIN) ||
      DEFAULT_CATALOG_DOMAIN,
  );
  const initialCategoryRef = useRef((initialQuery.category || "").trim());

  // Keep track of “tried” songs to avoid infinite skipping
  const attemptedSongIndicesRef = useRef(new Set());

  // YouTube players (hidden audio and large video)
  const hiddenAudioPlayerRef = useRef(null);
  const videoModePlayerRef = useRef(null);
  const entireContentRef = useRef(null);
  const newsletterContentRef = useRef(null);
  const pendingPlayRequestRef = useRef(null);
  const isUserSeekingRef = useRef(false);
  const sliderValueRef = useRef(0);
  const slideTouchTargetRef = useRef(null);
  const slideLyricsRef = useRef(null);
  const videoSlideTouchTargetRef = useRef(null);
  const videoSlideLyricsRef = useRef(null);
  const presenterTouchTargetRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);

  // New: track if the user explicitly paused the player.
  const userPausedRef = useRef(false);

  // New: for tracking audio playback progress.
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioTimeRef = useRef(0);
  const audioTimerRef = useRef(null);
  const progressAutoAdvanceRef = useRef(null);
  const isCameraSlidesMode = (mode) => mode === CAMERA_SLIDES_MODE;
  const isCameraPresentMode = (mode) => mode === CAMERA_PRESENT_MODE;
  const isCameraVideoMode = (mode) => mode === CAMERA_VIDEO_MODE;
  const isSlideLikeMode = (mode) =>
    mode === "slide" || mode === "videoslide" || isCameraSlidesMode(mode) || isCameraPresentMode(mode);
  const isVideoLikeMode = (mode) =>
    mode === "video" || mode === "videoslide" || isCameraVideoMode(mode);

  // For tile lighting
  const tileLightIntervalRef = useRef(null);
  const exportContainerRef = useRef(null);

  // Help modal
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const { setActiveSongTitle, setActiveCatalogTitle } = useContext(ActiveSongContext);

  // Theme selection
  const initialThemePreset = THEME_PRESETS.find((preset) => preset.id === initialQuery.theme) || defaultTheme;
  const initialThemeId = initialQuery.theme || defaultTheme.id;
  const initialBackgroundColor =
    initialThemeId === "custom"
      ? initialQuery.bg || defaultTheme.background
      : initialThemePreset.background;
  const initialTextColor =
    initialThemeId === "custom"
      ? initialQuery.text || defaultTheme.text
      : initialThemePreset.text;
  const initialFontId = FONT_OPTIONS.some((font) => font.id === initialQuery.fontFamily)
    ? initialQuery.fontFamily
    : FONT_OPTIONS[0].id;
  const initialBoldState = Boolean(initialQuery.bold);
  const initialCustomAccent = (() => {
    if (initialThemeId !== "custom") {
      return lightenHexColor(initialBackgroundColor, 0.35);
    }
    const suppliedAccent = normaliseHex(initialQuery.accent);
    if (suppliedAccent) return suppliedAccent;
    return lightenHexColor(initialBackgroundColor, 0.35);
  })();
  const [selectedThemeId, setSelectedThemeId] = useState(initialThemeId);
  const [customBackgroundColor, setCustomBackgroundColor] = useState(initialBackgroundColor);
  const [customTextColor, setCustomTextColor] = useState(initialTextColor);
  const [selectedFontId, setSelectedFontId] = useState(initialFontId);
  const [lyricsBold, setLyricsBold] = useState(initialBoldState);
  const [customGradientEnabled, setCustomGradientEnabled] = useState(
    initialThemeId === "custom" ? Boolean(initialQuery.gradient) : false
  );
  const [customGradientAccent, setCustomGradientAccent] = useState(initialCustomAccent);
  const [shareCopied, setShareCopied] = useState(false);
  const shareCopyTimeoutRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1920
  );
  const isTabletOrBelow = viewportWidth <= 1024;
  const isMobileViewport = viewportWidth <= 640;
  const [exportPreviewMode, setExportPreviewMode] = useState(false);
  const exportHeaderRef = useRef(null);
  const [exportHeaderHeight, setExportHeaderHeight] = useState(0);
  const suppressPlaybackRef = useRef(false);
  const liveStateRef = useRef({});
  const [cameraStreamError, setCameraStreamError] = useState(null);
  const stopCameraStream = useCallback(() => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }
  }, []);
  useEffect(() => {
    if (!tileSongs.length) {
      setCarouselIndex(0);
      return;
    }
    setCarouselIndex((prev) => Math.min(prev, tileSongs.length - 1));
  }, [tileSongs.length]);

  useEffect(() => {
    if (songViewMode === "carousel") {
      setCarouselIndex((prev) => Math.min(prev, Math.max(tileSongs.length - 1, 0)));
    }
  }, [songViewMode, tileSongs.length]);
  const cameraSlidesActive = isCameraSlidesMode(displayMode);
  const cameraPresentActive = isCameraPresentMode(displayMode);
  const cameraVideoActive = isCameraVideoMode(displayMode);
  const cameraModeActive =
    Boolean(currentSong) && (cameraSlidesActive || cameraPresentActive || cameraVideoActive);
  useEffect(() => {
    if (!cameraModeActive) {
      stopCameraStream();
      setCameraStreamError(null);
      return undefined;
    }
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.getUserMedia !== "function"
    ) {
      setCameraStreamError("Camera access is not supported in this browser.");
      return undefined;
    }
    let active = true;
    setCameraStreamError(null);
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" }, audio: false })
      .then((stream) => {
        if (!active) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        cameraStreamRef.current = stream;
        if (cameraVideoRef.current) {
          cameraVideoRef.current.srcObject = stream;
        }
      })
      .catch((error) => {
        if (!active) return;
        console.error("Camera access failed:", error);
        setCameraStreamError(error?.message || "Unable to access the camera.");
      });
    return () => {
      active = false;
      stopCameraStream();
    };
  }, [cameraModeActive, stopCameraStream]);
  const musicFontSize = useMemo(() => {
    return globalFontSize / 2;
  }, [globalFontSize]);
  const activePreset = useMemo(
    () => THEME_PRESETS.find((preset) => preset.id === selectedThemeId),
    [selectedThemeId]
  );
  const activeTheme = useMemo(() => {
    if (selectedThemeId === "custom" || !activePreset) {
      const accent = normaliseHex(customGradientAccent) || lightenHexColor(customBackgroundColor, 0.35);
      const gradient = customGradientEnabled
        ? `linear-gradient(145deg, ${customBackgroundColor} 0%, ${accent} 100%)`
        : null;
      return {
        background: customBackgroundColor,
        text: customTextColor,
        gradient,
      };
    }
    return {
      background: activePreset.background,
      text: activePreset.text,
      gradient: activePreset.gradient || null,
    };
  }, [
    selectedThemeId,
    activePreset,
    customBackgroundColor,
    customTextColor,
    customGradientEnabled,
    customGradientAccent,
  ]);
  const activeFont = useMemo(
    () => FONT_OPTIONS.find((font) => font.id === selectedFontId) || FONT_OPTIONS[0],
    [selectedFontId]
  );
  const showTiles = !currentSong;
  const showLyricsBox = currentSong && !cameraVideoActive && displayMode !== "presenter";
  const slideLikeMode = isSlideLikeMode(displayMode);
  const videoLikeMode = isVideoLikeMode(displayMode);
  const currentSlideContent = activeSlides[currentSlideIndex] || "";
  const adjustSlideFontToViewport = useCallback(() => {
    const isOverlayMode = displayMode === "videoslide";
    const container = isOverlayMode ? videoSlideTouchTargetRef.current : slideTouchTargetRef.current;
    const slideElement = isOverlayMode ? videoSlideLyricsRef.current : slideLyricsRef.current;
    if (!container || !slideElement) {
      if (responsiveSlideFontSize !== globalFontSize) {
        setResponsiveSlideFontSize(globalFontSize);
      }
      return;
    }
    if (!slideLikeMode || !showLyricsBox) {
      if (responsiveSlideFontSize !== globalFontSize) {
        setResponsiveSlideFontSize(globalFontSize);
      }
      return;
    }
    if (!isMobileViewport) {
      if (responsiveSlideFontSize !== globalFontSize) {
        setResponsiveSlideFontSize(globalFontSize);
      }
      return;
    }
    const bounds =
      isOverlayMode && videoSlideLyricsRef.current && videoSlideLyricsRef.current.parentElement
        ? videoSlideLyricsRef.current.parentElement
        : container;
    const minSize = 16;
    const step = 2;
    const maxIterations = 40;
    const availableHeight = Math.max(bounds.clientHeight - 16, 50);
    const availableWidth = Math.max(bounds.clientWidth - 16, 50);
    const mobileStartingSize = Math.min(globalFontSize * (isOverlayMode ? 0.6 : 0.75), 84);
    let nextSize = Math.min(isMobileViewport ? mobileStartingSize : globalFontSize, 72);
    slideElement.style.fontSize = `${nextSize}px`;
    let iterations = 0;
    while (
      (slideElement.scrollHeight > availableHeight || slideElement.scrollWidth > availableWidth) &&
      nextSize > minSize &&
      iterations < maxIterations
    ) {
      nextSize = Math.max(minSize, nextSize - step);
      slideElement.style.fontSize = `${nextSize}px`;
      iterations += 1;
    }
    if (nextSize !== responsiveSlideFontSize) {
      setResponsiveSlideFontSize(nextSize);
    }
  }, [
    displayMode,
    globalFontSize,
    isMobileViewport,
    responsiveSlideFontSize,
    showLyricsBox,
    slideLikeMode,
    videoSlideLyricsRef,
    videoSlideTouchTargetRef,
  ]);

  useLayoutEffect(() => {
    adjustSlideFontToViewport();
  }, [
    adjustSlideFontToViewport,
    currentSlideIndex,
    currentSong?.title,
    currentSlideContent,
    lyricsBold,
    activeTheme.text,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleViewportChange = () => {
      adjustSlideFontToViewport();
    };
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("orientationchange", handleViewportChange);
    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("orientationchange", handleViewportChange);
    };
  }, [adjustSlideFontToViewport]);
  const exportHeaderStyle = useMemo(() => {
    const defaultDark = {
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      color: "#ffffff",
      border: "1px solid rgba(255, 255, 255, 0.3)",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.35)",
      textShadow: "0 2px 6px rgba(0, 0, 0, 0.45)",
    };
    const rgb = hexToRgb(activeTheme.text);
    if (!rgb) {
      return defaultDark;
    }
    const brightness = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    if (brightness > 0.6) {
      return defaultDark;
    }
    return {
      backgroundColor: "rgba(255, 255, 255, 0.88)",
      color: "#111111",
      border: "1px solid rgba(0, 0, 0, 0.18)",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
      textShadow: "none",
    };
  }, [activeTheme.text]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const rootStyle = document.documentElement.style;
    const previousBgColor = document.body.style.backgroundColor;
    const previousBgImage = document.body.style.backgroundImage;
    const previousColor = document.body.style.color;
    rootStyle.setProperty("--lyrics-background", activeTheme.background);
    rootStyle.setProperty("--lyrics-text", activeTheme.text);
    rootStyle.setProperty("--lyrics-font", activeFont.value);
    document.body.style.backgroundColor = activeTheme.background;
    document.body.style.backgroundImage = activeTheme.gradient || "";
    document.body.style.color = activeTheme.text;
    return () => {
      document.body.style.backgroundColor = previousBgColor;
      document.body.style.backgroundImage = previousBgImage;
      document.body.style.color = previousColor;
      rootStyle.removeProperty("--lyrics-background");
      rootStyle.removeProperty("--lyrics-text");
      rootStyle.removeProperty("--lyrics-font");
    };
  }, [activeTheme.background, activeTheme.gradient, activeTheme.text, activeFont.value]);

  useEffect(() => {
    if (!customGradientEnabled) return;
    const sanitized = normaliseHex(customGradientAccent) || lightenHexColor(customBackgroundColor, 0.35);
    if (sanitized !== customGradientAccent) {
      setCustomGradientAccent(sanitized);
    }
  }, [customGradientEnabled, customGradientAccent, customBackgroundColor]);

  useEffect(() => {
    if (!exportPreviewMode) {
      setExportHeaderHeight(0);
      return;
    }
    const measure = () => {
      if (exportHeaderRef.current) {
        setExportHeaderHeight(exportHeaderRef.current.offsetHeight + 24);
      }
    };
    const raf = window.requestAnimationFrame(measure);
    return () => window.cancelAnimationFrame(raf);
  }, [exportPreviewMode, currentSong?.title]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleViewportResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleViewportResize);
    return () => window.removeEventListener("resize", handleViewportResize);
  }, []);

  useEffect(() => {
    return () => {
      if (shareCopyTimeoutRef.current) {
        clearTimeout(shareCopyTimeoutRef.current);
        shareCopyTimeoutRef.current = null;
      }
    };
  }, []);

  liveStateRef.current = {
    currentSong,
    currentSongIndex,
    displayMode,
    currentSlideIndex,
    exportPreviewMode,
    filteredSongsLength: filteredSongs.length,
    controlsVisible,
    topControlsDismissed,
  };

  /**
   * 3) Arrow functions and helpers
   */

  const getRandomSongIndex = useCallback((length, excludeIndex, attempted) => {
    if (length <= 1) return 0;
    const options = [];
    for (let i = 0; i < length; i += 1) {
      if (i !== excludeIndex && !attempted.has(i)) {
        options.push(i);
      }
    }
    if (!options.length) {
      return (excludeIndex + 1) % length;
    }
    return options[Math.floor(Math.random() * options.length)];
  }, []);

  // nextSong: load the next song.
  const nextSong = useCallback(() => {
    if (lockSongNavigation) {
      return;
    }
    if (!filteredSongs.length) return;
    if (attemptedSongIndicesRef.current.size >= filteredSongs.length) {
      attemptedSongIndicesRef.current = new Set();
      console.warn("No playable songs found. Stopping playback.");
      setIsYouTubePlaying(false);
      return;
    }
    let nextIndex;
    if (randomPlayNext && filteredSongs.length > 1) {
      nextIndex = getRandomSongIndex(
        filteredSongs.length,
        currentSongIndex,
        attemptedSongIndicesRef.current,
      );
    } else {
      nextIndex = currentSongIndex + 1;
      if (nextIndex >= filteredSongs.length) nextIndex = 0;
    }
    if (attemptedSongIndicesRef.current.has(nextIndex)) {
      setCurrentSongIndex(nextIndex);
      nextSong();
    } else {
      loadSong(nextIndex);
    }
  }, [
    filteredSongs,
    currentSongIndex,
    lockSongNavigation,
    randomPlayNext,
    getRandomSongIndex,
  ]);

  // prevSong: load the previous song.
  const prevSong = useCallback(() => {
    if (lockSongNavigation) {
      return;
    }
    if (!filteredSongs.length) return;
    let idx = currentSongIndex - 1;
    if (idx < 0) idx = filteredSongs.length - 1;
    loadSong(idx);
  }, [filteredSongs, currentSongIndex, lockSongNavigation]);

  // toggleControls: show/hide controls.
  const toggleControls = useCallback(() => {
    setControlsVisible((prev) => {
      const next = !prev;
      if (next) {
        setTopControlsDismissed(false);
        setTypeAheadSearching(false);
      }
      return next;
    });
  }, [setTopControlsDismissed, setTypeAheadSearching]);

  useEffect(() => {
    if (controlsVisible) {
      setBottomNavHidden(false);
    }
  }, [controlsVisible]);

  const dismissTopControls = useCallback(() => {
    setTopControlsDismissed(true);
    setControlsVisible(false);
    setTypeAheadSearching(false);
  }, [setTopControlsDismissed, setControlsVisible, setTypeAheadSearching]);

  const handleThemeSelectChange = useCallback((event) => {
    const newThemeId = event.target.value;
    setSelectedThemeId(newThemeId);
    if (newThemeId !== "custom") {
      const preset = THEME_PRESETS.find((preset) => preset.id === newThemeId);
      if (preset) {
        setCustomBackgroundColor(preset.background);
        setCustomTextColor(preset.text);
      }
      setCustomGradientEnabled(false);
    }
  }, []);

  const handleBackgroundColorChange = useCallback(
    (event) => {
      const nextColor = event.target.value;
      setCustomBackgroundColor(nextColor);
      if (customGradientEnabled) {
        const suggestedAccent = lightenHexColor(nextColor, 0.35);
        setCustomGradientAccent(suggestedAccent);
      }
      if (selectedThemeId !== "custom") {
        setSelectedThemeId("custom");
      }
    },
    [selectedThemeId, customGradientEnabled]
  );

  const handleTextColorChange = useCallback(
    (event) => {
      const nextColor = event.target.value;
      setCustomTextColor(nextColor);
      if (selectedThemeId !== "custom") {
        setSelectedThemeId("custom");
      }
    },
    [selectedThemeId]
  );

  const handleFontChange = useCallback((event) => {
    const nextFont = event.target.value;
    setSelectedFontId(nextFont);
  }, []);

  const toggleLyricsBold = useCallback(() => {
    setLyricsBold((prev) => !prev);
  }, []);

  const handleColumnChange = useCallback((event) => {
    const value = Number.parseInt(event.target.value, 10);
    if (Number.isFinite(value)) {
      const nextColumns = Math.min(Math.max(value, 1), MAX_ENTIRE_COLUMNS);
      setColumnPreferenceSource("user");
      setEntireColumnCount(nextColumns);
      setAutoEntireColumnCount(nextColumns);
    }
  }, []);

  const adjustEntireColumnsWithKeyboard = useCallback(
    (delta) => {
      setEntireColumnCount((prev) => {
        const next = Math.min(Math.max((prev || 1) + delta, 1), MAX_ENTIRE_COLUMNS);
        setColumnPreferenceSource("user");
        setAutoEntireColumnCount(next);
        return next;
      });
    },
    []
  );

  const adjustLineCountWithKeyboard = useCallback(
    (delta) => {
      setLinesPerSlide((prev) => {
        const next = Math.min(Math.max(prev + delta, 0), 25);
        return next;
      });
    },
    []
  );

  const adjustLinesOrColumns = useCallback(
    (delta) => {
      if (
        ["slide", "presenter"].includes(displayMode) ||
        isCameraSlidesMode(displayMode) ||
        isCameraPresentMode(displayMode)
      ) {
        adjustLineCountWithKeyboard(delta);
      } else if (["music", "entire", "newsletter", "book"].includes(displayMode)) {
        adjustEntireColumnsWithKeyboard(delta);
      } else {
        adjustLineCountWithKeyboard(delta);
      }
    },
    [adjustEntireColumnsWithKeyboard, adjustLineCountWithKeyboard, displayMode]
  );

  const handleCustomGradientToggle = useCallback(
    (event) => {
      const enabled = event.target.checked;
      setCustomGradientEnabled(enabled);
      if (enabled) {
        const accent = normaliseHex(customGradientAccent) || lightenHexColor(customBackgroundColor, 0.35);
        setCustomGradientAccent(accent);
      }
    },
    [customGradientAccent, customBackgroundColor]
  );

  const handleCustomGradientAccentChange = useCallback((event) => {
    setCustomGradientAccent(event.target.value);
  }, []);

  const handleShareLinkCopy = useCallback(async () => {
    if (typeof window === "undefined") return;
    const shareUrl = window.location.href;
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
        await navigator.clipboard.writeText(shareUrl);
        setShareCopied(true);
        if (shareCopyTimeoutRef.current) {
          clearTimeout(shareCopyTimeoutRef.current);
        }
        shareCopyTimeoutRef.current = setTimeout(() => {
          setShareCopied(false);
          shareCopyTimeoutRef.current = null;
        }, 2000);
      } else {
        throw new Error("Clipboard API unavailable");
      }
    } catch (error) {
      if (shareCopyTimeoutRef.current) {
        clearTimeout(shareCopyTimeoutRef.current);
        shareCopyTimeoutRef.current = null;
      }
      setShareCopied(false);
      window.prompt("Copy this link", shareUrl);
    }
  }, []);

  useEffect(() => {
    const headerLogo = document.querySelector(".header-logo");
    if (!headerLogo) return undefined;
    const previousCursor = headerLogo.style.cursor;
    headerLogo.style.cursor = "pointer";
    const handleToggle = (event) => {
      event.preventDefault();
      toggleControls();
    };
    headerLogo.addEventListener("click", handleToggle);
    return () => {
      headerLogo.removeEventListener("click", handleToggle);
      headerLogo.style.cursor = previousCursor;
    };
  }, [toggleControls]);

  useEffect(() => {
    const hamburger = document.querySelector(".menu-toggle");
    if (hamburger) {
      hamburger.style.visibility = controlsVisible ? "visible" : "hidden";
    }
    if (typeof document !== "undefined" && document.body) {
      document.body.classList.toggle("controls-hidden", !controlsVisible);
    }
  }, [controlsVisible]);

  useEffect(() => {
    if (typeof document === "undefined" || !document.body) return undefined;
    document.body.classList.add("worship-fullscreen");
    return () => {
      document.body.classList.remove("worship-fullscreen");
    };
  }, []);

  // createVideoPlayer: initialize or reload the large YouTube player.
  const createVideoPlayer = useCallback(
    (videoID, startSeconds = 0) => {
      if (!window.YT) return;
      if (!videoModePlayerRef.current) {
        videoModePlayerRef.current = new window.YT.Player("video-mode-player", {
          height: "100%",
          width: "100%",
          videoId: videoID,
          events: {
            onReady: () => {
              setIsYouTubePlaying(true);
              notifyParentMediaState("playing");
            },
            onStateChange: (e) => {
              if (e.data === window.YT.PlayerState.PLAYING) {
                setIsYouTubePlaying(true);
                notifyParentMediaState("playing");
              } else if (e.data === window.YT.PlayerState.ENDED) {
                if (userPausedRef.current) {
                  userPausedRef.current = false;
                  setIsYouTubePlaying(false);
                  notifyParentMediaState("paused");
                } else {
                  notifyParentMediaState("ended");
                  setIsYouTubePlaying(false);
                  if (autoPlayNext) {
                    nextSong();
                  }
                }
              } else {
                setIsYouTubePlaying(false);
                notifyParentMediaState("paused");
              }
            },
            onError: (err) => {
              console.error("Video player error:", err.data);
              notifyParentMediaState("ended");
              setIsYouTubePlaying(false);
              if (autoPlayNext) {
                nextSong();
              }
            },
          },
          playerVars: {
            autoplay: 1,
            controls: 1,
            modestbranding: 1,
            rel: 0,
            start: startSeconds,
      },
    });
  } else {
    if (typeof videoModePlayerRef.current.loadVideoById === "function") {
      videoModePlayerRef.current.loadVideoById({ videoId: videoID, startSeconds });
    }
  }
      if (videoModePlayerRef.current && typeof videoModePlayerRef.current.setPlaybackRate === "function") {
        videoModePlayerRef.current.setPlaybackRate(currentPlaybackRate);
      }
    },
    [autoPlayNext, currentPlaybackRate, nextSong, notifyParentMediaState]
  );

  // filterSongs: apply category and search filters.
  const filterSongs = useCallback(() => {
    if (!songs.length) {
      setFilteredSongs([]);
      return;
    }
    if (selectedCategory === CUSTOM_CATEGORY_OPTION) {
      setFilteredSongs(customSelectedSongs);
      return;
    }
    let subset = (!selectedCategory || selectedCategory === ALL_CATEGORIES_OPTION)
      ? songs
      : songs.filter((s) => s.categories.includes(selectedCategory));
    const normalizedQuery = normalizeSearchText(searchKeyword);
    const tokens = normalizedQuery.split(/\s+/).filter(Boolean);
    const result = subset.filter((song) => matchesSearchTokens(song, tokens));
    setFilteredSongs(result);
  }, [customSelectedSongs, matchesSearchTokens, searchKeyword, selectedCategory, songs]);

  const isSongMultilingual = useCallback((song) => {
    if (!song || !Array.isArray(song.lyrics)) return false;
    const fullText = song.lyrics
      .filter((p) => typeof p === "string")
      .join(" ")
      .toLowerCase();
    const hasLatin = /[a-z]/i.test(fullText);
    const hasTamil = /[\u0B80-\u0BFF]/.test(fullText);
    const hasMalayalam = /[\u0D00-\u0D7F]/.test(fullText);
    const scriptCount = [hasLatin, hasTamil, hasMalayalam].filter(Boolean).length;
    return scriptCount >= 2;
  }, []);

  const toggleCustomSongSelection = useCallback((song) => {
    if (!song) return;
    const slug = slugify(song.title);
    setCustomSelectedSlugs((prev) => {
      if (prev.includes(slug)) {
        return prev.filter((entry) => entry !== slug);
      }
      return [...prev, slug];
    });
  }, []);

  // loadSong: stop previous playback and load a new song.
  const loadSong = (index, initialSlideIndex = 0) => {
    setCustomSong(null);
    if (index < 0 || index >= filteredSongs.length) return;

    if (searchKeyword) {
      setSearchKeyword("");
    }

    notifyParentMediaState("waiting");

    // Save whether something was playing.
    const wasPlaying = isYouTubePlaying;

    // Stop any current playback.
    if (hiddenAudioPlayerRef.current) {
      if (typeof hiddenAudioPlayerRef.current.stopVideo === "function") {
        hiddenAudioPlayerRef.current.stopVideo();
      } else if (typeof hiddenAudioPlayerRef.current.pauseVideo === "function") {
        hiddenAudioPlayerRef.current.pauseVideo();
      }
    }
    if (videoModePlayerRef.current && typeof videoModePlayerRef.current.stopVideo === "function") {
      videoModePlayerRef.current.stopVideo();
    }

    // Clear audio timer.
    if (audioTimerRef.current) {
      clearInterval(audioTimerRef.current);
      audioTimerRef.current = null;
    }

    setCurrentSongIndex(index);
    setCurrentSlideIndex(initialSlideIndex);
    attemptedSongIndicesRef.current = new Set([index]);

    const song = filteredSongs[index];
    setCurrentSong(song);
    setActiveSongTitle(song ? song.title : "");

    const newLink = (song.youtubeUrls && song.youtubeUrls.length > 0)
      ? song.youtubeUrls[0]
      : null;
    setSelectedYouTubeLink(newLink);
    setYoutubeLinks(song.youtubeUrls);
    setMusescoreLink(song.musescoreUrl);
    if (song.youtubeUrls.length > 0) {
      setSelectedYouTubeLink(song.youtubeUrls[0]);
    } else {
      setSelectedYouTubeLink(null);
    }

    const detectLinesForSlides = () => {
      if (!Array.isArray(song.lyrics)) return null;
      if (!isSongMultilingual(song)) return null; // only auto-adjust for multilingual lyrics

      const firstTextParagraph = song.lyrics.find(
        (p) => typeof p === "string" && !p.toLowerCase().includes("<img")
      );
      if (!firstTextParagraph) return null;
      const count = firstTextParagraph
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean).length;
      return count > 0 ? Math.min(Math.max(count, 1), 25) : null;
    };

    const shouldAutoSetLines = isSlideLikeMode(displayMode) || displayMode === "presenter";
    const detectedLines = shouldAutoSetLines ? detectLinesForSlides() : null;
    const isEnglishOnlySong = Boolean(song?.isEnglish) && !isSongMultilingual(song);
    let linesForSlides = linesPerSlide;
    if (shouldAutoSetLines) {
      if (isEnglishOnlySong) {
        linesForSlides = 0;
        if (linesPerSlide !== 0) {
          setLinesPerSlide(0);
        }
      } else if (detectedLines !== null) {
        linesForSlides = detectedLines;
        if (detectedLines !== linesPerSlide) {
          setLinesPerSlide(detectedLines);
        }
      } else {
        const fallbackLines = initialLinesPerSlideRef.current;
        linesForSlides = fallbackLines;
        if (fallbackLines !== linesPerSlide) {
          setLinesPerSlide(fallbackLines);
        }
      }
      preservedSettingsRef.current = {
        ...preservedSettingsRef.current,
        linesPerSlide: linesForSlides,
      };
    }

    // Build slides for lyrics.
    const pSlides = buildParagraphSlides(song.title, song.lyrics);
    const lSlides = buildLineSlides(song.title, song.lyrics, linesForSlides);
    setParagraphSlides(pSlides);
    setLineSlides(lSlides);
    setActiveSlides(linesForSlides > 0 ? lSlides : pSlides);

    if (suppressPlaybackRef.current) {
      setIsYouTubePlaying(false);
      return;
    }

    // For non-video modes, always start new audio; for video-like modes, only if it was playing.
    if (isVideoLikeMode(displayMode)) {
      if (wasPlaying) {
        const newVid = extractYouTubeID(song.youtubeUrls[0]);
        if (newVid) createVideoPlayer(newVid);
      }
    } else {
      playAudioLink(newLink);
    }
  };

  // playAudioLink: load and play the hidden audio player.
  const ensureHiddenPlayer = useCallback(() => {
    if (hiddenAudioPlayerRef.current || !window || !window.YT || typeof window.YT.Player !== "function") {
      return;
    }
    hiddenAudioPlayerRef.current = new window.YT.Player("hidden-audio-player", {
      height: "0",
      width: "0",
      videoId: "",
      playerVars: {
        autoplay: 0,
        controls: 0,
        mute: 0,
        showinfo: 0,
        rel: 0,
      },
      events: {
        onReady: () => {
          const pending = pendingPlayRequestRef.current;
          if (pending && hiddenAudioPlayerRef.current) {
            const { link: pendingLink, startSeconds = 0 } = pending;
            const pendingId = extractYouTubeID(pendingLink);
            pendingPlayRequestRef.current = null;
            if (pendingId) {
              hiddenAudioPlayerRef.current.loadVideoById({ videoId: pendingId, startSeconds });
              if (typeof hiddenAudioPlayerRef.current.setPlaybackRate === "function") {
                hiddenAudioPlayerRef.current.setPlaybackRate(currentPlaybackRate);
              }
              if (typeof hiddenAudioPlayerRef.current.playVideo === "function") {
                hiddenAudioPlayerRef.current.playVideo();
              }
              setIsYouTubePlaying(true);
              userPausedRef.current = false;
              sliderValueRef.current = startSeconds;
              setAudioProgress(startSeconds);
              if (typeof hiddenAudioPlayerRef.current.getDuration === "function") {
                const duration = hiddenAudioPlayerRef.current.getDuration();
                if (Number.isFinite(duration) && duration > 0) {
                  setAudioDuration(duration);
                }
              }
            }
          }
        },
        onStateChange: onAudioPlayerStateChange,
        onError: onAudioPlayerError,
      },
    });
  }, [currentPlaybackRate, onAudioPlayerError, onAudioPlayerStateChange]);

  const playAudioLink = useCallback((link, startSeconds = 0) => {
    if (suppressPlaybackRef.current) return;
    if (!link) return;
    const vid = extractYouTubeID(link);
    if (!vid) return;
    sliderValueRef.current = startSeconds;
    setAudioProgress(startSeconds);

    const attemptPlayback = () => {
      const audioPlayer = hiddenAudioPlayerRef.current;
      if (!audioPlayer || typeof audioPlayer.loadVideoById !== "function") return false;
      audioPlayer.loadVideoById({ videoId: vid, startSeconds });
      setAudioDuration(0);
      if (typeof audioPlayer.setPlaybackRate === "function") {
        audioPlayer.setPlaybackRate(currentPlaybackRate);
      }
      if (typeof audioPlayer.playVideo === "function") {
        audioPlayer.playVideo();
      }
      setIsYouTubePlaying(true);
      userPausedRef.current = false;
      sliderValueRef.current = startSeconds;
      setAudioProgress(startSeconds);
      if (typeof audioPlayer.getDuration === "function") {
        const duration = audioPlayer.getDuration();
        if (Number.isFinite(duration) && duration > 0) {
          setAudioDuration(duration);
        }
      }
      return true;
    };

    if (!hiddenAudioPlayerRef.current || typeof hiddenAudioPlayerRef.current.loadVideoById !== "function") {
      pendingPlayRequestRef.current = { link, startSeconds };
      ensureHiddenPlayer();
      return;
    }

    attemptPlayback();
  }, [currentPlaybackRate, ensureHiddenPlayer]);

  const applyCustomSong = useCallback(
    (song) => {
      setCustomSong(song);
      setActiveSongTitle(song.title);
      setCurrentSong(song);
      const pSlides = buildParagraphSlides(song.title, song.lyrics);
      const lSlides = buildLineSlides(song.title, song.lyrics, linesPerSlide);
      setParagraphSlides(pSlides);
      setLineSlides(lSlides);
      setActiveSlides(linesPerSlide > 0 ? lSlides : pSlides);
      setCurrentSlideIndex(0);
      setSelectedYouTubeLink(song.youtubeUrls?.[0] || null);
      setYoutubeLinks(song.youtubeUrls || []);
      setMusescoreLink(song.musescoreUrl || null);
      if (song.youtubeUrls && song.youtubeUrls.length > 0) {
        playAudioLink(song.youtubeUrls[0]);
      } else {
        setIsYouTubePlaying(false);
      }
    },
    [linesPerSlide, playAudioLink]
  );

  const loadSongFromPanel = useCallback(
    (song) => {
      if (!song) return;
      const targetIndex = filteredSongs.findIndex(
        (entry) => slugify(entry.title) === slugify(song.title),
      );
      if (targetIndex >= 0) {
        loadSong(targetIndex);
        return;
      }
      applyCustomSong(song);
    },
    [applyCustomSong, filteredSongs, loadSong],
  );

  const handleOnDemandSubmit = useCallback(
    async (event) => {
      if (event) event.preventDefault();
      const trimmed = onDemandText.trim();
      setOnDemandError("");
      if (!trimmed) {
        setOnDemandError("Enter lyrics or a worship URL to preview.");
        return false;
      }
      const urlRegex = /(https?:\/\/[^\s"'<>]+)/gi;
      const urls = [...trimmed.matchAll(urlRegex)].map((match) => match[0]);
      const youtubeUrls = urls.filter((url) => extractYouTubeID(url));
      const paragraphs = trimmed
        .split(/\n\s*\n/)
        .map((segment) =>
          segment
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean)
            .join("\n")
        )
        .filter(Boolean);
      let content = paragraphs.length ? paragraphs : [];
      let additionalYoutube = [];
      if (urls.length > 0) {
        setOnDemandLoading(true);
        try {
          const { paragraphs: fetched, youtubeUrls: fetchedYoutube } = await fetchContentFromUrl(urls[0]);
          if (fetched.length) {
            content = fetched;
          }
          additionalYoutube = fetchedYoutube;
        } catch (error) {
          setOnDemandError("Unable to load content from that URL.");
          return false;
        } finally {
          setOnDemandLoading(false);
        }
      }
      if (!content.length) {
        content = [trimmed];
      }
      const allYoutube = Array.from(new Set([...youtubeUrls, ...additionalYoutube]));
      const song = {
        title: "On-Demand Worship",
        categories: ["On Demand"],
        isEnglish: true,
        youtubeUrls: allYoutube,
        musescoreUrl: null,
        lyrics: content,
        normalizedTitle: normalizeSearchText("On-Demand Worship"),
        normalizedLyrics: normalizeSearchText(content.join(" ")),
        normalizedCategories: normalizeSearchText("On Demand"),
      };
      applyCustomSong(song);
      setOnDemandError("");
      setOnDemandText("");
      if (onDemandInputRef.current) {
        onDemandInputRef.current.blur();
      }
      return true;
    },
    [onDemandText, applyCustomSong]
  );

  useEffect(() => {
    if (onDemandPopupOpen) {
      setTimeout(() => {
        onDemandInputRef.current?.focus();
      }, 0);
    }
  }, [onDemandPopupOpen]);

  // buildParagraphSlides: create an array with title + paragraphs.
  const buildParagraphSlides = (title, paragraphs) => [title, ...paragraphs];

  // buildLineSlides: create slides with a fixed number of lines.
  const buildLineSlides = (title, paragraphs, linesCount) => {
    if (!linesCount) return [];
    let all = [];
    paragraphs.forEach((p) => {
      if (p.toLowerCase().includes("<img")) {
        all.push(p);
      } else {
        const splitted = p.split("\n").map((s) => s.trim()).filter(Boolean);
        all.push(...splitted);
      }
    });
    const slides = [title];
    for (let i = 0; i < all.length; i += linesCount) {
      slides.push(all.slice(i, i + linesCount).join("\n"));
    }
    return slides;
  };

  const buildBookPages = useCallback((title, paragraphs, linesPerPage, includeTitle = true) => {
    if (!linesPerPage) return [];
    const allLines = [];
    if (includeTitle && title) {
      allLines.push(title);
      allLines.push(""); // small spacer after title
    }
    paragraphs.forEach((p) => {
      if (p.toLowerCase().includes("<img")) {
        allLines.push(p);
      } else {
        const splitted = p
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean);
        if (!splitted.length) return;
        allLines.push(...splitted);
      }
    });
    if (!allLines.length) return [];
    const pages = [];
    for (let i = 0; i < allLines.length; i += linesPerPage) {
      pages.push(allLines.slice(i, i + linesPerPage).join("\n"));
    }
    return pages;
  }, []);

  const bookFontSize = useMemo(() => clamp(Math.round(globalFontSize * 0.5), 12, 64), [globalFontSize]);

  const bookLinesPerPage = useMemo(() => {
    const ratio = bookFontSize > 0 ? BOOK_FONT_SIZE / bookFontSize : 1;
    const mobileMultiplier = isMobileViewport ? 0.8 : 1;
    const scaled = Math.round(BOOK_LINES_PER_PAGE * ratio * mobileMultiplier);
    const maxLines = isMobileViewport
      ? Math.max(8, Math.floor(BOOK_LINES_PER_PAGE * 1.1))
      : BOOK_LINES_PER_PAGE * 2;
    return Math.max(6, Math.min(maxLines, scaled));
  }, [bookFontSize, isMobileViewport]);

  const bookPages = useMemo(() => {
    if (!currentSong) return [];
    return buildBookPages(currentSong.title, currentSong.lyrics, bookLinesPerPage, false);
  }, [bookLinesPerPage, buildBookPages, currentSong]);
  const bookPagesPerView = useMemo(
    () => (isTabletOrBelow ? 1 : Math.max(1, Math.min(entireColumnCount, bookPages.length || 1))),
    [isTabletOrBelow, entireColumnCount, bookPages.length],
  );

  // getParagraphsFromContent: extract text and images.
  const getParagraphsFromContent = (el) => {
    let paragraphs = [];
    let current = "";
    function traverse(n) {
      if (n.nodeType === Node.TEXT_NODE) {
        if (n.textContent.trim()) current += n.textContent;
      } else if (n.nodeType === Node.ELEMENT_NODE) {
        if (n.tagName === "BR") {
          current += "\n";
        } else if (n.tagName === "P" || n.tagName === "DIV") {
          if (current.trim()) {
            paragraphs.push(current.trim());
            current = "";
          }
          n.childNodes.forEach(traverse);
          if (current.trim()) {
            paragraphs.push(current.trim());
            current = "";
          }
        } else if (n.tagName === "IMG") {
          const s = n.getAttribute("src");
          if (s)
            paragraphs.push(`<img src="${s}" style="max-width:100%;height:auto;"/>`);
        } else {
          n.childNodes.forEach(traverse);
        }
      }
    }
    el.childNodes.forEach(traverse);
    if (current.trim()) paragraphs.push(current.trim());
    return paragraphs;
  };

  // getCurrentTextOffset: calculate text offset (for slides).
  const getCurrentTextOffset = () => {
    if (!currentSong || currentSlideIndex === 0) return 0;
    if (linesPerSlide > 0 && activeSlides === lineSlides) {
      return (currentSlideIndex - 1) * linesPerSlide;
    } else {
      let off = 0;
      for (let i = 1; i < currentSlideIndex; i++) {
        const sl = paragraphSlides[i] || "";
        if (sl.toLowerCase().includes("<img")) {
          off++;
        } else {
          off += sl.split("\n").filter(Boolean).length || 1;
        }
      }
      return off;
    }
  };

  // offsetToSlideIndex: convert offset to slide index.
  const offsetToSlideIndex = (offset, newLinesPerSlide, pSlides) => {
    if (offset <= 0) return 0;
    if (newLinesPerSlide > 0) {
      return Math.floor(offset / newLinesPerSlide) + 1;
    } else {
      const paragraphs = pSlides.slice(1);
      let sum = 0, idx = 0;
      for (let p of paragraphs) {
        let lineCount = 1;
        if (!p.toLowerCase().includes("<img")) {
          lineCount = p.split("\n").filter(Boolean).length || 1;
        }
        if (sum + lineCount > offset) {
          return 1 + idx;
        }
        sum += lineCount;
        idx++;
      }
      return pSlides.length - 1;
    }
  };

  // rebuildActiveSlidesInPlace: re-calculate slides.
  const rebuildActiveSlidesInPlace = () => {
    if (!currentSong) return;
    const oldOffset = getCurrentTextOffset();
    const pSlides = buildParagraphSlides(currentSong.title, currentSong.lyrics);
    const lSlides = buildLineSlides(currentSong.title, currentSong.lyrics, linesPerSlide);
    setParagraphSlides(pSlides);
    setLineSlides(lSlides);
    const newActive = linesPerSlide > 0 ? lSlides : pSlides;
    setActiveSlides(newActive);
    const newIndex = offsetToSlideIndex(oldOffset, linesPerSlide, pSlides);
    const safe = newIndex >= newActive.length ? newActive.length - 1 : newIndex;
    setCurrentSlideIndex(safe < 0 ? 0 : safe);
  };

  const clampBookStartIndex = useCallback(
    (value, length, pagesPerView = 2) =>
      getBookPageStart(length, value, pagesPerView),
    [],
  );

  const advanceSlideInDirection = useCallback(
    (direction) => {
      if (direction === 0) return false;

      const isAtFirstSlide = currentSlideIndex <= 0;
      const isAtLastSlide = currentSlideIndex >= activeSlides.length - 1;

      if (displayMode === "book") {
        const bookLength = bookPages.length;
        const bookStart = clampBookStartIndex(
          currentSlideIndex,
          bookLength,
          bookPagesPerView,
        );
        if (direction > 0) {
          if (bookStart + bookPagesPerView < bookLength) {
            setCurrentSlideIndex(bookStart + bookPagesPerView);
            return true;
          }
          if (lockSongNavigation) return false;
          if (currentSongIndex >= filteredSongs.length - 1) return false;
          nextSong();
          return true;
        }
        if (direction < 0) {
          if (bookStart > 0) {
            setCurrentSlideIndex(Math.max(0, bookStart - bookPagesPerView));
            return true;
          }
          if (lockSongNavigation) return false;
          if (currentSongIndex <= 0) return false;
          const prevIndex = currentSongIndex - 1;
          const prevSongData = filteredSongs[prevIndex];
          if (prevSongData) {
            const prevBook = buildBookPages(prevSongData.title, prevSongData.lyrics, bookLinesPerPage, false);
            const lastPageStart = getBookLastPageStart(prevBook.length, bookPagesPerView);
            loadSong(prevIndex, lastPageStart);
          } else {
            loadSong(prevIndex);
          }
          return true;
        }
        return false;
      }

      if (direction > 0) {
        // next
        if (!isAtLastSlide) {
          setCurrentSlideIndex((p) => p + 1);
          return true;
        }
        if (lockSongNavigation) return false;

        const isAtLastSong = currentSongIndex >= filteredSongs.length - 1;
        if (isAtLastSong) {
          return false;
        }
        nextSong();
        return true;
      }

      if (direction < 0) {
        // previous
        if (!isAtFirstSlide) {
          setCurrentSlideIndex((p) => p - 1);
          return true;
        }
        if (lockSongNavigation) return false;

        const isAtFirstSong = currentSongIndex <= 0;
        if (isAtFirstSong) {
          return false;
        }

        const prevIndex = currentSongIndex - 1;
        const prevSongData = filteredSongs[prevIndex];
        if (prevSongData) {
          const pSlides = buildParagraphSlides(prevSongData.title, prevSongData.lyrics);
          const lSlides = buildLineSlides(prevSongData.title, prevSongData.lyrics, linesPerSlide);
          const targetSlides = linesPerSlide > 0 ? lSlides : pSlides;
          const lastSlideIndex = targetSlides.length > 0 ? targetSlides.length - 1 : 0;
          loadSong(prevIndex, lastSlideIndex);
        } else {
          loadSong(prevIndex);
        }
        return true;
      }

      return false;
    },
    [
      activeSlides.length,
      currentSlideIndex,
      lockSongNavigation,
      currentSongIndex,
      filteredSongs,
      nextSong,
      linesPerSlide,
      loadSong,
      clampBookStartIndex,
      bookPages.length,
      bookPagesPerView,
      buildBookPages,
      bookLinesPerPage,
    ],
  );

  // nextContent: advance slide or go to next song.
  const nextContent = useCallback(() => {
    if (autoAdvanceTimerRef.current) {
      clearInterval(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    if (
      displayMode === "slide" ||
      displayMode === "videoslide" ||
      isCameraSlidesMode(displayMode) ||
      isCameraPresentMode(displayMode)
    ) {
      if (currentSlideIndex < activeSlides.length - 1) {
        setCurrentSlideIndex((p) => p + 1);
      } else if (!lockSongNavigation) {
        nextSong();
      }
      if (autoAdvanceInterval > 0) startAutoAdvance(autoAdvanceInterval);
    } else if (displayMode === "book") {
      const bookLength = bookPages.length;
      const bookStart = clampBookStartIndex(
        currentSlideIndex,
        bookLength,
        bookPagesPerView,
      );
      if (bookStart + bookPagesPerView < bookLength) {
        setCurrentSlideIndex(bookStart + bookPagesPerView);
      } else if (!lockSongNavigation) {
        nextSong();
      }
      if (autoAdvanceInterval > 0) startAutoAdvance(autoAdvanceInterval);
    } else if (displayMode === "music") {
      if (lockSongNavigation) return;
      if (currentSongIndex + 2 < filteredSongs.length) {
        loadSong(currentSongIndex + 2);
      }
    } else if (displayMode === "presenter") {
      if (currentSlideIndex < activeSlides.length - 1) {
        setCurrentSlideIndex((p) => p + 1);
      } else if (!lockSongNavigation) {
        nextSong();
      }
      if (autoAdvanceInterval > 0) startAutoAdvance(autoAdvanceInterval);
    } else {
      if (!lockSongNavigation) {
        nextSong();
      }
    }
  }, [
    displayMode,
    currentSlideIndex,
    activeSlides,
    nextSong,
    autoAdvanceInterval,
    currentSongIndex,
    filteredSongs,
    lockSongNavigation,
    clampBookStartIndex,
    bookPages.length,
    bookPagesPerView,
  ]);

  // prevContent: go back one slide or previous song.
  const prevContent = useCallback(() => {
      if (
        displayMode === "slide" ||
        isCameraSlidesMode(displayMode) ||
        isCameraPresentMode(displayMode) ||
        displayMode === "presenter" ||
        displayMode === "videoslide"
      ) {
      if (currentSlideIndex > 0) {
        setCurrentSlideIndex((p) => p - 1);
      } else if (!lockSongNavigation) {
        if (!filteredSongs.length) return;
        let prevIndex = currentSongIndex - 1;
        if (prevIndex < 0) prevIndex = filteredSongs.length - 1;

        const prevSongData = filteredSongs[prevIndex];
        if (prevSongData) {
          const pSlides = buildParagraphSlides(prevSongData.title, prevSongData.lyrics);
          const lSlides = buildLineSlides(prevSongData.title, prevSongData.lyrics, linesPerSlide);
          const targetSlides = linesPerSlide > 0 ? lSlides : pSlides;
          const lastSlideIndex = targetSlides.length > 0 ? targetSlides.length - 1 : 0;
          loadSong(prevIndex, lastSlideIndex);
        } else {
          loadSong(prevIndex);
        }
      }
    } else if (displayMode === "book") {
      const bookLength = bookPages.length;
      const bookStart = clampBookStartIndex(
        currentSlideIndex,
        bookLength,
        bookPagesPerView,
      );
      if (bookStart > 0) {
        setCurrentSlideIndex(Math.max(0, bookStart - bookPagesPerView));
      } else if (!lockSongNavigation) {
        if (!filteredSongs.length) return;
        let prevIndex = currentSongIndex - 1;
        if (prevIndex < 0) prevIndex = filteredSongs.length - 1;

        const prevSongData = filteredSongs[prevIndex];
        if (prevSongData) {
          const prevBook = buildBookPages(prevSongData.title, prevSongData.lyrics, bookLinesPerPage, false);
          const lastPageStart = getBookLastPageStart(prevBook.length, bookPagesPerView);
          loadSong(prevIndex, lastPageStart);
        } else {
          loadSong(prevIndex);
        }
      }
    } else if (displayMode === "music") {
      if (lockSongNavigation) return;
      if (currentSongIndex - 2 >= 0) {
        loadSong(currentSongIndex - 2);
      }
    } else {
      if (!lockSongNavigation) {
        prevSong();
      }
    }
  }, [
    displayMode,
    currentSlideIndex,
    prevSong,
    currentSongIndex,
    filteredSongs,
    lockSongNavigation,
    linesPerSlide,
    loadSong,
    clampBookStartIndex,
    bookPages.length,
    bookPagesPerView,
    buildBookPages,
    bookLinesPerPage,
  ]);

  useEffect(() => {
    if (displayMode !== "book") return undefined;
    setCurrentSlideIndex((prev) => {
      const next = clampBookStartIndex(prev, bookPages.length, bookPagesPerView);
      return next === prev ? prev : next;
    });
    return undefined;
  }, [displayMode, clampBookStartIndex, bookPages.length, bookPagesPerView]);

  useTouchNavigation(slideTouchTargetRef, {
    onLeft: prevContent,
    onRight: nextContent,
  });

  useTouchNavigation(presenterTouchTargetRef, {
    onLeft: prevContent,
    onRight: nextContent,
  });

  useTouchNavigation(videoSlideTouchTargetRef, {
    onLeft: prevContent,
    onRight: nextContent,
  });

  const handleVideoSlideClick = useCallback(
    (event) => {
      if (!videoSlideTouchTargetRef.current) return;
      const bounds = videoSlideTouchTargetRef.current.getBoundingClientRect();
      const offsetX = event.clientX - bounds.left;
      if (!Number.isFinite(offsetX) || bounds.width === 0) {
        nextContent();
        return;
      }
      if (offsetX < bounds.width / 2) {
        prevContent();
      } else {
        nextContent();
      }
    },
    [nextContent, prevContent],
  );

  useEffect(() => {
    if (!isEmbeddedRef.current || typeof window === "undefined") {
      return undefined;
    }
    const handleSessionNavigation = (event) => {
      if (!event || typeof event.data !== "object" || event.data.type !== SESSION_NAVIGATION_MESSAGE) {
        return;
      }
      const expectedOrigin = parentOriginRef.current;
      if (expectedOrigin !== "*" && event.origin && event.origin !== expectedOrigin) {
        return;
      }
      const { action, requestId } = event.data;
      if (!requestId || (action !== "next" && action !== "previous")) return;
      const handled = advanceSlideInDirection(action === "next" ? 1 : -1);
      sendSessionNavigationResponse(requestId, action, handled);
    };
    window.addEventListener("message", handleSessionNavigation);
    return () => window.removeEventListener("message", handleSessionNavigation);
  }, [advanceSlideInDirection, sendSessionNavigationResponse]);

  // startAutoAdvance: start auto-advance timer.
  const startAutoAdvance = (sec) => {
    if (autoAdvanceTimerRef.current) clearInterval(autoAdvanceTimerRef.current);
    autoAdvanceTimerRef.current = setInterval(() => {
      nextContent();
    }, sec * 1000);
  };

  // stopAutoAdvance: clear auto-advance timer.
  const stopAutoAdvance = () => {
    if (autoAdvanceTimerRef.current) {
      clearInterval(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
  };

  // changePlaybackRate: adjust playback speed.
  const changePlaybackRate = (dir) => {
    let idx = playbackRates.indexOf(currentPlaybackRate);
    if (idx < 0) {
      setCurrentPlaybackRate(1);
      idx = playbackRates.indexOf(1);
    }
    const newI = idx + dir;
    if (newI >= 0 && newI < playbackRates.length) {
      setCurrentPlaybackRate(playbackRates[newI]);
    }
  };

  // onAudioPlayerStateChange: update audio progress for hidden audio.
  function onAudioPlayerStateChange(e) {
    if (e.data === window.YT.PlayerState.PLAYING) {
      setIsYouTubePlaying(true);
      notifyParentMediaState("playing");
      if (!audioTimerRef.current && hiddenAudioPlayerRef.current) {
        audioTimerRef.current = setInterval(() => {
          const player = hiddenAudioPlayerRef.current;
          if (!player || typeof player.getCurrentTime !== "function") return;
          const currentTime = player.getCurrentTime();
          audioTimeRef.current = currentTime;
          if (!isUserSeekingRef.current) {
            sliderValueRef.current = currentTime;
            setAudioProgress(currentTime);
          }
        }, 500);
      }
      if (hiddenAudioPlayerRef.current && typeof hiddenAudioPlayerRef.current.getDuration === "function") {
        const duration = hiddenAudioPlayerRef.current.getDuration();
        if (Number.isFinite(duration) && duration > 0) {
          setAudioDuration(duration);
        }
      }
    } else if (e.data === window.YT.PlayerState.ENDED) {
      clearInterval(audioTimerRef.current);
      audioTimerRef.current = null;
      if (userPausedRef.current) {
        userPausedRef.current = false;
        setIsYouTubePlaying(false);
        notifyParentMediaState("paused");
      } else {
        notifyParentMediaState("ended");
        setIsYouTubePlaying(false);
        if (autoPlayNext) {
          nextSong();
        }
      }
    } else {
      clearInterval(audioTimerRef.current);
      audioTimerRef.current = null;
      setIsYouTubePlaying(false);
      notifyParentMediaState("paused");
    }
  }
  function onAudioPlayerError() {
    console.error("Audio error");
    notifyParentMediaState("ended");
    setIsYouTubePlaying(false);
    if (autoPlayNext) {
      nextSong();
    }
  }

  useEffect(() => {
    if (
      !currentSong ||
      isVideoLikeMode(displayMode) ||
      lockSongNavigation ||
      !autoPlayNext ||
      typeof audioDuration !== "number" ||
      audioDuration <= 0 ||
      userPausedRef.current
    ) {
      progressAutoAdvanceRef.current = null;
      return;
    }
    const threshold = Math.max(0, audioDuration - 0.4);
    const reachedEnd = audioProgress >= threshold;
    if (
      reachedEnd &&
      !isYouTubePlaying &&
      progressAutoAdvanceRef.current !== currentSongIndex
    ) {
      progressAutoAdvanceRef.current = currentSongIndex;
      nextSong();
      return;
    }
    if (audioProgress < Math.max(0, audioDuration - 1)) {
      progressAutoAdvanceRef.current = null;
    }
  }, [
    audioDuration,
    audioProgress,
    currentSong,
    currentSongIndex,
    displayMode,
    isYouTubePlaying,
    lockSongNavigation,
    nextSong,
    autoPlayNext,
  ]);

  // extractYouTubeID: extract the 11-char ID from a URL.
  const extractYouTubeID = (url) => {
    if (!url) return null;
    let id = null;
    if (url.includes("youtu.be/")) {
      id = url.split("youtu.be/")[1].split(/[?&]/)[0];
    } else if (url.includes("youtube.com/watch?v=")) {
      id = url.split("watch?v=")[1].split("&")[0];
    } else if (url.includes("youtube.com/embed/")) {
      id = url.split("embed/")[1].split(/[?&]/)[0];
    } else if (url.includes("youtube.com/v/")) {
      id = url.split("v/")[1].split(/[?&]/)[0];
    }
    return id && id.length === 11 ? id : null;
  };

const fetchContentFromUrl = async (url) => {
  if (typeof window === "undefined") {
    return { paragraphs: [], youtubeUrls: [] };
  }
  const parseHtml = (html) => {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const nodes = [...doc.querySelectorAll("p, h1, h2, h3, li")];
    const paragraphs = nodes
      .map((node) => node.textContent?.trim())
      .filter(Boolean)
      .filter((text) => text.length > 10);
    const youtubeSet = new Set();
    [...doc.querySelectorAll("a[href]")].forEach((anchor) => {
      const id = extractYouTubeID(anchor.href);
      if (id) youtubeSet.add(anchor.href.trim());
    });
    [...doc.querySelectorAll("iframe[src]")].forEach((frame) => {
      const id = extractYouTubeID(frame.src);
      if (id) youtubeSet.add(frame.src.trim());
    });
    return {
      paragraphs,
      youtubeUrls: Array.from(youtubeSet),
    };
  };

  const fetchWithProxy = async () => {
    const withoutScheme = url.replace(/^https?:\/\//i, "");
    if (!withoutScheme) throw new Error("Invalid URL");
    const proxyUrl = `https://r.jina.ai/http://${withoutScheme}`;
    const fallbackRes = await fetch(proxyUrl);
    if (!fallbackRes.ok) throw new Error("Unable to load content");
    const html = await fallbackRes.text();
    return parseHtml(html);
  };

  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) {
      throw new Error("Unable to load content");
    }
    const html = await response.text();
    return parseHtml(html);
  } catch (error) {
    return fetchWithProxy();
  }
};

  // extractMusescoreEmbedURL: return an embed URL.
  const extractMusescoreEmbedURL = (link) => {
    if (!link) return null;
    try {
      const u = new URL(link);
      return u.origin + u.pathname + "/embed";
    } catch (e) {
      console.warn("Invalid musescore link:", link);
      return null;
    }
  };

  const toArray = (value) => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  };

  function slugify(value) {
    return (
      (value || "")
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "") || "worship"
    );
  }

  const timestamp = () => new Date().toISOString().replace(/[:.]/g, "-");

  const buildFileBase = (base) => `${slugify(base)}-${timestamp()}`;

  const buildFilename = (base, ext) => `${buildFileBase(base)}.${ext}`;

  const getDisplayModeLabel = () => {
    switch (displayMode) {
      case "slide":
        return "Slide";
      case CAMERA_SLIDES_MODE:
        return "Camera + Slides";
      case CAMERA_PRESENT_MODE:
        return "Camera + Present";
      case CAMERA_VIDEO_MODE:
        return "Camera + Video";
      case "video":
        return "Video";
      case "videoslide":
        return "Video + Slides";
      case "music":
        return "Music";
      case "presenter":
        return "Presenter";
      case "entire":
        return "Full Content";
      case "book":
        return "Book";
      case "newsletter":
        return "Newsletter";
      default:
        return displayMode;
    }
  };

  const getCurrentViewSongForExport = () => {
    if (currentSong) {
      const baseSong = buildSongPayload({ ...currentSong, linesPerSlide });
      const modeLabel = getDisplayModeLabel();
      if (displayMode === "slide" || isCameraSlidesMode(displayMode)) {
        const slideText = normaliseParagraph(getSlideContent());
        return {
          ...baseSong,
          title: `${currentSong.title} — ${modeLabel} View`,
          lyrics: ["Current Slide", slideText],
        };
      }
      if (displayMode === "presenter" || isCameraPresentMode(displayMode)) {
        const presenter = showPresenterModeCurrentNext();
        return {
          ...baseSong,
          title: `${currentSong.title} — ${modeLabel} View`,
          lyrics: [
            "Presenter View — Current Slide",
            normaliseParagraph(presenter.current || ""),
            "",
            "Presenter View — Next Slide",
            normaliseParagraph(presenter.next || ""),
          ],
        };
      }
      if (displayMode === "video" || isCameraVideoMode(displayMode)) {
        return {
          ...baseSong,
          title: `${currentSong.title} — Video View`,
          lyrics: [
            "Video playback mode.",
            "Lyrics are included below for reference:",
            ...baseSong.lyrics,
          ],
        };
      }
      if (displayMode === "videoslide") {
        const slideText = normaliseParagraph(getSlideContent());
        return {
          ...baseSong,
          title: `${currentSong.title} — Video + Slides View`,
          lyrics: [
            "Video with slide overlay.",
            "Current slide lyrics:",
            slideText,
          ],
        };
      }
      if (displayMode === "music") {
        return {
          ...baseSong,
          title: `${currentSong.title} — Music View`,
          lyrics: ["Music playback mode with lyrics:", ...baseSong.lyrics],
        };
      }
      if (displayMode === "book") {
        const bookStart = clampBookStartIndex(
          currentSlideIndex,
          bookPages.length,
          bookPagesPerView,
        );
        const pages = bookPages
          .slice(bookStart, bookStart + bookPagesPerView)
          .map((page) => normaliseParagraph(page || ""));
        return {
          ...baseSong,
          title: `${currentSong.title} — Book View`,
          lyrics: pages.flatMap((page, idx) => [
            `Book View — Page ${bookStart + idx + 1}`,
            page,
            "",
          ]),
        };
      }
      return {
        ...baseSong,
        title: `${currentSong.title} — ${modeLabel} View`,
      };
    }
    if (filteredSongs.length) {
      return {
        title: "Worship Song Tiles",
        categories: [],
        lyrics: filteredSongs.map((song, index) => `${index + 1}. ${song.title}`),
      };
    }
    return {
      title: "Worship Screen",
      categories: [],
      lyrics: ["No song selected."],
    };
  };

  const getSongsForCategoryExport = () => {
    if (!songs.length) return [];
    const applyLines = (song) => buildSongPayload({ ...song, linesPerSlide });
    if (!selectedCategory || selectedCategory === ALL_CATEGORIES_OPTION) {
      return songs.map(applyLines);
    }
    if (selectedCategory === CUSTOM_CATEGORY_OPTION) {
      return customSelectedSongs.map(applyLines);
    }
    return songs
      .filter((song) => toArray(song.categories).includes(selectedCategory))
      .map(applyLines);
  };

  const getSongsForCatalogExport = () => songs.map((song) => buildSongPayload({ ...song, linesPerSlide }));

  const getCatalogLabel = () => {
    if (selectedDomain === ALL_CATALOGS_VALUE) return "All Catalogs";
    const match = organizations.find((org) => org.domain === selectedDomain);
    return match?.title || "catalog";
  };

  const waitForNextFrame = () =>
    new Promise((resolve) => {
      if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
        setTimeout(resolve, 16);
        return;
      }
      window.requestAnimationFrame(() => resolve());
    });

  const waitForCondition = async (predicate, timeoutMs = 4000) => {
    const getNow = () => (typeof performance !== "undefined" ? performance.now() : Date.now());
    const start = getNow();
    while (!predicate(liveStateRef.current)) {
      if (getNow() - start > timeoutMs) {
        throw new Error("Timed out waiting for UI update during export.");
      }
      await waitForNextFrame();
    }
  };

  const withControlsHiddenForCapture = async (task) => {
    const activatePreview = !exportPreviewMode;
  const shouldHideControls = controlsVisible;
    if (activatePreview) {
      setExportPreviewMode(true);
    }
    if (shouldHideControls) {
      setControlsVisible(false);
    }
    if (activatePreview || shouldHideControls) {
      await waitForNextFrame();
      await waitForNextFrame();
    }
    try {
      await task();
    } finally {
      if (activatePreview || shouldHideControls) {
        await waitForNextFrame();
      }
      if (activatePreview) {
        setExportPreviewMode(false);
      }
      if (shouldHideControls) {
        setControlsVisible(true);
        setTopControlsDismissed(false);
      }
    }
  };

  const runExportTask = async (task) => {
    if (exporting) return;
    try {
      setExporting(true);
      await task();
    } catch (error) {
      console.error("Export failed", error);
      window.alert(`Export failed: ${error?.message || error}`);
    } finally {
      setExporting(false);
    }
  };

  const handleCurrentExport = async (type) => {
    if (!type) return;
    const container = exportContainerRef.current || document.getElementById("lyrics-container");
    if (!container) {
      window.alert("Unable to capture the worship screen for export.");
      return;
    }
    const songForText = getCurrentViewSongForExport();
    const baseName = currentSong ? `${currentSong.title}-${getDisplayModeLabel()}` : "worship-screen";
    const fileBase = buildFileBase(baseName);
    await runExportTask(async () => {
      if (type === "image") {
        await withControlsHiddenForCapture(async () => {
          await exportCurrentViewAsImage(container, fileBase);
        });
        return;
      }
      if (type === "ppt") {
        await withControlsHiddenForCapture(async () => {
          await exportCurrentViewAsPpt(container, fileBase);
        });
        return;
      }
      if (type === "docx") {
        await exportSongsToDocx([songForText], `${fileBase}.docx`);
        return;
      }
      if (type === "pdf") {
        await exportSongsToPdf([songForText], `${fileBase}.pdf`);
        return;
      }
      if (type === "song:image") {
        const captures =
          isSlideLikeMode(displayMode)
            ? await captureCurrentSongSlidesForExport()
            : await captureSongsForExport([currentSongIndex]);
        if (!captures.length) throw new Error("Unable to capture song slides.");
        await exportImageCollectionFromDataUrls(captures, `${fileBase}-song.zip`);
        return;
      }
      if (type === "song:ppt") {
        const captures =
          isSlideLikeMode(displayMode)
            ? await captureCurrentSongSlidesForExport()
            : await captureSongsForExport([currentSongIndex]);
        if (!captures.length) throw new Error("Unable to capture song slides.");
        await exportPptFromDataUrls(captures, `${fileBase}-song.pptx`);
        return;
      }
      if (type === "song:docx") {
        const payload = buildSongPayload({ ...currentSong, linesPerSlide });
        await exportSongsToDocx([payload], `${fileBase}-song.docx`);
        return;
      }
      if (type === "song:pdf") {
        const payload = buildSongPayload({ ...currentSong, linesPerSlide });
        await exportSongsToPdf([payload], `${fileBase}-song.pdf`);
        return;
      }
      throw new Error("Unsupported export type");
    });
  };

  const captureCurrentViewToDataUrl = async () => {
    const container = exportContainerRef.current || document.getElementById("lyrics-container");
    if (!container) {
      throw new Error("Unable to locate export container.");
    }
    let dataUrl = "";
    await withControlsHiddenForCapture(async () => {
      const canvas = await captureElement(container);
      dataUrl = canvas.toDataURL("image/png");
    });
    if (!dataUrl) {
      throw new Error("Failed to capture current view image.");
    }
    await waitForCondition((state) => !state.exportPreviewMode);
    return dataUrl;
  };

  const ensureDisplayMode = async (mode) => {
    if (liveStateRef.current.displayMode === mode) return;
    setDisplayMode(mode);
    await waitForCondition((state) => state.displayMode === mode);
  };

  const restoreToTiles = () => {
    setCurrentSong(null);
    setCustomSong(null);
    setParagraphSlides([]);
    setLineSlides([]);
    setActiveSlides([]);
    setActiveSongTitle("");
    setCurrentSongIndex(0);
    setCurrentSlideIndex(0);
    setIsYouTubePlaying(false);
    setOnDemandPopupOpen(false);
  };

  const captureSongsForExport = async (songIndices) => {
    if (!songIndices || !songIndices.length) return [];
    const captures = [];
    const previousFilteredSongs = filteredSongs.slice();
    const previousState = {
      songIndex: liveStateRef.current.currentSongIndex,
      displayMode: liveStateRef.current.displayMode,
      slideIndex: liveStateRef.current.currentSlideIndex,
      hasSong: Boolean(liveStateRef.current.currentSong),
    };
    const previousSuppress = suppressPlaybackRef.current;
    suppressPlaybackRef.current = true;
    try {
      const captureIndices = songIndices.filter((index) => index >= 0 && index < songs.length);
      if (!captureIndices.length) return [];
      const captureList = captureIndices.map((index) => songs[index]);
      setFilteredSongs(captureList);
      await waitForNextFrame();
      await waitForNextFrame();
      await waitForCondition((state) => state.filteredSongsLength === captureList.length);
      await ensureDisplayMode("entire");
      for (let counter = 0; counter < captureIndices.length; counter += 1) {
        const captureIndex = captureIndices[counter];
        await ensureDisplayMode("entire");
        if (liveStateRef.current.currentSongIndex !== counter || !liveStateRef.current.currentSong) {
          loadSong(counter);
          await waitForCondition(
            (state) => state.currentSongIndex === counter && Boolean(state.currentSong)
          );
        }
        setCurrentSlideIndex(0);
        await waitForCondition((state) => state.currentSlideIndex === 0);
        await waitForNextFrame();
        await waitForNextFrame();
        const dataUrl = await captureCurrentViewToDataUrl();
        captures.push({
          dataUrl,
          title: captureList[counter]?.title || songs[captureIndex]?.title || `Song ${captureIndex + 1}`,
        });
      }
    } finally {
      try {
        setFilteredSongs(previousFilteredSongs);
        await waitForNextFrame();
        await waitForNextFrame();
        await waitForCondition((state) => state.filteredSongsLength === previousFilteredSongs.length);
        if (previousState.displayMode) {
          await ensureDisplayMode(previousState.displayMode);
        }
        if (previousState.hasSong && typeof previousState.songIndex === "number" && previousState.songIndex >= 0) {
          loadSong(previousState.songIndex);
          await waitForCondition(
            (state) => state.currentSongIndex === previousState.songIndex && Boolean(state.currentSong)
          );
          setCurrentSlideIndex(previousState.slideIndex || 0);
        } else {
          restoreToTiles();
        }
      } catch (error) {
        console.warn("Failed to fully restore view after export capture.", error);
      }
      suppressPlaybackRef.current = previousSuppress;
    }
    return captures;
  };

  const captureCurrentSongSlidesForExport = async () => {
    if (!currentSong) {
      throw new Error("No current song to export.");
    }
    const captures = [];
    const previousState = {
      displayMode,
      slideIndex: currentSlideIndex,
    };
    suppressPlaybackRef.current = true;
    try {
      await ensureDisplayMode("slide");
      // Always start from slide 0 for clean capture.
      setCurrentSlideIndex(0);
      await waitForCondition((state) => state.currentSlideIndex === 0);
      await waitForNextFrame();
      const totalSlides = Array.isArray(activeSlides) && activeSlides.length ? activeSlides.length : 1;
      for (let i = 0; i < totalSlides; i += 1) {
        setCurrentSlideIndex(i);
        await waitForCondition((state) => state.currentSlideIndex === i);
        await waitForNextFrame();
        await waitForNextFrame();
        const dataUrl = await captureCurrentViewToDataUrl();
        captures.push({
          dataUrl,
          title: `${currentSong.title || "Slide"}-${i + 1}`,
        });
      }
    } finally {
      try {
        await ensureDisplayMode(previousState.displayMode);
        setCurrentSlideIndex(previousState.slideIndex || 0);
      } catch (error) {
        console.warn("Failed to restore state after slide export", error);
      }
      suppressPlaybackRef.current = false;
    }
    return captures;
  };

  const handleBulkExport = async (scope, format) => {
    const songsToExport =
      scope === "category" ? getSongsForCategoryExport() : getSongsForCatalogExport();
    if (!songsToExport.length) {
      const label =
        scope === "category"
          ? selectedCategory || ALL_CATEGORIES_OPTION
          : getCatalogLabel();
      window.alert(`No songs found for ${label}.`);
      return;
    }
    const label =
      scope === "category"
        ? (selectedCategory && selectedCategory !== ALL_CATEGORIES_OPTION
            ? selectedCategory
            : "all-categories")
        : getCatalogLabel();
    const fileBase = buildFileBase(`${label}-${scope}`);

    const buildSongIndices = () => {
      if (scope === "catalog") {
        return songs.map((_, idx) => idx);
      }
      const category = selectedCategory;
      if (!category || category === ALL_CATEGORIES_OPTION) {
        return songs.map((_, idx) => idx);
      }
      if (category === CUSTOM_CATEGORY_OPTION) {
        return customSelectedSongs
          .map((song) =>
            songs.findIndex(
              (entry) => slugify(entry.title) === slugify(song.title),
            ),
          )
          .filter((idx) => idx >= 0);
      }
      return songs
        .map((song, idx) => ({ song, idx }))
        .filter(({ song }) => toArray(song.categories).includes(category))
        .map(({ idx }) => idx);
    };

    if (format === "ppt" || format === "image") {
      const songIndices = buildSongIndices();
      if (!songIndices.length) {
        window.alert("No songs available for the selected export.");
        return;
      }
      await runExportTask(async () => {
        const captures = await captureSongsForExport(songIndices);
        if (!captures.length) {
          throw new Error("Unable to capture slides for export.");
        }
        if (format === "image") {
          await exportImageCollectionFromDataUrls(captures, `${fileBase}.zip`);
        } else {
          await exportPptFromDataUrls(captures, `${fileBase}.pptx`);
        }
      });
      return;
    }

    await runExportTask(async () => {
      if (format === "docx") {
        await exportSongsToDocx(songsToExport, `${fileBase}.docx`);
        return;
      }
      if (format === "pdf") {
        await exportSongsToPdf(songsToExport, `${fileBase}.pdf`);
        return;
      }
      throw new Error("Unsupported bulk export format");
    });
  };

  const handleBookPdfExport = async () => {
    const songsToExport = currentSong
      ? [buildSongPayload({ ...currentSong, linesPerSlide })]
      : getSongsForCategoryExport();
    if (!songsToExport.length) {
      const label = currentSong ? currentSong.title : selectedCategory || ALL_CATEGORIES_OPTION;
      window.alert(`No songs available for ${label}.`);
      return;
    }

    const catalogLabel = getCatalogLabel();
    const categoryLabel = currentSong
      ? toArray(currentSong.categories).join(", ") || selectedCategory || ALL_CATEGORIES_OPTION
      : selectedCategory || ALL_CATEGORIES_OPTION;
    const preferredColumns = Math.max(
      1,
      entireColumnsLocked ? entireColumnCount : autoEntireColumnCount
    );
    const bodyFontSize = Math.round(Math.max(11, Math.min(globalFontSize / 4, 18)));
    const settingsSummary = [
      `${preferredColumns} ${preferredColumns === 1 ? "Column" : "Columns"}`,
      `Font ~${bodyFontSize}pt`,
      lyricsBold ? "Bold lyrics" : "",
      selectedThemeId ? `Theme: ${selectedThemeId}` : "",
    ]
      .filter(Boolean)
      .join(" • ");
    const fileBase = currentSong
      ? buildFileBase(`${currentSong.title}-songbook`)
      : buildFileBase(`${catalogLabel}-${categoryLabel || "all"}-songbook`);
    const bookTitle = currentSong
      ? `${currentSong.title} — Lyrics Book`
      : `${catalogLabel} — ${categoryLabel || ALL_CATEGORIES_OPTION} Songbook`;
    const subtitle = `${catalogLabel}${categoryLabel ? ` • ${categoryLabel}` : ""}`;

    await runExportTask(async () => {
      await exportSongsToBookPdf(songsToExport, {
        filename: `${fileBase}.pdf`,
        bookTitle,
        catalogLabel,
        categoryLabel,
        subtitle,
        theme: { ...activeTheme, accent: customGradientAccent },
        preferredFontId: selectedFontId,
        columns: preferredColumns,
        bodyFontSize,
        lineHeight: Math.round(bodyFontSize * 1.35),
        boldLyrics: lyricsBold,
        settingsSummary,
      });
    });
  };

  const handlePlainBookPdfExport = async () => {
    const songsToExport = currentSong
      ? [buildSongPayload({ ...currentSong, linesPerSlide })]
      : getSongsForCategoryExport();
    if (!songsToExport.length) {
      const label = currentSong ? currentSong.title : selectedCategory || ALL_CATEGORIES_OPTION;
      window.alert(`No songs available for ${label}.`);
      return;
    }
    const catalogLabel = getCatalogLabel();
    const categoryLabel = currentSong
      ? toArray(currentSong.categories).join(", ") || selectedCategory || ALL_CATEGORIES_OPTION
      : selectedCategory || ALL_CATEGORIES_OPTION;
    const bodyFontSize = Math.round(Math.max(11, Math.min(globalFontSize / 4, 18)));
    const fileBase = currentSong
      ? buildFileBase(`${currentSong.title}-songbook-plain`)
      : buildFileBase(`${catalogLabel}-${categoryLabel || "all"}-songbook-plain`);
    const bookTitle = currentSong
      ? `${currentSong.title} — Plain Lyrics`
      : `${catalogLabel} — ${categoryLabel || ALL_CATEGORIES_OPTION} Plain Songbook`;
    const subtitle = `${catalogLabel}${categoryLabel ? ` • ${categoryLabel}` : ""}`;
    const preferredColumns = Math.max(
      1,
      entireColumnsLocked ? entireColumnCount : autoEntireColumnCount
    );
    await runExportTask(async () => {
      await exportSongsToBookPdf(songsToExport, {
        filename: `${fileBase}.pdf`,
        bookTitle,
        catalogLabel,
        categoryLabel,
        subtitle,
        theme: { background: "#ffffff", text: "#111111", accent: "#444444" },
        preferredFontId: selectedFontId,
        columns: preferredColumns,
        bodyFontSize,
        lineHeight: Math.round(bodyFontSize * 1.2),
        boldLyrics: false,
        settingsSummary: "Plain lines (stacked)",
        plainLines: true,
      });
    });
  };

  const onCurrentExportChange = async (event) => {
    const value = event.target.value;
    event.target.value = "";
    if (!value) return;
    if (value === "book:styled") {
      await handleBookPdfExport();
      return;
    }
    if (value === "book:plain") {
      await handlePlainBookPdfExport();
      return;
    }
    const currentValue = value.startsWith("view:") ? value.replace("view:", "") : value;
    await handleCurrentExport(currentValue);
  };

  const onBulkExportChange = async (event) => {
    const value = event.target.value;
    event.target.value = "";
    if (!value) return;
    const [scope, format] = value.split(":");
    if (!scope || !format) return;
    await handleBulkExport(scope, format);
  };

  // playYouTubeAudio: play hidden audio.
  const lastLoadedAudioRef = useRef(null);
  const playYouTubeAudio = useCallback(() => {
    if (suppressPlaybackRef.current) {
      setIsYouTubePlaying(false);
      return;
    }
    userPausedRef.current = false;
    if (!selectedYouTubeLink) {
      console.warn("No YT link available.");
      nextSong();
      return;
    }
    const vid = extractYouTubeID(selectedYouTubeLink);
    const audioPlayer = hiddenAudioPlayerRef.current;
    if (!vid || !audioPlayer || typeof audioPlayer.loadVideoById !== "function") {
      console.warn("Invalid YT ID or missing hidden player.");
      setIsYouTubePlaying(false);
      return;
    }
    if (lastLoadedAudioRef.current !== vid) {
      audioPlayer.loadVideoById(vid);
      lastLoadedAudioRef.current = vid;
    }
    if (typeof audioPlayer.setPlaybackRate === "function") {
      audioPlayer.setPlaybackRate(currentPlaybackRate);
    }
    if (typeof audioPlayer.playVideo === "function") {
      audioPlayer.playVideo();
    }
    setIsYouTubePlaying(true);
  }, [currentPlaybackRate, nextSong, selectedYouTubeLink]);

  // pauseYouTubeAudio: pause hidden audio.
  const pauseYouTubeAudio = useCallback(() => {
    if (hiddenAudioPlayerRef.current && typeof hiddenAudioPlayerRef.current.pauseVideo === "function") {
      hiddenAudioPlayerRef.current.pauseVideo();
      userPausedRef.current = true;
    }
  }, []);

  // pauseVideoModePlayer: pause large video.
  const pauseVideoModePlayer = useCallback(() => {
    if (videoModePlayerRef.current && typeof videoModePlayerRef.current.pauseVideo === "function") {
      videoModePlayerRef.current.pauseVideo();
      userPausedRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (!isEmbeddedRef.current || typeof window === "undefined") {
      return undefined;
    }

    const handleControlMessage = (event) => {
      if (!event || typeof event.data !== "object" || event.data === null) {
        return;
      }
      if (event.data.type !== EMBED_CONTROL_MESSAGE) {
        return;
      }
      const expectedOrigin = parentOriginRef.current;
      if (expectedOrigin !== "*" && event.origin && event.origin !== expectedOrigin) {
        return;
      }

      const action = event.data.action;
      if (action === "play") {
        if (videoLikeMode) {
          if (videoModePlayerRef.current && typeof videoModePlayerRef.current.playVideo === "function") {
            userPausedRef.current = false;
            videoModePlayerRef.current.playVideo();
          } else if (selectedYouTubeLink) {
            const videoID = extractYouTubeID(selectedYouTubeLink);
            if (videoID) {
              userPausedRef.current = false;
              createVideoPlayer(videoID, Math.max(0, audioTimeRef.current || 0));
            }
          }
        } else if (selectedYouTubeLink) {
          userPausedRef.current = false;
          playAudioLink(selectedYouTubeLink, Math.max(0, audioTimeRef.current || 0));
        } else {
          playYouTubeAudio();
        }
      } else if (action === "pause") {
        if (videoLikeMode) {
          pauseVideoModePlayer();
        } else {
          pauseYouTubeAudio();
        }
      }
    };

    window.addEventListener("message", handleControlMessage);
    return () => window.removeEventListener("message", handleControlMessage);
  }, [createVideoPlayer, displayMode, pauseVideoModePlayer, pauseYouTubeAudio, playAudioLink, playYouTubeAudio, selectedYouTubeLink]);

  // togglePlayPause: toggle play/pause.
  const togglePlayPause = () => {
    if (suppressPlaybackRef.current) return;
    if (videoLikeMode) {
      if (isYouTubePlaying) {
        pauseVideoModePlayer();
      } else if (selectedYouTubeLink) {
        const vid = extractYouTubeID(selectedYouTubeLink);
        if (vid && videoModePlayerRef.current) {
          userPausedRef.current = false;
          videoModePlayerRef.current.playVideo();
        }
      }
    } else {
      if (isYouTubePlaying) pauseYouTubeAudio();
      else playYouTubeAudio();
    }
  };

  // showPresenterModeCurrentNext: get current and next slide texts.
  const showPresenterModeCurrentNext = () => {
    const c = activeSlides[currentSlideIndex] || "";
    const nI = currentSlideIndex + 1;
    const n = nI < activeSlides.length ? activeSlides[nI] : "<i>No next</i>";
    return { current: c, next: n };
  };

  // handleAudioSeek: seek when dragging progress bar.
  const handleAudioSeek = useCallback((newTime) => {
    if (!Number.isFinite(newTime)) return;
    const clamped = Math.max(0, newTime);
    sliderValueRef.current = clamped;
    if (hiddenAudioPlayerRef.current && typeof hiddenAudioPlayerRef.current.seekTo === "function") {
      hiddenAudioPlayerRef.current.seekTo(clamped, true);
      audioTimeRef.current = clamped;
      setAudioProgress(clamped);
    }
  }, []);

  // formatTime: format seconds to mm:ss.
  const formatTime = (sec) => {
    const minutes = Math.floor(sec / 60);
    const seconds = Math.floor(sec % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const recalcFlowLayout = useCallback(
    (mode) => {
      const isNewsletter = mode === "newsletter";
      const contentRef = isNewsletter ? newsletterContentRef : entireContentRef;
      if (!contentRef.current) return;
      const contentEl = contentRef.current;
      const containerEl = contentEl.closest("#lyrics-container") || contentEl.parentElement;
      if (!containerEl) return;

      const minFontSize = isNewsletter ? 10 : MIN_ENTIRE_FONT_SIZE;
      const baseFontSize = isNewsletter
        ? Math.min(Math.max(Math.round(globalFontSize / 2.2), minFontSize), 72)
        : Math.min(Math.max(Math.round(globalFontSize * 0.7), minFontSize), 120);
      if (isNewsletter && isTabletOrBelow) {
        const gapPx = Math.max(8, Math.min(28, Math.floor(containerEl.clientWidth * 0.02) || 20));
        contentEl.style.columnCount = "1";
        contentEl.style.columnGap = `${gapPx}px`;
        contentEl.style.fontSize = `${baseFontSize}px`;
        contentEl.style.maxHeight = `${containerEl.clientHeight}px`;
        if (newsletterFontSize !== baseFontSize) {
          setNewsletterFontSize(baseFontSize);
        }
        return;
      }

      const clampColumns = (value) => Math.min(Math.max(value, 1), MAX_ENTIRE_COLUMNS);
      const minColumnWidth = isNewsletter ? MIN_FLOW_COLUMN_WIDTH : MIN_ENTIRE_COLUMN_WIDTH;
      const suggestedColumns = clampColumns(
        Math.max(1, Math.floor(containerEl.clientWidth / minColumnWidth))
      );

      const lockedColumns = entireColumnsLocked;
      const initialColumns = lockedColumns ? entireColumnCount : suggestedColumns;

      const gapPx = Math.max(8, Math.min(28, Math.floor(containerEl.clientWidth * 0.02) || 20));

      const applyColumnStyles = (cols, size) => {
        contentEl.style.columnCount = String(cols);
        contentEl.style.columnGap = cols > 1 ? `${gapPx}px` : "0px";
        contentEl.style.fontSize = `${size}px`;
        contentEl.style.maxHeight = `${containerEl.clientHeight}px`;
      };

      const fitsWithinBounds = () =>
        contentEl.scrollHeight <= containerEl.clientHeight + 1 &&
        contentEl.scrollWidth <= containerEl.clientWidth + 1;

      const evaluateForColumns = (cols) => {
        let size = baseFontSize;
        applyColumnStyles(cols, size);
        while (!fitsWithinBounds() && size > minFontSize) {
          size = Math.max(size - 2, minFontSize);
          applyColumnStyles(cols, size);
        }
        return fitsWithinBounds() ? size : null;
      };

      const maxAutoColumns = clampColumns(Math.max(2, Math.min(3, suggestedColumns || 1)));
      const columnCandidates = lockedColumns
        ? isNewsletter
          ? [initialColumns]
          : Array.from(
              { length: Math.max(1, clampColumns(Math.max(initialColumns, suggestedColumns)) - initialColumns + 1) },
              (_, i) => initialColumns + i
            )
        : Array.from({ length: maxAutoColumns }, (_, i) => i + 1);

      let bestColumns = initialColumns;
      let bestFontSize = evaluateForColumns(initialColumns);

      columnCandidates.forEach((cols) => {
        if (cols === initialColumns) return;
        const fitSize = evaluateForColumns(cols);
        if (fitSize === null) return;
        if (
          bestFontSize === null ||
          fitSize > bestFontSize ||
          (fitSize === bestFontSize && cols < bestColumns)
        ) {
          bestFontSize = fitSize;
          bestColumns = cols;
        }
      });

      if (bestFontSize === null) {
        bestFontSize = minFontSize;
        bestColumns = lockedColumns ? initialColumns : clampColumns(Math.max(2, suggestedColumns));
        applyColumnStyles(bestColumns, bestFontSize);
      }

      if (!lockedColumns) {
        setAutoEntireColumnCount((prev) => (prev === bestColumns ? prev : bestColumns));
      } else if (!isNewsletter && bestColumns !== entireColumnCount) {
        setEntireColumnCount(bestColumns);
        setAutoEntireColumnCount(bestColumns);
      }

      if (flowColumnGap !== gapPx) {
        setFlowColumnGap(gapPx);
      }

      // Ensure final layout reflects the best fit that was selected.
      applyColumnStyles(bestColumns, bestFontSize);

      if (isNewsletter) {
        if (newsletterFontSize !== bestFontSize) {
          setNewsletterFontSize(bestFontSize);
        }
      } else {
        if (entireFontSize !== bestFontSize) {
          setEntireFontSize(bestFontSize);
        }
      }
    },
    [
      globalFontSize,
      isTabletOrBelow,
      entireColumnsLocked,
      entireColumnCount,
      newsletterFontSize,
      entireFontSize,
      linesPerSlide,
    ]
  );

  useLayoutEffect(() => {
    if (displayMode !== "entire" || !currentSong) return;
    recalcFlowLayout("entire");
  }, [
    displayMode,
    currentSong,
    globalFontSize,
    controlsVisible,
    exportPreviewMode,
    youtubeLinks,
    linesPerSlide,
    recalcFlowLayout,
  ]);

  useLayoutEffect(() => {
    if (displayMode !== "newsletter" || !currentSong) return;
    recalcFlowLayout("newsletter");
  }, [
    displayMode,
    currentSong,
    globalFontSize,
    controlsVisible,
    exportPreviewMode,
    youtubeLinks,
    linesPerSlide,
    recalcFlowLayout,
  ]);

  useEffect(() => {
    if (displayMode !== "entire") return;
    const handleResize = () => recalcFlowLayout("entire");
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [displayMode, linesPerSlide, recalcFlowLayout]);

  useEffect(() => {
    if (displayMode !== "newsletter") return;
    const handleResize = () => recalcFlowLayout("newsletter");
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [displayMode, recalcFlowLayout]);

  useEffect(() => {
    if (displayMode === "newsletter") {
      recalcFlowLayout("newsletter");
    }
  }, [globalFontSize, displayMode, recalcFlowLayout]);

  useEffect(() => {
    if (displayMode !== "entire" || !entireContentRef.current) return;
    const imgs = Array.from(entireContentRef.current.getElementsByTagName("img"));
    if (!imgs.length) return;
    const handleImgLoad = () => recalcFlowLayout("entire");
    imgs.forEach((img) => img.addEventListener("load", handleImgLoad));
    return () => imgs.forEach((img) => img.removeEventListener("load", handleImgLoad));
  }, [displayMode, currentSong, linesPerSlide, recalcFlowLayout]);

  useEffect(() => {
    if (displayMode !== "newsletter" || !newsletterContentRef.current) return;
    const imgs = Array.from(newsletterContentRef.current.getElementsByTagName("img"));
    if (!imgs.length) return;
    const handleImgLoad = () => recalcFlowLayout("newsletter");
    imgs.forEach((img) => img.addEventListener("load", handleImgLoad));
    return () => imgs.forEach((img) => img.removeEventListener("load", handleImgLoad));
  }, [displayMode, currentSong, recalcFlowLayout]);

  useEffect(() => {
    if (displayMode !== "entire" && displayMode !== "newsletter") {
      setAutoEntireColumnCount(1);
      setEntireFontSize(globalFontSize);
      setNewsletterFontSize(Math.max(10, Math.round(globalFontSize / 3)));
    }
  }, [displayMode, globalFontSize, recalcFlowLayout]);

  useEffect(() => {
    if (displayMode === "entire") {
      recalcFlowLayout("entire");
    }
  }, [globalFontSize, displayMode, recalcFlowLayout]);

  useEffect(() => {
    if (!currentSong) {
      sliderValueRef.current = 0;
      setAudioProgress(0);
      setAudioDuration(0);
    }
  }, [currentSong]);

  useEffect(() => {
    if (columnPreferenceSource === "user") return;
    const nextColumns = clamp(
      getDefaultColumnsForMode(displayMode),
      1,
      MAX_ENTIRE_COLUMNS
    );
    if (nextColumns !== entireColumnCount) {
      setEntireColumnCount(nextColumns);
      setAutoEntireColumnCount(nextColumns);
    }
  }, [columnPreferenceSource, displayMode, entireColumnCount]);

  /**
   * 4) useEffects
   */

  // Insert YouTube Iframe API and create hidden audio player.
  useEffect(() => {
    const scriptId = "youtube-iframe-script";

    const attachPlayerWhenReady = () => {
      ensureHiddenPlayer();
    };

    if (window.YT && typeof window.YT.Player === "function") {
      attachPlayerWhenReady();
    } else {
      const previousCallback = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof previousCallback === "function") previousCallback();
        attachPlayerWhenReady();
      };
      if (!document.getElementById(scriptId)) {
        const tag = document.createElement("script");
        tag.id = scriptId;
        tag.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(tag);
      }
    }

    return () => {
      /* keep player for other pages; no teardown */
    };
  }, [ensureHiddenPlayer]);

  // Load organizations feed.
  useEffect(() => {
    window.populateBlogspotSelector = (data) => {
      if (!data.feed || !data.feed.entry) {
        setOrganizations([]);
        return;
      }
      const entries = data.feed.entry;
      const orgs = entries.map((entry) => {
        const title = entry.title.$t;
        const domain = normalizeCatalogDomain(title);
        return { title, domain };
      });
      setOrganizations(orgs);
      if (orgs.length > 0) {
        const queryDomain = initialQuery.domain ? resolveCatalogDomainParam(initialQuery.domain) : "";
        const fallbackDomain = orgs[0].domain;
        const nextDomain =
          queryDomain === ALL_CATALOGS_VALUE
            ? ALL_CATALOGS_VALUE
            : queryDomain && orgs.some((org) => org.domain === queryDomain)
            ? queryDomain
            : fallbackDomain;
        setSelectedDomain((prev) => {
          if (prev && (prev === ALL_CATALOGS_VALUE || orgs.some((org) => org.domain === prev))) {
            return prev;
          }
          return nextDomain;
        });
      }
    };
    const scriptId = "gcc-organizations-feed";
    let old = document.getElementById(scriptId);
    if (old) document.body.removeChild(old);
    const sc = document.createElement("script");
    sc.id = scriptId;
    sc.src = `https://gcc-organizations.blogspot.com/feeds/posts/default?alt=json-in-script&max-results=500&callback=populateBlogspotSelector`;
    document.body.appendChild(sc);
    return () => {
      window.populateBlogspotSelector = null;
      const s = document.getElementById(scriptId);
      if (s) document.body.removeChild(s);
    };
  }, []);

  useEffect(() => {
    const handleDomainEvent = (event) => {
      const incoming = resolveCatalogDomainParam(event?.detail || "");
      if (!incoming) return;
      setSelectedDomain((prev) => (prev === incoming ? prev : incoming));
    };
    window.addEventListener(DOMAIN_EVENT, handleDomainEvent);
    return () => window.removeEventListener(DOMAIN_EVENT, handleDomainEvent);
  }, []);

  // Load selected blog feed (songs).
  useEffect(() => {
    if (!selectedDomain) return undefined;
    setSongs([]);
    setFilteredSongs([]);
    setCurrentSong(null);
    setCustomSong(null);
    setOnDemandText("");
    setOnDemandError("");
    setCustomSelectedSlugs((prev) =>
      selectedDomain === initialDomainRef.current ? prev : [],
    );
    setCurrentSongIndex(0);
    setCurrentSlideIndex(0);
    setActiveSongTitle("");

    const cleanupFns = [];
    let isActive = true;
    const cleanup = () => {
      isActive = false;
      cleanupFns.forEach((fn) => fn());
    };
    const registerCleanup = (fn) => cleanupFns.push(fn);

    const EXCLUDED_CATEGORIES = new Set(["session", "system", "present"]);

    const buildSongFromEntry = (entry, categorySet) => {
      const title = entry.title.$t;
      const content = entry.content.$t;
      const rawCategories = entry.category ? entry.category.map((c) => c.term || "") : [];
      const normalized = rawCategories
        .map((cat) => (typeof cat === "string" ? cat.trim() : ""))
        .filter(Boolean);
      if (normalized.some((cat) => EXCLUDED_CATEGORIES.has(cat.toLowerCase()))) {
        return null;
      }
      const categories = normalized.filter(
        (cat) => !EXCLUDED_CATEGORIES.has(cat.toLowerCase())
      );
      categories.forEach((c) => {
        if (!EXCLUDED_CATEGORIES.has(c.toLowerCase())) {
          categorySet.add(c);
        }
      });
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = content;
      let yts = [];
      [...tempDiv.getElementsByTagName("a")].forEach((lnk) => {
        if (lnk.href.includes("youtube") || lnk.href.includes("youtu.be")) yts.push(lnk.href);
      });
      [...tempDiv.getElementsByTagName("iframe")].forEach((fr) => {
        if (fr.src.includes("youtube") || fr.src.includes("youtu.be")) yts.push(fr.src);
      });
      const ytre = /(https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)\/[^\s"'<>]+)/gi;
      let mch;
      while ((mch = ytre.exec(content)) !== null) {
        yts.push(mch[0]);
      }
      yts = [...new Set(yts)];
      let musescoreUrl = null;
      [...tempDiv.getElementsByTagName("a")].forEach((a) => {
        if (a.href.includes("musescore.com")) musescoreUrl = a.href;
      });
      if (!musescoreUrl) {
        const msr = /(https?:\/\/(?:www\.)?musescore\.com\/[^\s"'<>]+)/gi;
        const arr = content.match(msr);
        if (arr && arr.length > 0) musescoreUrl = arr[0];
      }
      const paragraphs = getParagraphsFromContent(tempDiv);
      const normalizedTitle = normalizeSearchText(title);
      const normalizedLyrics = normalizeSearchText(paragraphs.join(" "));
      const normalizedCategories = normalizeSearchText(categories.join(" "));
      return {
        title,
        categories,
        isEnglish: categories.includes("English"),
        youtubeUrls: yts,
        musescoreUrl,
        lyrics: paragraphs,
        normalizedTitle,
        normalizedLyrics,
        normalizedCategories,
      };
    };

    const finalizeSongs = (songList, categorySet) => {
      if (!isActive) return;
      if (!songList.length) {
        setSongs([]);
        setCategories([]);
        return;
      }
      const desiredCategory = initialCategoryRef.current;
      const isInitialDomain =
        initialDomainRef.current && selectedDomain
          ? selectedDomain === initialDomainRef.current
          : Boolean(!initialDomainRef.current);
      if (isInitialDomain && desiredCategory) {
        categorySet.add(desiredCategory);
      }
      const categoriesList = [
        ALL_CATEGORIES_OPTION,
        CUSTOM_CATEGORY_OPTION,
        ...Array.from(categorySet).filter((cat) => cat !== CUSTOM_CATEGORY_OPTION),
      ];
      setSongs(songList);
      setCategories(categoriesList);
      if (isInitialDomain) {
        const defaultCategory = ALL_CATEGORIES_OPTION;
        if (desiredCategory) {
          const desiredSlug = slugify(desiredCategory);
          const matchedCategory =
            categoriesList.find((cat) => slugify(cat) === desiredSlug) ||
            (desiredSlug === "all" || desiredSlug === "all-categories" ? defaultCategory : null);
          setSelectedCategory(matchedCategory || desiredCategory);
        } else {
          setSelectedCategory(defaultCategory);
        }
        setSearchKeyword(initialQuery.search || "");
      } else {
        setSearchKeyword("");
        setSelectedCategory(ALL_CATEGORIES_OPTION);
      }
    };

    const fetchCatalogFeed = (domain) =>
      new Promise((resolve, reject) => {
        const sanitized = domain.replace(/[^a-z0-9]/gi, "_");
        const callbackName = `gccCatalogFeed_${sanitized}_${Date.now()}_${Math.random()
          .toString(36)
          .slice(2, 10)}`;
        let cleaned = false;
        const cleanupScript = () => {
          if (cleaned) return;
          cleaned = true;
          const existing = document.getElementById(callbackName);
          if (existing && existing.parentNode) {
            existing.parentNode.removeChild(existing);
          }
          if (window[callbackName]) {
            delete window[callbackName];
          }
        };
        registerCleanup(cleanupScript);
        window[callbackName] = (data) => {
          cleanupScript();
          resolve({ domain, data });
        };
        const script = document.createElement("script");
        script.id = callbackName;
        script.src = `https://${domain}/feeds/posts/default?alt=json-in-script&max-results=500&callback=${callbackName}`;
        script.async = true;
        script.onerror = () => {
          cleanupScript();
          reject(new Error(`Failed to load catalog ${domain}`));
        };
        document.body.appendChild(script);
      });

    const loadSingleCatalog = async (domain) => {
      try {
        const response = await fetchCatalogFeed(domain);
        if (!isActive) return;
        const categorySet = new Set();
        const aggregated = [];
        response.data?.feed?.entry?.forEach((entry) => {
          const song = buildSongFromEntry(entry, categorySet);
          if (song) aggregated.push(song);
        });
        finalizeSongs(aggregated, categorySet);
      } catch (error) {
        console.warn("Failed to load catalog feed", domain, error);
      }
    };

    const loadAllCatalogs = async () => {
      try {
        const domains = Array.from(
          new Set(organizations.map((org) => org.domain).filter(Boolean))
        );
        if (!domains.length) {
          return;
        }
        const results = await Promise.all(
          domains.map((dom) =>
            fetchCatalogFeed(dom)
              .then((result) => result)
              .catch((error) => {
                console.warn("Failed to load catalog feed", dom, error);
                return null;
              })
          )
        );
        if (!isActive) return;
        const successful = results.filter(Boolean);
        if (!successful.length) {
          setSongs([]);
          setCategories([]);
          return;
        }
        const categorySet = new Set();
        const aggregated = [];
        successful.forEach((entry) => {
          entry.data?.feed?.entry?.forEach((songEntry) => {
            const song = buildSongFromEntry(songEntry, categorySet);
            if (song) aggregated.push(song);
          });
        });
        finalizeSongs(aggregated, categorySet);
      } catch (error) {
        console.warn("Failed to load catalog feeds", error);
      }
    };

    if (selectedDomain === ALL_CATALOGS_VALUE && !organizations.length) {
      return cleanup;
    }
    if (selectedDomain === ALL_CATALOGS_VALUE) {
      loadAllCatalogs();
    } else {
      loadSingleCatalog(selectedDomain);
    }

    return cleanup;
  }, [selectedDomain, organizations]);

  // Filter songs whenever songs/category/search change.
  useEffect(() => {
    filterSongs();
  }, [songs, selectedCategory, searchKeyword, filterSongs]);

  useEffect(() => {
    if (initialSongAppliedRef.current) return;
    if (!initialQuery.song) {
      initialSongAppliedRef.current = true;
      return;
    }
    if (!filteredSongs.length) return;
    const targetSlug = initialQuery.song.toLowerCase();
    const matchIndex = filteredSongs.findIndex((song) => slugify(song.title) === targetSlug);
    if (matchIndex >= 0) {
      loadSong(matchIndex);
    }
    initialSongAppliedRef.current = true;
  }, [filteredSongs]);

  useEffect(() => {
    if (pendingSlideRef.current === null) return;
    if (!activeSlides || !activeSlides.length) return;
    const targetSlide = pendingSlideRef.current;
    pendingSlideRef.current = null;
    const clampedSlide = Math.max(0, Math.min(targetSlide, activeSlides.length - 1));
    setCurrentSlideIndex(clampedSlide);
  }, [activeSlides]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (initialQuery.song && !initialSongAppliedRef.current) return;
    const params = new URLSearchParams(window.location.search || "");
    if (selectedDomain) {
      if (selectedDomain === ALL_CATALOGS_VALUE) {
        params.set("domain", "all");
      } else {
        params.set("domain", toShortDomainParam(selectedDomain));
      }
    } else {
      params.delete("domain");
    }
    if (selectedCategory && selectedCategory !== ALL_CATEGORIES_OPTION) {
      params.set("category", selectedCategory);
    } else {
      params.delete("category");
    }
    if (selectedCategory === CUSTOM_CATEGORY_OPTION && customSelectedSlugs.length) {
      params.set(CUSTOM_SONGS_PARAM, customSelectedSlugs.join(","));
    } else {
      params.delete(CUSTOM_SONGS_PARAM);
    }
    if (searchKeyword) params.set("search", searchKeyword);
    else params.delete("search");
    params.set("display", displayMode || "slide");
    if (linesPerSlide > 0) params.set("lines", String(linesPerSlide));
    else params.delete("lines");
    if (globalFontSize !== DEFAULT_FONT_SIZE) params.set("font", String(globalFontSize));
    else params.delete("font");
    if (autoAdvanceInterval > 0) params.set("auto", String(autoAdvanceInterval));
    else params.delete("auto");
    if (autoPlayNext) params.set("autonext", "1");
    else params.delete("autonext");
    if (selectedThemeId) {
      params.set("theme", selectedThemeId);
    }
    if (selectedThemeId === "custom") {
      params.set("bg", customBackgroundColor);
      params.set("text", customTextColor);
      if (customGradientEnabled) {
        params.set("gradient", "1");
        params.set("accent", normaliseHex(customGradientAccent) || customGradientAccent);
      } else {
        params.delete("gradient");
        params.delete("accent");
      }
    } else {
      params.delete("bg");
      params.delete("text");
      params.delete("gradient");
      params.delete("accent");
    }
    if (selectedFontId && selectedFontId !== FONT_OPTIONS[0].id) {
      params.set("fontFamily", selectedFontId);
    } else {
      params.delete("fontFamily");
    }
    if (lyricsBold) params.set("bold", "1");
    else params.delete("bold");
    if (entireColumnsLocked && entireColumnCount > 0) params.set("columns", String(entireColumnCount));
    else params.delete("columns");
    if (currentSong) {
      params.set("song", slugify(currentSong.title));
      if (isSlideLikeMode(displayMode) && currentSlideIndex > 0) {
        params.set("slide", String(currentSlideIndex));
      } else {
        params.delete("slide");
      }
    } else {
      params.delete("song");
      params.delete("slide");
    }
    if (songViewMode) params.set("songViewMode", songViewMode);
    else params.delete("songViewMode");
    if (lockSongNavigation) params.set("lockSong", "1");
    else params.delete("lockSong");
    const nextSearch = params.toString();
    const currentRelative = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const newRelative = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`;
    if (newRelative !== currentRelative) {
      window.history.replaceState(null, "", newRelative);
    }
  }, [
    selectedDomain,
    selectedCategory,
    searchKeyword,
    displayMode,
    linesPerSlide,
    globalFontSize,
    autoAdvanceInterval,
    autoPlayNext,
    selectedThemeId,
    customBackgroundColor,
    customTextColor,
    customGradientEnabled,
    customGradientAccent,
    selectedFontId,
    lyricsBold,
    entireColumnCount,
    entireColumnsLocked,
    currentSong,
    currentSlideIndex,
    initialQuery.song,
    songViewMode,
    lockSongNavigation,
    customSelectedSlugs,
  ]);

  // Keyboard shortcuts (including "G" for tiles).
  useEffect(() => {
    const handleKey = (e) => {
      const ae = document.activeElement;
      if (ae && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA")) {
        if (e.key === "Escape") {
          ae.blur();
        }
        return;
      }
      if (REMOTE_SLIDE_NEXT_KEYS.has(e.key)) {
        e.preventDefault();
        nextContent();
        return;
      }
      if (REMOTE_SLIDE_PREV_KEYS.has(e.key)) {
        e.preventDefault();
        prevContent();
        return;
      }
      if (e.key === "Tab" && e.shiftKey) {
        e.preventDefault();
        sendSessionFocusRequest("controls");
        return;
      }

      if (e.key === "Backspace") {
        e.preventDefault();
      }

      if (e.key === "ArrowRight" || e.key === "PageDown") {
        e.preventDefault();
        nextContent();
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        prevContent();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        prevSong();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        nextSong();
      } else if (e.key === " ") {
        e.preventDefault();
        togglePlayPause();
      } else if (e.key.toLowerCase() === "g") {
        // "G" shows tiles (back to catalog)
        setCurrentSong(null);
        setCustomSong(null);
        setParagraphSlides([]);
        setLineSlides([]);
        setActiveSlides([]);
        setActiveSongTitle("");
      } else if (
        e.key.toLowerCase() === "control" ||
        e.key.toLowerCase() === "h" ||
        e.key.toLowerCase() === "c"
      ) {
        toggleControls();
      } else if (e.key === ">" || e.key === ".") {
        e.preventDefault();
        changePlaybackRate(1);
      } else if (e.key === "<" || e.key === ",") {
        e.preventDefault();
        changePlaybackRate(-1);
      } else if (e.key.toLowerCase() === "l") {
        toggleLyricsBold();
      } else if (e.key.toLowerCase() === "y") {
        handleShareLinkCopy();
      } else if (e.key >= "1" && e.key <= "9") {
        const n = parseInt(e.key) - 1;
        if (youtubeLinks.length > 0) {
          const safe = Math.min(n, youtubeLinks.length - 1);
          setSelectedYouTubeLink(youtubeLinks[safe]);
          if (isYouTubePlaying) {
            playYouTubeAudio();
          } else if (isVideoLikeMode(displayMode)) {
            const vid = extractYouTubeID(youtubeLinks[safe]);
            if (vid) createVideoPlayer(vid);
          }
        }
      } else if (e.key.toLowerCase() === "s") {
        setDisplayMode("slide");
      } else if (e.key.toLowerCase() === "k") {
        setDisplayMode(CAMERA_SLIDES_MODE);
      } else if (e.key.toLowerCase() === "r") {
        setDisplayMode(CAMERA_PRESENT_MODE);
      } else if (e.key.toLowerCase() === "j") {
        setDisplayMode(CAMERA_VIDEO_MODE);
      } else if (e.key.toLowerCase() === "f") {
        setDisplayMode("entire");
      } else if (e.key.toLowerCase() === "v") {
        setDisplayMode("video");
      } else if (e.key.toLowerCase() === "m") {
        setDisplayMode("music");
      } else if (e.key.toLowerCase() === "p") {
        setDisplayMode("presenter");
      } else if (e.key.toLowerCase() === "n") {
        setDisplayMode("newsletter");
      } else if (e.key === "+" || e.key === "=") {
        setGlobalFontSize((p) => Math.min(p + 1, 150));
      } else if (e.key === "-" || e.key === "_") {
        setGlobalFontSize((p) => Math.max(p - 1, 10));
      } else if (e.key === "]" || e.key === "}") {
        e.preventDefault();
        adjustLinesOrColumns(1);
      } else if (e.key === "[" || e.key === "{") {
        e.preventDefault();
        adjustLinesOrColumns(-1);
      } else if (e.key === "*") {
        setAutoAdvanceInterval((p) => Math.min(p + 1, 120));
      } else if (e.key === "/") {
        setAutoAdvanceInterval((p) => Math.max(p - 1, 0));
      } else if (e.key === "?") {
        e.preventDefault();
        setHelpModalOpen(true);
      } else if (e.key.toLowerCase() === "t") {
        document.getElementById("display-mode-selector")?.focus();
      } else if (e.key.toLowerCase() === "o") {
        document.getElementById("blogspot-selector")?.focus();
      } else if (e.key.toLowerCase() === "b") {
        setDisplayMode("book");
      } else if (e.key.toLowerCase() === "i") {
        document.getElementById("song-selector")?.focus();
      } else if (e.key.toLowerCase() === "e") {
        document.getElementById("search-input")?.focus();
      } else if (e.key === "Alt" || e.key === "Option") {
        if (currentSong) {
          e.preventDefault();
          setTilesPanelOpen((prev) => !prev);
        }
      } else if (e.key.toLowerCase() === "d") {
        document.getElementById("lyrics")?.focus();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [
    nextContent,
    prevContent,
    prevSong,
    nextSong,
    togglePlayPause,
    toggleControls,
    toggleLyricsBold,
    handleShareLinkCopy,
    changePlaybackRate,
    youtubeLinks,
    isYouTubePlaying,
    displayMode,
    createVideoPlayer,
    adjustLinesOrColumns,
    setHelpModalOpen,
    currentSong,
    setTilesPanelOpen,
  ]);

  // Rebuild slides if linesPerSlide or currentSong changes.
  useEffect(() => {
    rebuildActiveSlidesInPlace();
  }, [linesPerSlide, currentSong]);

  useEffect(() => {
    if (!currentSong) {
      setActiveSongTitle("");
    }
  }, [currentSong, setActiveSongTitle]);

  useEffect(() => {
    preservedSettingsRef.current = {
      displayMode,
      linesPerSlide,
      globalFontSize,
      autoAdvanceInterval,
      selectedCategory,
    };
  }, [displayMode, linesPerSlide, globalFontSize, autoAdvanceInterval, selectedCategory]);

  useEffect(() => {
    if (!currentSong) {
      setTilesPanelOpen(false);
    }
  }, [currentSong]);

  useEffect(() => {
    if (tilesPanelPinned && currentSong) {
      setTilesPanelOpen(true);
    }
    if (!tilesPanelPinned && !tilesPanelOpen) {
      setTilesPanelSearch("");
    }
  }, [tilesPanelPinned, currentSong, tilesPanelOpen]);

  useEffect(() => {
    if (!tilesPanelOpen) {
      setTilesPanelSearch("");
    }
  }, [tilesPanelOpen]);

  useEffect(() => {
    if (!tilesPanelOpen) {
      setTilesPanelMinimized(false);
    }
  }, [tilesPanelOpen]);

  useEffect(() => {
    tilesPanelResizeStartWidth.current = tilesPanelWidth;
  }, [tilesPanelWidth]);

  useEffect(() => {
    if (!isResizingTilesPanel) return undefined;
    const handleMouseMove = (event) => {
      const delta = tilesPanelResizeStartX.current - event.clientX;
      const newWidth = Math.max(320, Math.min(520, tilesPanelResizeStartWidth.current + delta));
      setTilesPanelWidth(newWidth);
    };
    const handleMouseUp = () => setIsResizingTilesPanel(false);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizingTilesPanel]);

  const handleTilesPanelWidthChange = useCallback(
    (value) => {
      const parsed = Number.parseInt(value, 10);
      if (Number.isFinite(parsed)) {
        setTilesPanelWidth(Math.max(320, Math.min(520, parsed)));
      }
    },
    [setTilesPanelWidth],
  );

  useEffect(() => {
    if (!currentSong) return;
    const stored = preservedSettingsRef.current;
    if (!stored) return;
    if (displayMode !== stored.displayMode) {
      setDisplayMode(stored.displayMode);
    }
    if (linesPerSlide !== stored.linesPerSlide) {
      setLinesPerSlide(stored.linesPerSlide);
    }
    if (globalFontSize !== stored.globalFontSize) {
      setGlobalFontSize(stored.globalFontSize);
    }
    if (autoAdvanceInterval !== stored.autoAdvanceInterval) {
      setAutoAdvanceInterval(stored.autoAdvanceInterval);
    }
    if (selectedCategory !== stored.selectedCategory) {
      setSelectedCategory(stored.selectedCategory);
    }
  }, [
    currentSong,
    currentSongIndex,
    displayMode,
    linesPerSlide,
    globalFontSize,
    autoAdvanceInterval,
    selectedCategory,
  ]);

  // Auto-advance timer.
  useEffect(() => {
    if (autoAdvanceInterval > 0) {
      startAutoAdvance(autoAdvanceInterval);
    } else {
      stopAutoAdvance();
    }
    return () => stopAutoAdvance();
  }, [autoAdvanceInterval, nextContent]);

  // When switching display modes, transfer playback time.
  useEffect(() => {
    if (suppressPlaybackRef.current) return;
    const videoLike = isVideoLikeMode(displayMode);
    if (videoLike) {
      const startSeconds = audioTimeRef.current || 0;
      if (isYouTubePlaying && hiddenAudioPlayerRef.current) {
        pauseYouTubeAudio();
      }
      const videoID = extractYouTubeID(selectedYouTubeLink);
      if (selectedYouTubeLink && videoID) {
        createVideoPlayer(videoID, startSeconds);
      }
    } else {
      if (videoModePlayerRef.current && isYouTubePlaying) {
        const videoPlayer = videoModePlayerRef.current;
        const time =
          typeof videoPlayer.getCurrentTime === "function" ? videoPlayer.getCurrentTime() || 0 : 0;
        pauseVideoModePlayer();
        const data =
          typeof videoPlayer.getVideoData === "function" ? videoPlayer.getVideoData() : null;
        const vid = data && data.video_id;
        const audioPlayer = hiddenAudioPlayerRef.current;
        if (vid && audioPlayer && typeof audioPlayer.loadVideoById === "function") {
          audioPlayer.loadVideoById({ videoId: vid, startSeconds: time });
          if (typeof audioPlayer.setPlaybackRate === "function") {
            audioPlayer.setPlaybackRate(currentPlaybackRate);
          }
          if (typeof audioPlayer.playVideo === "function") {
            audioPlayer.playVideo();
          }
          setIsYouTubePlaying(true);
        }
      } else {
        pauseVideoModePlayer();
      }
    }
  }, [displayMode, selectedYouTubeLink, createVideoPlayer]);

  // Update playback rate for both players.
  useEffect(() => {
    if (isVideoLikeMode(displayMode) && videoModePlayerRef.current) {
      if (typeof videoModePlayerRef.current.setPlaybackRate === "function") {
        videoModePlayerRef.current.setPlaybackRate(currentPlaybackRate);
      } else {
        console.warn("videoModePlayerRef is not a valid YT.Player yet.");
      }
    }
    if (hiddenAudioPlayerRef.current) {
      if (typeof hiddenAudioPlayerRef.current.setPlaybackRate === "function") {
        hiddenAudioPlayerRef.current.setPlaybackRate(currentPlaybackRate);
      } else {
        console.warn("hiddenAudioPlayerRef is not a valid YT.Player yet.");
      }
    }
  }, [currentPlaybackRate, displayMode]);

  // On window resize, adjust font size.
  const getMaxFontSize = () => {
    const w = window.innerWidth;
    if (w <= 400) return 30;
    if (w <= 600) return 40;
    if (w <= 1024) return 60;
    return DEFAULT_FONT_SIZE;
  };
  useEffect(() => {
    const handleResize = () => {
      const maxF = getMaxFontSize();
      if (globalFontSize > maxF) setGlobalFontSize(maxF);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [globalFontSize]);

  /**
   * 5) Tile Light Mode (unchanged)
   */
  const vibrantColors = [
    "#FF5733",
    "#33FF57",
    "#3357FF",
    "#F1C40F",
    "#8E44AD",
    "#E67E22",
    "#1ABC9C",
    "#E74C3C",
    "#2ECC71",
    "#3498DB",
  ];
  const getRandomInt = (mn, mx) => Math.floor(Math.random() * (mx - mn + 1)) + mn;
  const changeTileColor = (tile) => {
    const oldBG = tile.style.backgroundColor || "white";
    const oldTX = tile.style.color || "black";
    const newC = vibrantColors[getRandomInt(0, vibrantColors.length - 1)];
    tile.style.backgroundColor = newC;
    tile.style.color = "white";
    setTimeout(() => {
      tile.style.backgroundColor = oldBG;
      tile.style.color = oldTX;
    }, 1000);
  };
  const randomizeTileColors = () => {
    const tiles = document.querySelectorAll(".tile");
    if (!tiles.length) return;
    const num = getRandomInt(1, Math.min(tiles.length, 5));
    for (let i = 0; i < num; i++) {
      const rnd = getRandomInt(0, tiles.length - 1);
      changeTileColor(tiles[rnd]);
    }
  };
  const initTileLighting = () => {
    if (tileLightModeActive) return;
    setTileLightModeActive(true);
    tileLightIntervalRef.current = setInterval(randomizeTileColors, 1000);
  };
  const deactivateTileLightMode = () => {
    if (!tileLightModeActive) return;
    clearInterval(tileLightIntervalRef.current);
    tileLightIntervalRef.current = null;
    setTileLightModeActive(false);
  };
  const handleToggleTileLight = () => {
    if (!tileLightModeActive) initTileLighting();
    else deactivateTileLightMode();
  };

  /**
   * 6) Render
   */

  // getSlideContent: return current slide content.
  const getSlideContent = () => {
    if (!activeSlides || !activeSlides.length) return "";
    if (currentSlideIndex < 0) return "";
    if (currentSlideIndex >= activeSlides.length) {
      nextSong();
      return "";
    }
    return activeSlides[currentSlideIndex];
  };

  const currentSongIsMultilingual = useMemo(() => isSongMultilingual(currentSong), [currentSong, isSongMultilingual]);

  const applyParentheticalOpacity = useCallback(
    (html) => {
      if (!currentSongIsMultilingual || !html) return html;
      return html.replace(/\(([^)]+)\)/g, '<span class="lyrics-parenthetical">($1)</span>');
    },
    [currentSongIsMultilingual]
  );

  const formatLyricsHtml = useCallback(
    (value) => {
      if (value === null || value === undefined) return "";
      const text = String(value);
      if (text.toLowerCase().includes("<img")) {
        return text;
      }
      const withBreaks = text.replace(/\n/g, "<br>");
      const withParens = applyParentheticalOpacity(withBreaks);
      const withBrackets = withParens.replace(/\[([^\]]+)\]/g, '<span class="lyrics-bracket">[$1]</span>');
      return withBrackets;
    },
    [applyParentheticalOpacity]
  );

  const formatSlideHtmlForBook = (value) => {
    if (value === null || value === undefined) return "";
    const text = String(value);
    if (text.toLowerCase().includes("<img")) {
      return text;
    }
    return text.replace(/\n/g, "<br>");
  };

  const isTitleSlide = () => currentSlideIndex === 0;
  const currentSongIsEnglish = currentSong?.isEnglish;
  const presenterMode = showPresenterModeCurrentNext();
  // Always show export controls so exports are available even when a watermark is applied.
  const showExportControls = Boolean(initialQuery.admin);

  useEffect(() => {
    const allowTypeAhead = (showTiles || controlsVisible) && !exportPreviewMode;
    if (!allowTypeAhead) return undefined;

    const startTypeAheadSearch = () => {
      setTypeAheadSearching(true);
      if (controlsVisible) {
        setControlsVisible(false);
      }
    };

    const handleTypeToSearch = (event) => {
      if (event.defaultPrevented) {
        return;
      }
      const target = event.target;
      if (!target) return;
      const tagName = target.tagName ? target.tagName.toLowerCase() : "";
      const isEditable =
        target.isContentEditable ||
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select";
      if (isEditable) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === "?") {
        event.preventDefault();
        setHelpModalOpen(true);
        return;
      }

      if (event.key === "Backspace") {
        event.preventDefault();
        startTypeAheadSearch();
        setSearchKeyword((prev) => prev.slice(0, -1));
        return;
      }
      if (event.key === "Escape") {
        if (searchKeyword) {
          event.preventDefault();
          startTypeAheadSearch();
          setSearchKeyword("");
        }
        return;
      }
      if (event.key.length === 1) {
        event.preventDefault();
        startTypeAheadSearch();
        setSearchKeyword((prev) => `${prev}${event.key}`);
      }
    };

    window.addEventListener("keydown", handleTypeToSearch);
    return () => window.removeEventListener("keydown", handleTypeToSearch);
  }, [controlsVisible, exportPreviewMode, searchKeyword, showTiles, setHelpModalOpen]);

  useEffect(() => {
    if (!typeAheadSearching) return;
    if (controlsVisible || !searchKeyword) {
      setTypeAheadSearching(false);
    }
  }, [controlsVisible, searchKeyword, typeAheadSearching]);
  // Always show lyrics box (so Back button is visible) when a song is selected.
  const normalizedThemeBackground = normaliseHex(activeTheme.background) || "#030303";
  const themeAnchorColor = selectedThemeId === "classic" ? "#030303" : normalizedThemeBackground;
  const controlsSurfaceColor = toColorWithAlpha(
    blendHexColors(themeAnchorColor, "#000000", 0.72),
    0.95
  );
  const controlsBorderColor = "rgba(255, 255, 255, 0.18)";
  const tileBackgroundColor = "rgba(255, 255, 255, 0.02)";
  const tileBorderColor = "rgba(255, 255, 255, 0.12)";
  const interactiveFillColor = "rgba(255, 255, 255, 0.06)";
  const interactiveHoverColor = "rgba(255, 255, 255, 0.16)";
  const sliderAccentColor = "#ff0000";
  const sliderTrackColor = "rgba(255, 255, 255, 0.18)";
  const controlsRowGap = isMobileViewport ? 4 : isTabletOrBelow ? 8 : 10;
  const controlsContainerStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: isMobileViewport ? 6 : isTabletOrBelow ? 10 : 10,
    width: isTabletOrBelow ? "100%" : "clamp(720px, 92vw, 1180px)",
    margin: isTabletOrBelow ? "0 auto" : "12px auto 0",
    boxSizing: "border-box",
    color: activeTheme.text,
    fontFamily: activeFont.value,
    position: "sticky",
    top: isTabletOrBelow ? 0 : 12,
    left: 0,
    right: 0,
    padding: isMobileViewport ? "10px 14px" : isTabletOrBelow ? "12px clamp(12px, 4vw, 24px)" : "16px 20px",
    backgroundColor: controlsSurfaceColor,
    borderRadius: isTabletOrBelow ? 0 : 18,
    border: `1px solid ${controlsBorderColor}`,
    boxShadow: "0 18px 36px rgba(0, 0, 0, 0.32)",
    backdropFilter: "blur(12px)",
    zIndex: 1000,
  };
  const baseControlsRowStyle = {
    display: "flex",
    flexDirection: "row",
    flexWrap: isMobileViewport ? "wrap" : "nowrap",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: controlsRowGap,
    width: "100%",
  };
  const topControlsRowStyle = isMobileViewport
    ? {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(140px, 1fr))",
        columnGap: 8,
        rowGap: 6,
        width: "100%",
        marginBottom: 6,
        alignItems: "center",
      }
    : isTabletOrBelow
    ? {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
        columnGap: 10,
        rowGap: 8,
        width: "100%",
        marginBottom: 12,
        alignItems: "center",
        gridAutoFlow: "row dense",
      }
    : {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        columnGap: 12,
        rowGap: 8,
        alignItems: "center",
        width: "100%",
        marginBottom: 14,
        gridAutoFlow: "row dense",
      };
  const bottomControlsRowStyle = isMobileViewport
    ? {
        display: "grid",
        gridTemplateColumns: "1fr",
        rowGap: 4,
        columnGap: 0,
        width: "100%",
        alignItems: "center",
      }
    : isTabletOrBelow
    ? {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
        columnGap: 10,
        rowGap: 8,
        width: "100%",
        alignItems: "center",
        gridAutoFlow: "row dense",
      }
    : {
        ...baseControlsRowStyle,
      };
  const themeControlsRowStyle = isTabletOrBelow
    ? {
        ...baseControlsRowStyle,
        marginTop: isMobileViewport ? 6 : 8,
      }
    : {
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(180px, 1fr))",
        columnGap: 12,
        rowGap: 8,
        width: "100%",
        marginTop: 12,
        alignItems: "center",
      };
  const exportControlsRowStyle = isTabletOrBelow
    ? {
        ...baseControlsRowStyle,
        marginTop: isMobileViewport ? 6 : 8,
      }
    : {
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(180px, 1fr))",
        columnGap: 12,
        rowGap: 8,
        width: "100%",
        marginTop: 12,
        alignItems: "center",
      };
  const typographyControlsRowStyle = isTabletOrBelow
    ? {
        ...baseControlsRowStyle,
      }
    : {
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(180px, 1fr))",
        columnGap: 12,
        rowGap: 8,
        width: "100%",
        alignItems: "center",
      };
  const sliderLabelStyle = isTabletOrBelow
    ? {
        display: "flex",
        alignItems: "center",
        gap: 6,
        color: "#ccc",
        marginLeft: 0,
        width: "100%",
        flex: "1 1 220px",
        fontFamily: activeFont.value,
      }
    : {
        marginLeft: 0,
        color: "#ccc",
        display: "flex",
        alignItems: "center",
        gap: 6,
        flex: "1 1 220px",
        minWidth: 180,
        maxWidth: 320,
        fontFamily: activeFont.value,
      };
  const autoSliderLabelStyle = isTabletOrBelow
    ? { ...sliderLabelStyle }
    : {
        color: "#ccc",
        marginLeft: 0,
        display: "flex",
        alignItems: "center",
        gap: 6,
        flex: "1 1 220px",
        minWidth: 180,
        maxWidth: 320,
        fontFamily: activeFont.value,
      };
  const sliderClusterStyle = isMobileViewport
    ? {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(140px, 1fr))",
        columnGap: 8,
        rowGap: 6,
        width: "100%",
        margin: "2px 0",
      }
    : isTabletOrBelow
    ? {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
        columnGap: 12,
        rowGap: 6,
        flex: "1 1 100%",
        width: "100%",
        alignItems: "center",
      }
    : {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        columnGap: 12,
        rowGap: 6,
        flex: "1 1 480px",
        alignItems: "center",
      };
  const sliderInputStyle = isMobileViewport
    ? { flex: "1 1 120px", minWidth: 120, width: "100%" }
    : isTabletOrBelow
    ? { flex: "1 1 160px", minWidth: 140, width: "100%" }
    : { flex: "1 1 200px", minWidth: 180, maxWidth: 260, width: "100%" };
  const sliderValueStyle = isMobileViewport
    ? { minWidth: 28, textAlign: "right", fontFamily: activeFont.value }
    : isTabletOrBelow
    ? { minWidth: 32, textAlign: "right", fontFamily: activeFont.value }
    : { marginLeft: 6, fontFamily: activeFont.value };
  const fieldFlexStyle = isMobileViewport
    ? { flex: "1 1 140px", minWidth: 130, fontFamily: activeFont.value }
    : isTabletOrBelow
    ? { flex: "1 1 180px", minWidth: 150, fontFamily: activeFont.value }
    : { flex: "1 1 200px", minWidth: 140, maxWidth: 260, fontFamily: activeFont.value };
  const songViewToggleContainerStyle = {
    display: "inline-flex",
    borderRadius: 999,
    overflow: "hidden",
    border: `1px solid ${tileBorderColor}`,
    backgroundColor: toColorWithAlpha("#ffffff", 0.03),
    boxShadow: "0 6px 18px rgba(0, 0, 0, 0.55)",
    alignSelf: isMobileViewport ? "stretch" : "center",
  };
  const toggleActiveBackground = toColorWithAlpha("#ffffff", 0.12);
  const songViewToggleButtonStyle = (mode) => ({
    flex: 1,
    padding: isMobileViewport ? "3px 6px" : "4px 10px",
    fontSize: isMobileViewport ? 11 : 12,
    fontWeight: songViewMode === mode ? 700 : 500,
    border: "none",
    backgroundColor: songViewMode === mode ? toggleActiveBackground : "transparent",
    color: "#ffffff",
    cursor: "pointer",
    transition: "background-color 0.2s ease, color 0.2s ease",
  });
  const tileFontSettings = useMemo(() => {
    const baseSize = globalFontSize / 4;
    if (songViewMode === "tile") {
      return { base: isMobileViewport ? baseSize : baseSize + 6, min: 16 };
    }
    if (songViewMode === "cards") {
      return { base: isMobileViewport ? baseSize - 2 : baseSize + 2, min: 14 };
    }
    if (songViewMode === "buttons") {
      return { base: isMobileViewport ? baseSize + 1 : baseSize + 2, min: isMobileViewport ? 15 : 14 };
    }
    if (songViewMode === "list") {
      return { base: isMobileViewport ? baseSize - 2 : baseSize + 2, min: 15 };
    }
    if (songViewMode === "carousel") {
      return { base: isMobileViewport ? baseSize + 2 : baseSize + 8, min: 18 };
    }
    return { base: isMobileViewport ? baseSize - 4 : baseSize - 2, min: 13 };
  }, [songViewMode, isMobileViewport, globalFontSize]);

  const computeTileFontSize = useCallback(
    (title) => {
      const { base, min } = tileFontSettings;
      const length = (title || "").length;
      const scale =
        length > 64 ? 0.72 : length > 48 ? 0.8 : length > 36 ? 0.88 : 1;
      return Math.max(min, Math.round(base * scale));
    },
    [tileFontSettings]
  );

  const uniformTileFontSize = useMemo(() => {
    if (songViewMode !== "tile") return null;
    if (isMobileViewport) return 18;
    if (isTabletOrBelow) return 20;
    return 22;
  }, [songViewMode, isMobileViewport, isTabletOrBelow]);
  const tileContainerStyle = (() => {
    const topOffset = `calc(var(--site-header-height, 72px) + ${isTabletOrBelow ? "8px" : "12px"})`;
    const shared = {
      backgroundColor: activeTheme.background,
      backgroundImage: activeTheme.gradient || "none",
      backgroundSize: activeTheme.gradient ? "cover" : "auto",
      backgroundRepeat: activeTheme.gradient ? "no-repeat" : "repeat",
      color: activeTheme.text,
      padding: isTabletOrBelow ? "16px 12px" : "24px",
      paddingTop: topOffset,
      fontFamily: activeFont.value,
      fontWeight: lyricsBold ? 600 : 400,
    };
    if (!showTiles) {
      return { ...shared, display: "none" };
    }
    if (songViewMode === "tile") {
      return {
        ...shared,
        display: "grid",
        gap: isMobileViewport ? 10 : isTabletOrBelow ? 14 : 18,
        gridTemplateColumns: isMobileViewport
          ? "repeat(1, minmax(0, 1fr))"
          : "repeat(auto-fit, minmax(220px, 1fr))",
        alignItems: "stretch",
        justifyItems: "stretch",
      };
    }
    if (songViewMode === "cards") {
      return {
        ...shared,
        display: "grid",
        gap: isTabletOrBelow ? 14 : 24,
        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
        alignItems: "stretch",
        justifyItems: "center",
      };
    }
    if (songViewMode === "buttons") {
      return {
        ...shared,
        display: "flex",
        flexWrap: "wrap",
        gap: isTabletOrBelow ? 10 : 12,
        alignItems: "stretch",
        justifyContent: "center",
      };
    }
    if (songViewMode === "list") {
      return {
        ...shared,
        display: "flex",
        flexDirection: "column",
        gap: isTabletOrBelow ? 10 : 12,
        alignItems: "stretch",
        paddingTop: isTabletOrBelow ? 10 : 16,
      };
    }
    if (songViewMode === "carousel") {
      return {
        ...shared,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobileViewport ? "18px 12px" : "32px 24px",
        gap: isMobileViewport ? 16 : 20,
        minHeight: "50vh",
      };
    }
    return {
      ...shared,
      display: "grid",
      gap: isMobileViewport ? 10 : isTabletOrBelow ? 14 : 20,
      gridTemplateColumns: isMobileViewport
        ? "repeat(1, minmax(0, 1fr))"
        : isTabletOrBelow
        ? "repeat(auto-fit, minmax(280px, 1fr))"
        : "repeat(auto-fit, minmax(400px, 1fr))",
      alignItems: "stretch",
      justifyItems: "center",
    };
  })();
  const normalizeTileTitleSegment = (value) => {
    const cleaned = (value || "")
      .replace(/[^a-z0-9]+/gi, " ")
      .trim();
    return cleaned ? cleaned.toUpperCase() : ALL_CATEGORIES_OPTION.toUpperCase();
  };
  const tileTitleText = `${normalizeTileTitleSegment(
    formatDomainOptionLabel(selectedDomain),
  )} : ${normalizeTileTitleSegment(selectedCategory || ALL_CATEGORIES_OPTION)}`;
  useEffect(() => {
    setActiveCatalogTitle(tileTitleText);
    return () => setActiveCatalogTitle("");
  }, [tileTitleText, setActiveCatalogTitle]);
  const tilePageTitleSectionStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: isMobileViewport ? "20px 0 14px" : "26px 0 18px",
    padding: isTabletOrBelow ? "0 12px" : 0,
    fontFamily: activeFont.value,
    color: activeTheme.text,
  };
  const tilePageTitleTextStyle = {
    fontSize: isMobileViewport ? 20 : isTabletOrBelow ? 22 : 26,
    fontWeight: 700,
    letterSpacing: 0.7,
    textTransform: "uppercase",
    textAlign: "center",
  };
  const onDemandTextareaStyle = {
    width: "100%",
    flex: 1,
    minHeight: 240,
    backgroundColor: hexToRgbaString(activeTheme.background, 0.9),
    border: `1px solid ${hexToRgbaString(activeTheme.text, 0.25)}`,
    borderRadius: 10,
    padding: "12px 14px",
    color: activeTheme.text,
    fontFamily: activeFont.value,
    fontSize: isMobileViewport ? 14 : 15,
    resize: "vertical",
    lineHeight: 1.6,
  };
  const onDemandSubmitButtonStyle = {
    width: 36,
    height: 36,
    borderRadius: 999,
    border: `1px solid ${hexToRgbaString(activeTheme.text, 0.3)}`,
    backgroundColor: hexToRgbaString(activeTheme.text, 0.12),
    color: activeTheme.text,
    fontSize: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  };
  const onDemandErrorStyle = {
    marginTop: 8,
    fontSize: 12,
    color: "#ef5350",
  };
  const onDemandPopupOverlayStyle = {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 1600,
  };
  const onDemandPopupContentStyle = {
    width: "clamp(460px, 80vw, 960px)",
    minHeight: isMobileViewport ? 360 : 420,
    backgroundColor: hexToRgbaString(activeTheme.background, 0.95),
    border: `1px solid ${hexToRgbaString(activeTheme.text, 0.25)}`,
    borderRadius: 16,
    padding: isMobileViewport ? 16 : 32,
    boxShadow: "0 24px 60px rgba(0,0,0,0.48)",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  };
  const onDemandPopupHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };
  const onDemandPopupCloseStyle = {
    border: "none",
    background: "transparent",
    color: activeTheme.text,
    fontSize: 24,
    cursor: "pointer",
  };
  const viewModeTileColors = (() => {
    if (songViewMode === "grid") {
      return {
        background: blendHexColors(tileBackgroundColor, activeTheme.text, 0.2),
        border: blendHexColors(tileBorderColor, activeTheme.text, 0.35),
      };
    }
    if (songViewMode === "tile") {
      return {
        background: hexToRgbaString(activeTheme.text, 0.08),
        border: hexToRgbaString(activeTheme.text, 0.2),
      };
    }
    if (songViewMode === "buttons") {
      return {
        background: hexToRgbaString(activeTheme.text, 0.15),
        border: hexToRgbaString(activeTheme.text, 0.25),
      };
    }
    if (songViewMode === "list") {
      return {
        background: hexToRgbaString(activeTheme.text, 0.1),
        border: hexToRgbaString(activeTheme.text, 0.2),
      };
    }
    if (songViewMode === "carousel") {
      return {
        background: blendHexColors(tileBackgroundColor, activeTheme.text, 0.12),
        border: hexToRgbaString(activeTheme.text, 0.24),
      };
    }
    return {
      background: tileBackgroundColor,
      border: tileBorderColor,
    };
  })();
  const tileItemBaseStyle = {
    backgroundColor: viewModeTileColors.background,
    color: activeTheme.text,
    border: `1px solid ${viewModeTileColors.border}`,
    fontFamily: activeFont.value,
    fontWeight: lyricsBold ? 600 : 500,
    fontSize: uniformTileFontSize || undefined,
    backgroundImage: songViewMode === "tile" ? "none" : activeTheme.gradient || "none",
    backgroundBlendMode: activeTheme.gradient ? "overlay" : "normal",
    display: "flex",
    alignItems: "center",
    justifyContent: songViewMode === "list" ? "space-between" : "center",
    textAlign: songViewMode === "list" ? "left" : "center",
    cursor: "pointer",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    flex: songViewMode === "buttons" ? "0 1 auto" : "0 1 1",
    padding: songViewMode === "list" ? (isMobileViewport ? "12px 16px" : "14px 22px") : undefined,
  };
  const tileItemVariantStyle = (() => {
    if (songViewMode === "tile") {
      return {
        width: "100%",
        padding: isMobileViewport ? 14 : 18,
        minHeight: isMobileViewport ? 120 : 140,
        borderRadius: 16,
        boxShadow: "0 16px 32px rgba(0, 0, 0, 0.25)",
        backgroundImage: "none",
      };
    }
    if (songViewMode === "cards") {
      return {
        width: "100%",
        padding: isMobileViewport ? 16 : 24,
        minHeight: isMobileViewport ? 150 : 180,
        borderRadius: 20,
        boxShadow: "0 20px 40px rgba(0, 0, 0, 0.24)",
      };
    }
    if (songViewMode === "buttons") {
      return {
        padding: isMobileViewport ? "9px 12px" : "10px 16px",
        borderRadius: 999,
        minHeight: "auto",
        boxShadow: "none",
        flex: isMobileViewport ? "1 1 100%" : "0 0 auto",
        minWidth: isMobileViewport ? "100%" : undefined,
        maxWidth: isMobileViewport ? "100%" : undefined,
        width: isMobileViewport ? "100%" : undefined,
        margin: isMobileViewport ? "2px 0" : "4px 4px 4px 0",
      };
    }
    if (songViewMode === "list") {
      return {
        width: "100%",
        borderRadius: 14,
        boxShadow: "0 12px 28px rgba(0, 0, 0, 0.18)",
        marginBottom: isMobileViewport ? 6 : 8,
      };
    }
    if (songViewMode === "carousel") {
      return {
        width: "100%",
        display: "grid",
        gridTemplateColumns: "1fr",
        padding: isMobileViewport ? "24px 18px" : "32px 32px",
        borderRadius: 26,
        boxShadow: "0 30px 60px rgba(0, 0, 0, 0.28)",
        gap: 16,
        justifyItems: "center",
        alignItems: "center",
        maxWidth: isMobileViewport ? "100%" : "min(900px, 90vw)",
        minHeight: "50vh",
      };
    }
    return {
      width: "100%",
      padding: isMobileViewport ? 14 : 20,
      minHeight: isMobileViewport ? 115 : 140,
      borderRadius: 18,
      boxShadow: "0 16px 34px rgba(0, 0, 0, 0.22)",
    };
  })();
  const tileItemStyle = { ...tileItemBaseStyle, ...tileItemVariantStyle };
  const floatingOnDemandButtonStyle = {
    position: "fixed",
    bottom: isMobileViewport ? 80 : 110,
    right: isMobileViewport ? 12 : 24,
    width: isMobileViewport ? 52 : 58,
    height: isMobileViewport ? 52 : 58,
    borderRadius: "50%",
    backgroundColor: tileItemStyle.backgroundColor,
    border: tileItemStyle.border,
    color: tileItemStyle.color,
    fontFamily: activeFont.value,
    fontSize: isMobileViewport ? 20 : 26,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 18px 32px rgba(0, 0, 0, 0.35)",
    zIndex: 1400,
  };
  const handleCarouselPrev = useCallback(() => {
    if (tileSongs.length <= 1) return;
    setCarouselIndex((prev) => (prev - 1 + tileSongs.length) % tileSongs.length);
  }, [tileSongs.length]);

  const handleCarouselNext = useCallback(() => {
    if (tileSongs.length <= 1) return;
    setCarouselIndex((prev) => (prev + 1) % tileSongs.length);
  }, [tileSongs.length]);

  const handleCarouselTouchStart = useCallback((event) => {
    if (!event.touches || event.touches.length === 0) return;
    const touch = event.touches[0];
    carouselTouchStartRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const handleCarouselTouchEnd = useCallback(
    (event) => {
      if (!event.changedTouches || event.changedTouches.length === 0) return;
      const touch = event.changedTouches[0];
      const start = carouselTouchStartRef.current;
      const deltaX = touch.clientX - start.x;
      const deltaY = touch.clientY - start.y;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      const threshold = 40;
      if (absX < threshold && absY < threshold) return;
      if (absX >= absY) {
        if (deltaX < 0) {
          handleCarouselNext();
        } else {
          handleCarouselPrev();
        }
      } else {
        if (deltaY < 0) {
          handleCarouselNext();
        } else {
          handleCarouselPrev();
        }
      }
    },
    [handleCarouselNext, handleCarouselPrev]
  );
  const compactButtonStyle = isMobileViewport
    ? { alignSelf: "flex-start", marginTop: 2 }
    : isTabletOrBelow
    ? { alignSelf: "flex-start", marginTop: 4 }
    : {};
  const navButtonBaseStyle = {
    backgroundColor: "rgba(255, 255, 255, 0.04)",
    color: "#ffffff",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    fontFamily: activeFont.value,
    boxShadow: "none",
    transition: "transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease",
    opacity: 1,
    position: "static",
    transform: "translateY(0)",
    borderRadius: 999,
    padding: 0,
    minWidth: isTabletOrBelow ? 44 : 48,
    minHeight: isTabletOrBelow ? 44 : 48,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: isTabletOrBelow ? 18 : 20,
    bottom: "auto",
    left: "auto",
    margin: 0,
    cursor: "pointer",
  };
  const tilesPanelToggleButtonStyle = {
    ...navButtonBaseStyle,
    borderRadius: 999,
    padding: isTabletOrBelow ? "6px 14px" : "8px 16px",
    minWidth: isTabletOrBelow ? 44 : 52,
    minHeight: isTabletOrBelow ? 44 : 48,
    order: 2,
  };
  const carouselButtonStyle = {
    ...navButtonBaseStyle,
    borderRadius: 999,
    padding: isMobileViewport ? "10px 14px" : "12px 18px",
    minWidth: isMobileViewport ? 56 : 64,
    minHeight: isMobileViewport ? 44 : 52,
  };
  const topControlsCloseButtonStyle = {
    backgroundColor: interactiveFillColor,
    color: "#ffffff",
    border: `1px solid ${tileBorderColor}`,
    fontFamily: activeFont.value,
    boxShadow: "0 10px 24px rgba(0, 0, 0, 0.25)",
    transition: "transform 0.2s ease, background-color 0.2s ease",
    marginLeft: "auto",
    marginTop: isTabletOrBelow ? 4 : 0,
    borderRadius: 999,
    width: isTabletOrBelow ? 40 : 36,
    height: isTabletOrBelow ? 40 : 36,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: isTabletOrBelow ? 20 : 18,
    lineHeight: 1,
    cursor: "pointer",
    justifySelf: "end",
    gridColumn: "auto",
  };
  const handleNavHover = useCallback(
    (event) => {
      event.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.12)";
      event.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.35)";
      event.currentTarget.style.transform = "translateY(-2px)";
    },
    []
  );

  const handleNavLeave = useCallback(
    (event) => {
      event.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.04)";
      event.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
      event.currentTarget.style.transform = "translateY(0)";
    },
    []
  );
  const entireContentBlocks = useMemo(() => {
    if (!currentSong) return [];
    if (!linesPerSlide) {
      return currentSong.lyrics;
    }

    const blocks = [];
    let buffer = [];
    const flushBuffer = () => {
      if (!buffer.length) return;
      blocks.push(buffer.join("\n"));
      buffer = [];
    };

    currentSong.lyrics.forEach((paragraph) => {
      if (paragraph.toLowerCase().includes("<img")) {
        flushBuffer();
        blocks.push(paragraph);
      } else {
        const lines = paragraph
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean);
        lines.forEach((line) => {
          buffer.push(line);
          if (buffer.length >= linesPerSlide) {
            flushBuffer();
          }
        });
      }
    });

    flushBuffer();
    return blocks;
  }, [currentSong, linesPerSlide]);

  const showAudioSlider = currentSong && !videoLikeMode;
  const showNextSongPreview = !customSong && currentSongIndex + 1 < filteredSongs.length;
  const renderedEntireColumns = isMobileViewport
    ? 1
    : entireColumnsLocked
    ? entireColumnCount
    : autoEntireColumnCount;
  const navControlsVisible = !exportPreviewMode && (controlsVisible || isTabletOrBelow || topControlsDismissed);
  const navSupportsSlides =
    currentSong &&
    (slideLikeMode || ["presenter", "entire", "music", "book", "newsletter"].includes(displayMode));
  const playButtonVisible = currentSong && (selectedYouTubeLink || youtubeLinks.length === 0) &&
    [
      "slide",
      "presenter",
      "entire",
      "music",
      "book",
      "newsletter",
      "video",
      "videoslide",
      CAMERA_SLIDES_MODE,
      CAMERA_PRESENT_MODE,
      CAMERA_VIDEO_MODE,
    ].includes(displayMode);
  const navBarShouldRender =
    currentSong &&
    navControlsVisible &&
    !bottomNavHidden &&
    (navSupportsSlides || playButtonVisible || showAudioSlider);
  const mobileBottomNavHeight = 64;
  const navBarBottomOffset = isMobileViewport
    ? mobileBottomNavHeight + 0
    : 60;
  const navButtonGap = isMobileViewport ? 8 : isTabletOrBelow ? 12 : 18;
  const navButtonBarStyle = {
    position: "fixed",
    left: isMobileViewport ? 8 : "50%",
    right: isMobileViewport ? 8 : "auto",
    transform: isMobileViewport ? "none" : "translateX(-50%)",
    bottom: isMobileViewport ? Math.max(navBarBottomOffset, 40) : navBarBottomOffset,
    display: "flex",
    gap: navButtonGap,
    alignItems: "center",
    padding: isMobileViewport ? "6px 10px" : isTabletOrBelow ? "8px 14px" : "12px 20px",
    borderRadius: 16,
    backgroundColor: hexToRgbaString(activeTheme.background, 0.25),
    border: `1px solid ${hexToRgbaString(activeTheme.text, 0.22)}`,
    boxShadow: "0 18px 30px rgba(0, 0, 0, 0.32)",
    backdropFilter: "blur(12px)",
    zIndex: 1500,
    maxWidth: isMobileViewport
      ? "none"
      : isTabletOrBelow
        ? "min(660px, calc(100% - 24px))"
        : "min(1400px, calc(100% - 32px))",
    width: isMobileViewport ? "auto" : "min(1400px, calc(100% - 32px))",
    flexWrap: "wrap",
    justifyContent: "center",
    overflowX: isMobileViewport ? "auto" : "visible",
  };
  const navButtonGroupStyle = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "nowrap",
    gap: navButtonGap,
    flex: "0 1 auto",
    whiteSpace: "nowrap",
    overflowX: isMobileViewport ? "auto" : "visible",
    padding: isMobileViewport ? "2px 0" : 0,
    order: isMobileViewport ? 1 : undefined,
  };
  const navSliderTimeStyle = {
    fontFamily: activeFont.value,
    fontSize: 12,
    color: hexToRgbaString(activeTheme.text, 0.85),
    minWidth: isMobileViewport ? 64 : 80,
    textAlign: "center",
  };
  const navSelectRowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: isMobileViewport ? 8 : 10,
    flexWrap: "nowrap",
    flex: isMobileViewport ? "1 1 100%" : "0 1 auto",
    order: isMobileViewport ? 2 : undefined,
    overflowX: isMobileViewport ? "auto" : "visible",
  };
  const navSettingsRowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: isMobileViewport ? 8 : 10,
    flexWrap: "nowrap",
    flex: isMobileViewport ? "1 1 100%" : "0 1 auto",
    order: isMobileViewport ? 3 : undefined,
    overflowX: isMobileViewport ? "auto" : "visible",
  };
  const navSelectStyle = {
    backgroundColor: hexToRgbaString(activeTheme.background, 0.35),
    color: activeTheme.text,
    border: `1px solid ${hexToRgbaString(activeTheme.text, 0.22)}`,
    borderRadius: 999,
    padding: isMobileViewport ? "6px 10px" : "8px 12px",
    fontFamily: activeFont.value,
    fontSize: isMobileViewport ? 12 : 13,
    minWidth: isMobileViewport ? 120 : 180,
    maxWidth: isMobileViewport ? 180 : 240,
    width: "auto",
    flex: isMobileViewport ? "0 0 auto" : "0 1 180px",
    appearance: "none",
    cursor: "pointer",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.35)",
  };
  const navControlPillStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: isMobileViewport ? "6px 10px" : "8px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255, 255, 255, 0.2)",
    backgroundColor: "rgba(255, 255, 255, 0.04)",
    color: activeTheme.text,
    fontFamily: activeFont.value,
    fontSize: isMobileViewport ? 12 : 13,
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.35)",
    flex: isMobileViewport ? "0 0 auto" : "0 1 auto",
    minWidth: isMobileViewport ? 120 : 220,
  };
  const navLineSliderStyle = {
    width: isMobileViewport ? 70 : 120,
    accentColor: sliderAccentColor,
    background: sliderTrackColor,
    height: 4,
    borderRadius: 999,
  };
  const navTileButtonGroupStyle = {
    display: "flex",
    alignItems: "center",
    gap: navButtonGap,
    order: isMobileViewport ? 1 : undefined,
  };
  const carouselControlsLayoutStyle = {
    display: "flex",
    flexDirection: isMobileViewport ? "column" : "row",
    alignItems: "center",
    justifyContent: "center",
    gap: isMobileViewport ? 12 : 18,
    width: "100%",
  };
  const carouselIndicatorStyle = {
    fontFamily: activeFont.value,
    fontSize: 12,
    letterSpacing: 1,
    color: hexToRgbaString(activeTheme.text, 0.7),
    textTransform: "uppercase",
  };
  const tilesPanelBackdropStyle = {
    position: "fixed",
    inset: 0,
    backgroundColor: "transparent",
    zIndex: 1440,
  };
  const tilesPanelMinimizedWidth = 120;
  const tilesPanelComputedWidth = isMobileViewport
    ? "100%"
    : `${tilesPanelMinimized ? tilesPanelMinimizedWidth : tilesPanelWidth}px`;
  const tilesPanelTopOffset = isMobileViewport
    ? 0
    : Math.max(
        48,
        exportPreviewMode ? Math.max(exportHeaderHeight, 80) : 48,
      );
  const contentRightOffset = tilesPanelOpen && !isMobileViewport
    ? tilesPanelMinimized
      ? tilesPanelMinimizedWidth
      : tilesPanelWidth
    : 0;
  const tilesPanelStyle = {
    position: "fixed",
    top: tilesPanelTopOffset,
    bottom: 0,
    right: 0,
    width: tilesPanelComputedWidth,
    backgroundColor: hexToRgbaString(activeTheme.background, 0.98),
    borderLeft: `1px solid ${hexToRgbaString(activeTheme.text, 0.25)}`,
    padding: isMobileViewport
      ? "0 12px 20px"
      : tilesPanelMinimized
      ? "12px 12px 16px"
      : "32px 16px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    zIndex: 1450,
    boxShadow: "-10px 0 40px rgba(0, 0, 0, 0.55)",
  };
  const tilesPanelHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    gap: 12,
  };
  const tilesPanelHeaderWrapperStyle = {
    position: "sticky",
    top: 0,
    backgroundColor: hexToRgbaString(activeTheme.background, 0.98),
    paddingTop: isMobileViewport ? 68 : 0,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    zIndex: 1460,
  };
  const tilesPanelListWrapperStyle = {
    flex: 1,
    overflowY: "auto",
    paddingRight: 4,
  };
  const tilesPanelResizeHandleStyle = {
    position: "absolute",
    left: -8,
    top: 0,
    bottom: 0,
    width: 16,
    cursor: isMobileViewport || tilesPanelMinimized ? "default" : "ew-resize",
    zIndex: 1465,
  };
  const closeTilesPanel = useCallback(() => {
    if (tilesPanelPinned) return;
    setTilesPanelOpen(false);
  }, [tilesPanelPinned]);
  const handleTilesPanelResizeStart = useCallback(
    (event) => {
      if (isMobileViewport || tilesPanelMinimized) return;
      tilesPanelResizeStartX.current = event.clientX;
      tilesPanelResizeStartWidth.current = tilesPanelWidth;
      setIsResizingTilesPanel(true);
      event.preventDefault();
    },
    [isMobileViewport, tilesPanelMinimized, tilesPanelWidth],
  );
  const tilesPanelPinButtonStyle = {
    border: "none",
    background: "transparent",
    color: tilesPanelPinned ? activeTheme.text : hexToRgbaString(activeTheme.text, 0.6),
    fontSize: 18,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };
  const tilesPanelMinimizeButtonStyle = {
    border: "none",
    background: "transparent",
    color: tilesPanelMinimized ? activeTheme.text : hexToRgbaString(activeTheme.text, 0.6),
    fontSize: 18,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  };
  const tilesPanelHeaderTextStyle = {
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: 1.2,
  };
  const tilesPanelCloseButtonStyle = {
    border: "none",
    background: "transparent",
    color: activeTheme.text,
    fontSize: 20,
    cursor: "pointer",
  };
  const tilesPanelListStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  };
  const tilesPanelSongButtonStyle = {
    ...tileItemStyle,
    width: "100%",
    minHeight: 48,
    borderRadius: 12,
    boxShadow: "none",
    position: "relative",
    justifyContent: "space-between",
    gap: 12,
    padding: "10px 14px",
    textAlign: "left",
    backgroundImage: "none",
    fontSize: isMobileViewport ? 16 : 14,
    lineHeight: isMobileViewport ? 1.35 : 1.2,
  };
  const tilesPanelSearchInputStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 10,
    border: `1px solid ${hexToRgbaString(activeTheme.text, 0.25)}`,
    background: hexToRgbaString(activeTheme.background, 0.9),
    color: activeTheme.text,
    fontFamily: activeFont.value,
    marginBottom: 14,
  };
  const tilesPanelEmptyStateStyle = {
    fontSize: 14,
    color: hexToRgbaString(activeTheme.text, 0.7),
    textAlign: "center",
  };
  const tilesPanelButtonIconStyle = {
    width: 20,
    height: 20,
    display: "block",
  };
  const tilesPanelActiveSongStyle = {
    backgroundColor: hexToRgbaString(activeTheme.text, 0.15),
    borderColor: hexToRgbaString(activeTheme.text, 0.45),
  };
  const tilesPanelMinimizedGridStyle = {
    display: "grid",
    gap: 8,
    gridTemplateColumns: "repeat(auto-fill, minmax(40px, 1fr))",
    justifyContent: "center",
    margin: "8px 0",
  };
  const tilesPanelMinimizedLetterStyle = {
    width: 40,
    height: 40,
    borderRadius: 999,
    border: `1px solid ${hexToRgbaString(activeTheme.text, 0.4)}`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: hexToRgbaString(activeTheme.background, 0.1),
    color: activeTheme.text,
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
  };
  const tilesPanelActiveLetterStyle = {
    backgroundColor: hexToRgbaString(activeTheme.text, 0.35),
    color: activeTheme.background,
    borderColor: hexToRgbaString(activeTheme.text, 0.65),
  };
  const [panelShowAll, setPanelShowAll] = useState(false);
  const tilesPanelSourceSongs = useMemo(() => {
    if (panelShowAll) return songs;
    if (!isCustomCategory) return tileSongs;
    return customSelectedSongs;
  }, [customSelectedSongs, isCustomCategory, panelShowAll, songs, tileSongs]);
  const tilesPanelFilteredSongs = useMemo(() => {
    const query = tilesPanelSearch.trim().toLowerCase();
    return tilesPanelSourceSongs
      .map((song, idx) => ({ song, idx }))
      .filter(({ song }) => {
        if (!query) return true;
        return song.title.toLowerCase().includes(query);
      });
  }, [tilesPanelSearch, tilesPanelSourceSongs]);
  const tilesPanelMinimizedTiles = useMemo(
    () =>
      tilesPanelFilteredSongs.map(({ song, idx }) => ({
        idx,
        letter: (song.title || "").trim().charAt(0).toUpperCase() || "?",
      })),
    [tilesPanelFilteredSongs],
  );
  const carouselEmptyStyle = {
    color: hexToRgbaString(activeTheme.text, 0.8),
    fontFamily: activeFont.value,
    textAlign: "center",
    padding: "32px 16px",
  };
  const typeAheadIndicatorStyle = {
    position: "fixed",
    left: "50%",
    transform: "translateX(-50%)",
    bottom: navBarBottomOffset + (isMobileViewport ? 74 : 86),
    padding: "4px 12px",
    borderRadius: 999,
    backgroundColor: hexToRgbaString(activeTheme.background, 0.7),
    border: `1px solid ${hexToRgbaString(activeTheme.text, 0.25)}`,
    color: hexToRgbaString(activeTheme.text, 0.9),
    fontFamily: activeFont.value,
    fontSize: isMobileViewport ? 11 : 12,
    letterSpacing: 0.3,
    textTransform: "lowercase",
    textAlign: "center",
    maxWidth: "calc(100% - 24px)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    zIndex: 1650,
    pointerEvents: "none",
  };
  const newsletterColumnCount = isTabletOrBelow ? 1 : Math.max(1, renderedEntireColumns);
  const mobileTopOffset = "calc(clamp(56px, 12vw, 120px) + env(safe-area-inset-top, 0px))";
  const bookStartIndex = clampBookStartIndex(
    currentSlideIndex,
    bookPages.length,
    bookPagesPerView,
  );
  const bookPageHtmls = useMemo(() => {
    if (!bookPages.length) return [];
    return bookPages
      .slice(bookStartIndex, bookStartIndex + bookPagesPerView)
      .map((page) => formatSlideHtmlForBook(page || ""));
  }, [bookPages, bookPagesPerView, bookStartIndex, formatSlideHtmlForBook]);
  const bookViewStyle = {
    flex: 1,
    width: "100%",
    height: isMobileViewport
      ? `calc(100vh - ${mobileBottomNavHeight + 24}px - env(safe-area-inset-bottom, 0px))`
      : "100%",
    display: "grid",
    gap: isMobileViewport ? 12 : 28,
    gridTemplateColumns: isTabletOrBelow
      ? "1fr"
      : `repeat(${bookPagesPerView}, minmax(0, 1fr))`,
    alignItems: "stretch",
    minHeight: 0,
    maxHeight: isMobileViewport
      ? `calc(100vh - ${mobileBottomNavHeight + 24}px - env(safe-area-inset-bottom, 0px))`
      : "100%",
    alignSelf: "stretch",
    justifyContent: "stretch",
    padding: isMobileViewport ? "0" : "0",
    paddingTop: isMobileViewport ? mobileTopOffset : undefined,
    paddingBottom: isMobileViewport ? "12px" : undefined,
  };
  const bookPageStyle = {
    boxShadow: "0 28px 56px rgba(0, 0, 0, 0.32)",
    borderRadius: 24,
    border: `1px solid ${hexToRgbaString(activeTheme.text, 0.24)}`,
    backgroundColor: hexToRgbaString(activeTheme.background, 0.95),
    padding: isMobileViewport ? "18px" : "28px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: 12,
    fontFamily: activeFont.value,
    color: activeTheme.text,
    flex: 1,
    minWidth: 0,
    height: "100%",
    minHeight: isMobileViewport ? "0" : "420px",
    maxHeight: "100%",
    overflow: "hidden",
  };
  const bookPageContentStyle = {
    flex: 1,
    textAlign: "left",
    fontSize: bookFontSize,
    lineHeight: 1.5,
    display: "flex",
    flexDirection: "column",
    gap: isMobileViewport ? 10 : 14,
    overflowY: "auto",
  };
  const bookPageNumberStyle = {
    fontSize: isMobileViewport ? 12 : 14,
    color: hexToRgbaString(activeTheme.text, 0.7),
    alignSelf: "flex-end",
    marginTop: "auto",
  };
  const lyricsContainerStyle = {
    backgroundColor: cameraModeActive ? "transparent" : activeTheme.background,
    backgroundImage: cameraModeActive ? "none" : activeTheme.gradient || "none",
    backgroundSize: cameraModeActive ? "cover" : activeTheme.gradient ? "cover" : "auto",
    backgroundAttachment: cameraModeActive ? "scroll" : activeTheme.gradient ? "fixed" : "scroll",
    color: activeTheme.text,
    "--lyrics-background": activeTheme.background,
    "--lyrics-text": activeTheme.text,
    "--lyrics-font": activeFont.value,
    fontFamily: activeFont.value,
    boxSizing: "border-box",
    paddingTop: exportPreviewMode ? Math.max(exportHeaderHeight, 80) : 0,
    marginRight: contentRightOffset,
    width:
      contentRightOffset > 0
        ? `calc(100% - ${contentRightOffset}px)`
        : "100%",
    transition: "margin-right 0.25s ease, width 0.25s ease",
    position: "relative",
  };
  const baseLyricsBoxStyle = {
    display: showLyricsBox ? "flex" : "none",
    backgroundColor: activeTheme.background,
    backgroundImage: activeTheme.gradient || "none",
    backgroundSize: activeTheme.gradient ? "cover" : "auto",
    backgroundRepeat: activeTheme.gradient ? "no-repeat" : "repeat",
    color: activeTheme.text,
    padding: isMobileViewport ? "12px 12px 36px" : isTabletOrBelow ? "32px 24px 48px" : "56px 64px 56px",
    flex: 1,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: isMobileViewport ? 12 : 18,
    fontFamily: activeFont.value,
    fontWeight: lyricsBold ? 600 : 400,
    position: "relative",
  };
  const cameraLyricsOverrides = cameraModeActive
    ? {
        backgroundColor: "transparent",
        backgroundImage: "none",
        padding: "12px 16px 20px",
        flex: "0 0 auto",
        justifyContent: "flex-end",
        gap: isMobileViewport ? 6 : 10,
        position: "absolute",
        bottom: 0,
        left: 0,
        width: "100%",
        height: isMobileViewport ? "35%" : "30%",
        zIndex: 1200,
      }
    : {};
  const lyricsBoxStyle = {
    ...baseLyricsBoxStyle,
    ...cameraLyricsOverrides,
  };
  const cameraPresentWrapperStyle = {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: isMobileViewport ? 8 : 12,
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    zIndex: 1210,
  };
  const cameraPresentCurrentStyle = {
    width: "100%",
    fontSize: isMobileViewport ? responsiveSlideFontSize : globalFontSize,
    fontWeight: lyricsBold ? 700 : 600,
    lineHeight: 1.2,
    color: activeTheme.text,
    textShadow: "0 4px 18px rgba(0, 0, 0, 0.85)",
  };
  const cameraPresentNextStyle = {
    width: "100%",
    fontSize: Math.max(12, Math.round(globalFontSize * 0.45)),
    opacity: 0.7,
    color: activeTheme.text,
    lineHeight: 1.2,
    fontWeight: 500,
  };
  const cameraVideoWrapperStyle = {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    zIndex: 0,
    backgroundColor: "#000",
    pointerEvents: "none",
  };
  const cameraVideoStyle = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    filter: "brightness(0.95)",
  };
  const cameraErrorOverlayStyle = {
    position: "absolute",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.65)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: 20,
    gap: 8,
    zIndex: 10,
    pointerEvents: "auto",
  };
  const cameraErrorTextStyle = {
    color: "#fff",
    fontFamily: activeFont.value,
    fontSize: isMobileViewport ? 12 : 14,
    lineHeight: 1.4,
    maxWidth: 360,
  };
  const [cardRect, setCardRect] = useState({ left: 55, top: 12, width: 32, height: 36 });
  const [dragState, setDragState] = useState(null);

  useEffect(() => {
    if (!dragState) return undefined;
    const handleMove = (event) => {
      event.preventDefault();
      const deltaX = ((event.clientX - dragState.startX) / window.innerWidth) * 100;
      const deltaY = ((event.clientY - dragState.startY) / window.innerHeight) * 100;
      const startRect = dragState.rect;
      if (dragState.mode === "move") {
        const nextLeft = clamp(startRect.left + deltaX, 0, 100 - startRect.width);
        const nextTop = clamp(startRect.top + deltaY, 0, 100 - startRect.height);
        setCardRect((prev) => ({
          ...prev,
          left: nextLeft,
          top: nextTop,
        }));
      } else if (dragState.mode.startsWith("resize")) {
        let nextLeft = startRect.left;
        let nextTop = startRect.top;
        let nextWidth = startRect.width;
        let nextHeight = startRect.height;
        const minSize = 30;
        const maxSize = 90;
        const mode = dragState.mode.replace("resize-", "");
        if (mode.includes("e")) {
          nextWidth = clamp(startRect.width + deltaX, minSize, Math.min(maxSize, 100 - nextLeft));
        }
        if (mode.includes("s")) {
          nextHeight = clamp(startRect.height + deltaY, minSize, Math.min(maxSize, 100 - nextTop));
        }
        if (mode.includes("w")) {
          nextWidth = clamp(startRect.width - deltaX, minSize, Math.min(maxSize, startRect.width + nextLeft));
          nextLeft = clamp(startRect.left + deltaX, 0, 100 - nextWidth);
        }
        if (mode.includes("n")) {
          nextHeight = clamp(startRect.height - deltaY, minSize, Math.min(maxSize, startRect.height + nextTop));
          nextTop = clamp(startRect.top + deltaY, 0, 100 - nextHeight);
        }
        setCardRect({
          left: nextLeft,
          top: nextTop,
          width: nextWidth,
          height: nextHeight,
        });
      }
    };
    const handleUp = () => {
      setDragState(null);
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
  }, [dragState]);

  const beginDrag = (mode) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragState({
      mode,
      startX: event.clientX,
      startY: event.clientY,
      rect: { ...cardRect },
    });
  };

  const videoContainerStyle = cameraVideoActive
    ? {
        display: videoLikeMode && currentSong ? "block" : "none",
        position: "absolute",
        top: `${cardRect.top}%`,
        left: `${cardRect.left}%`,
        width: `${cardRect.width}%`,
        height: `${cardRect.height}%`,
        borderRadius: 18,
        overflow: "hidden",
        boxShadow: "0 30px 80px rgba(0,0,0,0.65)",
        backgroundColor: "transparent",
        zIndex: 10,
        pointerEvents: "auto",
        touchAction: "none",
      }
    : {
        display: videoLikeMode && currentSong ? "block" : "none",
        backgroundColor: "inherit",
      };
  const videoPlayerStyle = {
    width: "100%",
    height: "100%",
    backgroundColor: "transparent",
    opacity: cameraVideoActive ? 0.88 : 1,
    pointerEvents: "auto",
  };
  const videoMoveHandleStyle = {
    position: "absolute",
    top: 12,
    left: "50%",
    transform: "translateX(-50%)",
    width: 150,
    height: 26,
    borderRadius: 999,
    backgroundColor: "rgba(255, 255, 255, 0.18)",
    color: "#fff",
    display: cameraVideoActive ? "flex" : "none",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    cursor: "grab",
    border: "1px solid rgba(255,255,255,0.35)",
    zIndex: 12,
    pointerEvents: "auto",
    touchAction: "none",
  };
  const videoResizeHandleStyle = {
    position: "absolute",
    bottom: 10,
    right: 10,
    width: 18,
    height: 18,
    borderRadius: 4,
    backgroundColor: "rgba(255,255,255,0.35)",
    border: "1px solid rgba(255,255,255,0.6)",
    cursor: "nwse-resize",
    zIndex: 12,
    pointerEvents: "auto",
    touchAction: "none",
  };
  const edgeHandles = [
    { mode: "resize-n", style: { top: 0, left: "50%", transform: "translate(-50%,-50%)", width: 40, height: 20, cursor: "ns-resize", zIndex: 20, touchAction: "none" } },
    { mode: "resize-s", style: { bottom: 0, left: "50%", transform: "translate(-50%,50%)", width: 40, height: 20, cursor: "ns-resize", zIndex: 20, touchAction: "none" } },
    { mode: "resize-e", style: { top: "50%", right: 0, transform: "translate(50%,-50%)", width: 20, height: 40, cursor: "ew-resize", zIndex: 20, touchAction: "none" } },
    { mode: "resize-w", style: { top: "50%", left: 0, transform: "translate(-50%,-50%)", width: 20, height: 40, cursor: "ew-resize", zIndex: 20, touchAction: "none" } },
    { mode: "resize-ne", style: { top: 0, right: 0, transform: "translate(50%,-50%)", width: 20, height: 20, cursor: "nesw-resize", zIndex: 20, touchAction: "none" } },
    { mode: "resize-nw", style: { top: 0, left: 0, transform: "translate(-50%,-50%)", width: 20, height: 20, cursor: "nwse-resize", zIndex: 20, touchAction: "none" } },
    { mode: "resize-se", style: { bottom: 0, right: 0, transform: "translate(50%,50%)", width: 20, height: 20, cursor: "nwse-resize", zIndex: 20, touchAction: "none" } },
    { mode: "resize-sw", style: { bottom: 0, left: 0, transform: "translate(-50%,50%)", width: 20, height: 20, cursor: "nesw-resize", zIndex: 20, touchAction: "none" } },
  ];

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }} onContextMenu={(e) => e.preventDefault()}>
      <SEO
        title="Worship - Global Christian Community"
        description="Browse worship songs and display lyrics with background music or karaoke."
        keywords="worship, Bible songs, Christian music, Global Christian Community"
        url="https://globalchristiancommunity.com/worship"
      />
      <div id="lyrics-container" ref={exportContainerRef} style={lyricsContainerStyle}>
        {cameraModeActive && (
          <div id="camera-video-wrapper" style={cameraVideoWrapperStyle}>
            <video
              ref={cameraVideoRef}
              autoPlay
              muted
              playsInline
              style={cameraVideoStyle}
              aria-label="Camera feed"
            />
            {cameraStreamError && (
              <div style={cameraErrorOverlayStyle} role="alert">
                <p style={cameraErrorTextStyle}>{cameraStreamError}</p>
                <p style={cameraErrorTextStyle}>Allow camera access to continue the live experience.</p>
              </div>
            )}
          </div>
        )}
        {!cameraModeActive && <AnimatedWaveBackdrop layers={activeTheme.waveLayers} />}
        {controlsVisible && !exportPreviewMode && (
          <div id="controls" style={controlsContainerStyle}>            {/* Top row */}            <div className="controls-row top-row" style={topControlsRowStyle}>
              <input
                type="text"
                id="search-input"
                placeholder="Search.."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                style={{
                  ...fieldFlexStyle,
                  flex: isTabletOrBelow ? "1 1 220px" : "2 1 260px",
                  minWidth: isTabletOrBelow ? 180 : 220,
                  width: "100%",
                  gridColumn: isMobileViewport ? "1 / -1" : !isTabletOrBelow ? "span 2" : "auto",
                }}
              />
              <select
                id="blogspot-selector"
                title="Catalog"
                value={selectedDomain}
                onChange={(e) => handleDomainSelect(e.target.value)}
                style={{
                  ...fieldFlexStyle,
                  flex: "1 1 200px",
                  minWidth: 160,
                  width: "100%",
                }}
              >
                <option value="">Choose Catalog</option>
                <option value={ALL_CATALOGS_VALUE}>All</option>
                {organizations.map((org) => (
                  <option key={org.domain} value={org.domain}>
                    {formatDomainOptionLabel(org.domain)}
                  </option>
                ))}
              </select>
              <select
                id="song-selector"
                title="Song"
                value={currentSongIndex >= 0 ? currentSongIndex : ""}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (val >= 0) loadSong(val);
                }}
                style={{
                  ...fieldFlexStyle,
                  flex: isTabletOrBelow ? "1 1 220px" : "2 1 260px",
                  width: "100%",
                  gridColumn: isMobileViewport ? "1 / -1" : !isTabletOrBelow ? "span 2" : "auto",
                }}
              >
                <option value="">Select a song</option>
                {filteredSongs.map((s, idx) => (
                  <option key={s.title + idx} value={idx}>
                    {s.title}
                  </option>
                ))}
              </select>
              <select
                id="category-selector"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{
                  ...fieldFlexStyle,
                  flex: "1 1 180px",
                  minWidth: 150,
                  width: "100%",
                }}
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              {selectedCategory === CUSTOM_CATEGORY_OPTION && (
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flex: isTabletOrBelow ? "1 1 220px" : "1 1 260px",
                    minWidth: 200,
                    width: "100%",
                  }}
                >
                  <button
                    type="button"
                    style={{
                      flex: 1,
                      padding: "8px 12px",
                      borderRadius: 10,
                      backgroundColor: interactiveFillColor,
                      color: activeTheme.text,
                      border: `1px solid ${tileBorderColor}`,
                      fontFamily: activeFont.value,
                      cursor: filteredSongs.length ? "pointer" : "not-allowed",
                      opacity: filteredSongs.length ? 1 : 0.6,
                    }}
                    onClick={() => {
                      if (!filteredSongs.length) return;
                      loadSong(0);
                    }}
                  >
                    Play Selected ({filteredSongs.length})
                  </button>
                  <button
                    type="button"
                    style={{
                      padding: "8px 12px",
                      borderRadius: 10,
                      backgroundColor: "transparent",
                      color: activeTheme.text,
                      border: `1px solid ${tileBorderColor}`,
                      fontFamily: activeFont.value,
                      cursor: customSelectedSlugs.length ? "pointer" : "not-allowed",
                      opacity: customSelectedSlugs.length ? 1 : 0.6,
                    }}
                    onClick={() => {
                      if (!customSelectedSlugs.length) return;
                      setCustomSelectedSlugs([]);
                    }}
                  >
                    Clear
                  </button>
                </div>
              )}
              <label
                id="display-mode-label"
                style={{
                  ...fieldFlexStyle,
                  flex: isTabletOrBelow ? "1 1 200px" : "1 1 200px",
                  minWidth: isTabletOrBelow ? 160 : 180,
                  width: "100%",
                }}
              >
                    <select
                      id="display-mode-selector"
                      value={displayMode}
                      onChange={(e) => setDisplayMode(e.target.value)}
                      style={isTabletOrBelow ? { width: "100%", fontFamily: activeFont.value } : { fontFamily: activeFont.value }}
                    >
                      <option value="slide">Slide</option>
                      <option value={CAMERA_SLIDES_MODE}>Camera + Slides</option>
                      <option value={CAMERA_PRESENT_MODE}>Camera + Present</option>
                      <option value={CAMERA_VIDEO_MODE}>Camera + Video</option>
                      <option value="video">Video</option>
                      <option value="videoslide">Video + Slides</option>
                      <option value="music">Music</option>
                      <option value="presenter">Present</option>
                      <option value="entire">Full Content</option>
                      <option value="book">Book</option>
                      <option value="newsletter">Newsletter</option>
                    </select>
              </label>
              <div
                className="song-view-toggle"
                style={{
                  ...songViewToggleContainerStyle,
                  gridColumn: isMobileViewport ? "1 / -1" : "auto",
                  justifySelf: isMobileViewport ? "stretch" : "end",
                }}
                aria-label="Change catalog view"
              >
                {SONG_VIEW_MODES.map((mode) => (
                  <button
                    key={mode.id}
                    type="button"
                    onClick={() => setSongViewMode(mode.id)}
                    style={songViewToggleButtonStyle(mode.id)}
                    title={`${mode.label} view`}
                    aria-pressed={songViewMode === mode.id}
                  >
                    {mode.symbol}
                  </button>
                ))}
              </div>
              <button
                id="help-btn"
                title="Keyboard Shortcuts"
                style={{
                  fontSize: 16,
                  padding: "3px 8px",
                  cursor: "pointer",
                  marginLeft: 0,
                  marginTop: isTabletOrBelow ? 4 : 0,
                  alignSelf: isTabletOrBelow ? "flex-start" : "center",
                  justifySelf: isTabletOrBelow ? "start" : "end",
                }}
                onClick={() => setHelpModalOpen(true)}
              >
                ?
              </button>
            </div>

            {/* Bottom row */}            <div className="controls-row bottom-row" style={bottomControlsRowStyle}>              <button
                id="back-to-songs-btn"
                style={{
                  display: currentSong ? "inline-flex" : "none",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "8px 14px",
                  borderRadius: 10,
                  backgroundColor: interactiveFillColor,
                  color: activeTheme.text,
                  border: `1px solid ${tileBorderColor}`,
                  fontFamily: activeFont.value,
                  flex: isTabletOrBelow ? "1 1 140px" : "0 0 auto",
                }}
                onClick={() => {
                  setCurrentSong(null);
                  setCustomSong(null);
                  setParagraphSlides([]);
                  setLineSlides([]);
                  setActiveSlides([]);
                  setActiveSongTitle("");
                }}
              >
                ← Back
              </button>
              <select
                id="youtube-selector"
                disabled={youtubeLinks.length === 0}
                style={{
                  ...fieldFlexStyle,
                  flex: isTabletOrBelow ? "0 1 110px" : "0 1 120px",
                  minWidth: isTabletOrBelow ? 90 : 110,
                  maxWidth: 140,
                }}
                onChange={(e) => {
                    const idx = parseInt(e.target.value, 10);
                  if (idx >= 0 && idx < youtubeLinks.length) {
                    const link = youtubeLinks[idx];
                    setSelectedYouTubeLink(link);
                    if (videoLikeMode) {
                      const vid = extractYouTubeID(youtubeLinks[idx]);
                      if (vid) createVideoPlayer(vid);
                    } else if (isYouTubePlaying) {
                      playAudioLink(link);
                    }
                  }
                }}
              >
                {youtubeLinks.length === 0 ? (
                  <option value="-1">No YT links</option>
                ) : (
                  youtubeLinks.map((lk, i) => (
                    <option key={lk} value={i}>
                      {i + 1}
                    </option>
                  ))
                )}
              </select>
              <button
                id="slide-play-pause-btn"
                style={{
                  display: selectedYouTubeLink ? "block" : "none",
                  ...(isTabletOrBelow ? { marginTop: isMobileViewport ? 2 : 4 } : {}),
                  fontFamily: activeFont.value,
                }}
                onClick={togglePlayPause}
              >
                {isYouTubePlaying ? "⏸" : "▶"}
              </button>
              <span
                id="playback-rate-display"
              style={{
                marginLeft: isTabletOrBelow ? 0 : 8,
                marginTop: isMobileViewport ? 0 : isTabletOrBelow ? 2 : 0,
                display: "flex",
                alignItems: "center",
                fontWeight: 600,
                flex: "0 0 auto",
              }}
            >
              {currentPlaybackRate.toFixed(2)}x Speed
            </span>
              <div style={sliderClusterStyle} className="slider-cluster">                <label style={sliderLabelStyle}>
                  L
                  <input
                    type="range"
                    id="lines-per-slide-range"
                    min="0"
                    max="25"
                    value={linesPerSlide}
                    style={{
                      ...sliderInputStyle,
                      accentColor: sliderAccentColor,
                      background: sliderTrackColor,
                    }}
                    onChange={(e) => setLinesPerSlide(parseInt(e.target.value, 10))}
                  />
                  <span id="lines-per-slide-value" style={sliderValueStyle}>
                    {linesPerSlide}
                  </span>
                </label>
                <label style={sliderLabelStyle}>                  F
                  <input
                    type="range"
                    id="font-range"
                    min="10"
                    max="150"
                    value={globalFontSize}
                    style={{
                      ...sliderInputStyle,
                      accentColor: sliderAccentColor,
                      background: sliderTrackColor,
                    }}
                    onChange={(e) => setGlobalFontSize(parseInt(e.target.value, 10))}
                  />
                  <span id="font-size-value" style={sliderValueStyle}>
                    {globalFontSize}
                  </span>
                </label>
                <label style={autoSliderLabelStyle}>                  A
                  <input
                    type="range"
                    id="auto-advance-range"
                    min="0"
                    max="120"
                    value={autoAdvanceInterval}
                    style={{
                      ...sliderInputStyle,
                      accentColor: sliderAccentColor,
                      background: sliderTrackColor,
                    }}
                    onChange={(e) => setAutoAdvanceInterval(parseInt(e.target.value, 10))}
                  />
                  <span id="auto-advance-value" style={sliderValueStyle}>
                    {autoAdvanceInterval}
                  </span>
                  <span style={{ marginLeft: 4 }}>s</span>
                </label>
              </div>
            <button
              id="toggle-light-mode-btn"
              title="Toggle Tile Light Mode"
              style={{
                fontSize: 16,
                padding: isTabletOrBelow ? "6px 12px" : 0,
                marginLeft: isTabletOrBelow ? 0 : 1,
                cursor: "pointer",
                marginTop: isMobileViewport ? 4 : isTabletOrBelow ? 6 : 0,
                ...compactButtonStyle,
              }}
              onClick={handleToggleTileLight}
            >
              ✨
            </button>
          </div>
            <div
              className="controls-row typography-row"
              style={{ ...typographyControlsRowStyle, marginTop: isTabletOrBelow ? 6 : 8 }}
            >
              <label
                htmlFor="lyrics-font-selector"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#ccc",
                  fontFamily: activeFont.value,
                  flex: isTabletOrBelow ? "1 1 220px" : "0 0 auto",
                }}
              >
                Font
                <select
                  id="lyrics-font-selector"
                  value={selectedFontId}
                  onChange={handleFontChange}
                  style={{
                    fontFamily: activeFont.value,
                    minWidth: isTabletOrBelow ? "100%" : 180,
                  }}
                >
                  {FONT_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={toggleLyricsBold}
                aria-pressed={lyricsBold}
                style={{
                  fontFamily: activeFont.value,
                  fontWeight: lyricsBold ? 700 : 500,
                  padding: "6px 14px",
                  borderRadius: 6,
                  border: `1px solid ${controlsBorderColor}`,
                  backgroundColor: lyricsBold ? blendHexColors(activeTheme.background, activeTheme.text, 0.45) : controlsSurfaceColor,
                  color: activeTheme.text,
                  cursor: "pointer",
                  minWidth: 100,
                }}
              >
                {lyricsBold ? "Bold On" : "Bold Off"}
              </button>
              <label
                htmlFor="lyrics-column-selector"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  color: "#ccc",
                  fontFamily: activeFont.value,
                  flex: isTabletOrBelow ? "1 1 220px" : "0 0 auto",
                }}
              >
                Columns
                <input
                  id="lyrics-column-selector"
                  type="range"
                  min="1"
                  max={MAX_ENTIRE_COLUMNS}
                  step="1"
                  value={entireColumnCount}
                  onChange={handleColumnChange}
                  style={{ flex: 1, minWidth: isTabletOrBelow ? 140 : 160 }}
                />
                <span style={{ minWidth: 20, textAlign: "right" }}>
                  {entireColumnCount}
                </span>
              </label>
            </div>
            <div
              className="controls-row theme-row"
              style={themeControlsRowStyle}
            >
              <label
                htmlFor="theme-selector"
                style={{ color: "#ccc", marginRight: 6, flex: isTabletOrBelow ? "0 0 auto" : undefined }}
              >
                Theme
              </label>
              <select
                id="theme-selector"
                value={selectedThemeId}
                onChange={handleThemeSelectChange}
                style={fieldFlexStyle}
              >
                {THEME_PRESETS.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.label}
                  </option>
                ))}
                <option value="custom">Custom</option>
              </select>
              <label
                htmlFor="theme-background-color"
                style={{
                  color: "#ccc",
                  marginLeft: isTabletOrBelow ? 0 : 10,
                  marginTop: isTabletOrBelow ? 10 : 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Background
                <input
                  type="color"
                  id="theme-background-color"
                  value={selectedThemeId === "custom" ? customBackgroundColor : activeTheme.background}
                  onChange={handleBackgroundColorChange}
                  style={{ marginLeft: 6, cursor: "pointer", background: "transparent", border: "none", width: 36, height: 24, padding: 0 }}
                />
              </label>
              <label
                htmlFor="theme-text-color"
                style={{
                  color: "#ccc",
                  marginLeft: isTabletOrBelow ? 0 : 10,
                  marginTop: isTabletOrBelow ? 10 : 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Text
                <input
                  type="color"
                  id="theme-text-color"
                  value={selectedThemeId === "custom" ? customTextColor : activeTheme.text}
                  onChange={handleTextColorChange}
                  style={{ marginLeft: 6, cursor: "pointer", background: "transparent", border: "none", width: 36, height: 24, padding: 0 }}
                />
              </label>
              {selectedThemeId === "custom" && (
                <label
                  htmlFor="theme-gradient-toggle"
                  style={{
                    color: "#ccc",
                    marginLeft: isTabletOrBelow ? 0 : 10,
                    marginTop: isTabletOrBelow ? 10 : 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  Gradient
                  <input
                    id="theme-gradient-toggle"
                    type="checkbox"
                    checked={customGradientEnabled}
                    onChange={handleCustomGradientToggle}
                  />
                </label>
              )}
              {selectedThemeId === "custom" && customGradientEnabled && (
                <label
                  htmlFor="theme-gradient-accent"
                  style={{
                    color: "#ccc",
                    marginLeft: isTabletOrBelow ? 0 : 10,
                    marginTop: isTabletOrBelow ? 10 : 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Accent
                  <input
                    type="color"
                    id="theme-gradient-accent"
                    value={normaliseHex(customGradientAccent) || customGradientAccent}
                    onChange={handleCustomGradientAccentChange}
                    style={{ marginLeft: 6, cursor: "pointer", background: "transparent", border: "none", width: 36, height: 24, padding: 0 }}
                  />
                </label>
              )}
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  marginLeft: isTabletOrBelow ? 0 : 8,
                  marginTop: isTabletOrBelow ? 10 : 0,
                  fontFamily: activeFont.value,
                  color: hexToRgbaString(activeTheme.text, 0.85),
                }}
              >
                <input
                  type="checkbox"
                  checked={lockSongNavigation}
                  onChange={(event) => setLockSongNavigation(event.target.checked)}
                  style={{ width: 18, height: 18 }}
                />
                Stay on song
              </label>
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  marginLeft: isTabletOrBelow ? 0 : 8,
                  marginTop: isTabletOrBelow ? 10 : 0,
                  fontFamily: activeFont.value,
                  color: hexToRgbaString(activeTheme.text, 0.85),
                }}
              >
                <input
                  type="checkbox"
                  checked={autoPlayNext}
                  onChange={(event) => setAutoPlayNext(event.target.checked)}
                  style={{ width: 18, height: 18 }}
                />
                Auto next
              </label>
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  marginLeft: isTabletOrBelow ? 0 : 8,
                  marginTop: isTabletOrBelow ? 10 : 0,
                  fontFamily: activeFont.value,
                  color: hexToRgbaString(activeTheme.text, 0.85),
                }}
              >
                <input
                  type="checkbox"
                  checked={randomPlayNext}
                  onChange={(event) => setRandomPlayNext(event.target.checked)}
                  style={{ width: 18, height: 18 }}
                />
                Random
              </label>
              <button
                type="button"
                onClick={handleShareLinkCopy}
                title="Copy shareable link"
                style={{
                  marginLeft: isTabletOrBelow ? 0 : 10,
                  marginTop: isTabletOrBelow ? 10 : 0,
                  fontFamily: activeFont.value,
                }}
              >
                Copy Share Link
              </button>
              {shareCopied && (
                <span
                  style={{
                    marginLeft: isTabletOrBelow ? 10 : 6,
                    color: activeTheme.text,
                    opacity: 0.7,
                    marginTop: isTabletOrBelow ? 6 : 0,
                  }}
                >
                  Link copied
                </span>
              )}
              <button
                type="button"
                onClick={dismissTopControls}
                style={topControlsCloseButtonStyle}
                onMouseEnter={handleNavHover}
                onFocus={handleNavHover}
                onMouseLeave={handleNavLeave}
                onBlur={handleNavLeave}
                title="Hide top controls"
                aria-label="Hide top controls"
              >
                ✕
              </button>
            </div>
            {showExportControls && (
              <div className="controls-row export-row" style={exportControlsRowStyle}>
                <select
                  id="export-current-view"
                  title="Export Current Song / View"
                  onChange={onCurrentExportChange}
                  disabled={exporting}
                  style={fieldFlexStyle}
                >
                  <option value="">Export Current Song…</option>
                  <option value="view:image">Current Slide — Image (PNG)</option>
                  <option value="view:ppt">Current Slide — PowerPoint</option>
                  <option value="view:pdf">Current Slide — PDF</option>
                  <option value="view:docx">Current Slide — Word</option>
                  <option value="song:image">Current Song — All Slides (Images ZIP)</option>
                  <option value="song:ppt">Current Song — All Slides (PowerPoint)</option>
                  <option value="song:pdf">Current Song — All Slides (PDF)</option>
                  <option value="song:docx">Current Song — All Slides (Word)</option>
                  <option value="book:styled">Song Book — Styled PDF</option>
                  <option value="book:plain">Song Book — Plain PDF</option>
                </select>
                <select
                  id="export-bulk"
                  title="Export Collections"
                  onChange={onBulkExportChange}
                  disabled={exporting}
                  style={{
                    marginLeft: isTabletOrBelow ? 0 : 6,
                    ...(isTabletOrBelow ? { flex: "1 1 260px", minWidth: 220, marginTop: 8 } : {}),
                    fontFamily: activeFont.value,
                  }}
                >
                  <option value="">Export Collections…</option>
                  <option value="category:pdf">Current Category — PDF</option>
                  <option value="category:docx">Current Category — Word</option>
                  <option value="category:ppt">Current Category — PowerPoint</option>
                  <option value="category:image">Current Category — Images (ZIP)</option>
                  <option value="catalog:pdf">Entire Catalog — PDF</option>
                  <option value="catalog:docx">Entire Catalog — Word</option>
                  <option value="catalog:ppt">Entire Catalog — PowerPoint</option>
                  <option value="catalog:image">Entire Catalog — Images (ZIP)</option>
                </select>
                {exporting && (
                  <span
                    className="export-status"
                    aria-live="polite"
                    style={{ marginTop: isTabletOrBelow ? 8 : 0 }}
                  >
                    Preparing export…
                  </span>
                )}
              </div>
            )}
        </div>
        )}

        {exportPreviewMode && currentSong && (
          <div ref={exportHeaderRef} className="export-header" style={exportHeaderStyle}>
            <span>{currentSong.title}</span>
          </div>
        )}
        {/* Tiles container */}        <div
          id="tiles-container"
          style={tileContainerStyle}
        >
        {songViewMode === "carousel" ? (
            tileSongs.length ? (
              <>
                <div
                  style={carouselControlsLayoutStyle}
                  onTouchStart={handleCarouselTouchStart}
                  onTouchEnd={handleCarouselTouchEnd}
                >
                  <button
                    type="button"
                    style={carouselButtonStyle}
                    onClick={handleCarouselPrev}
                    onMouseEnter={handleNavHover}
                    onFocus={handleNavHover}
                    onMouseLeave={handleNavLeave}
                    onBlur={handleNavLeave}
                    disabled={tileSongs.length <= 1}
                    aria-label="Previous song tile"
                  >
                    ⬅
                  </button>
                    <div
                      className="tile carousel-tile"
                      style={{
                        ...tileItemStyle,
                        fontSize: computeTileFontSize(tileSongs[carouselIndex]?.title),
                        lineHeight: isMobileViewport ? 1.3 : 1.18,
                        color: activeTheme.text,
                        position: "relative",
                        backgroundColor:
                          isCustomCategory &&
                          tileSongs[carouselIndex] &&
                          customSelectedSet.has(slugify(tileSongs[carouselIndex].title))
                            ? hexToRgbaString(activeTheme.text, 0.22)
                            : tileItemStyle.backgroundColor,
                      }}
                    onClick={() => {
                      const targetSong = tileSongs[carouselIndex];
                      if (!targetSong) return;
                      if (isCustomCategory) {
                        toggleCustomSongSelection(targetSong);
                        return;
                      }
                      loadSong(carouselIndex);
                    }}
                  >
                    <span
                      style={{
                        padding: "0 12px",
                        display: "block",
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                        textAlign: "center",
                      }}
                    >
                      {tileSongs[carouselIndex]?.title || ""}
                    </span>
                    {isCustomCategory && tileSongs[carouselIndex] && (() => {
                      const isSelected = customSelectedSet.has(
                        slugify(tileSongs[carouselIndex].title),
                      );
                      return (
                        <span
                          style={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            width: 20,
                            height: 20,
                            borderRadius: 6,
                            border: `1px solid ${hexToRgbaString(activeTheme.text, 0.6)}`,
                            backgroundColor: isSelected
                              ? hexToRgbaString(activeTheme.text, 0.85)
                              : "transparent",
                            color: isSelected
                              ? activeTheme.background
                              : hexToRgbaString(activeTheme.text, 0.7),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                          aria-hidden="true"
                        >
                          {isSelected ? "✓" : ""}
                        </span>
                      );
                    })()}
                  </div>
                  <button
                    type="button"
                    style={carouselButtonStyle}
                    onClick={handleCarouselNext}
                    onMouseEnter={handleNavHover}
                    onFocus={handleNavHover}
                    onMouseLeave={handleNavLeave}
                    onBlur={handleNavLeave}
                    disabled={tileSongs.length <= 1}
                    aria-label="Next song tile"
                  >
                    ➡
                  </button>
                </div>
                <div style={carouselIndicatorStyle}>
                  {tileSongs.length ? `${carouselIndex + 1} / ${tileSongs.length}` : ""}
                </div>
              </>
            ) : (
              <div style={carouselEmptyStyle}>No songs available.</div>
            )
          ) : (
            tileSongs.map((song, idx) => (
              <div
                key={song.title + idx}
                className="tile"
                style={{
                  ...tileItemStyle,
                  fontSize: uniformTileFontSize || computeTileFontSize(song.title),
                  lineHeight: isMobileViewport ? 1.3 : 1.18,
                  color: activeTheme.text,
                  position: "relative",
                  backgroundColor:
                    isCustomCategory && customSelectedSet.has(slugify(song.title))
                      ? hexToRgbaString(activeTheme.text, 0.22)
                      : tileItemStyle.backgroundColor,
                  border:
                    isCustomCategory && customSelectedSet.has(slugify(song.title))
                      ? `2px solid ${hexToRgbaString(activeTheme.text, 0.75)}`
                      : tileItemStyle.border,
                }}
                onClick={() => {
                  if (isCustomCategory) {
                    toggleCustomSongSelection(song);
                    return;
                  }
                  loadSong(idx);
                }}
              >
                <span
                  style={{
                    padding: "0 10px",
                    display: "block",
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                >
                  {song.title}
                </span>
                {isCustomCategory && (() => {
                  const isSelected = customSelectedSet.has(slugify(song.title));
                  return (
                    <span
                      style={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        width: 20,
                        height: 20,
                        borderRadius: 6,
                        border: `1px solid ${hexToRgbaString(activeTheme.text, 0.6)}`,
                        backgroundColor: isSelected
                          ? hexToRgbaString(activeTheme.text, 0.85)
                          : "transparent",
                        color: isSelected
                          ? activeTheme.background
                          : hexToRgbaString(activeTheme.text, 0.7),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                      aria-hidden="true"
                    >
                      {isSelected ? "✓" : ""}
                    </span>
                  );
                })()}
              </div>
            ))
          )}
        </div>
        {showTiles && (
          <button
            type="button"
            aria-label="Open on-demand request popup"
            style={floatingOnDemandButtonStyle}
            onClick={() => {
              if (!onDemandPopupOpen) {
                setOnDemandPopupOpen(true);
              }
            }}
          >
            +
          </button>
        )}

        {onDemandPopupOpen && (
          <div
            style={onDemandPopupOverlayStyle}
            role="dialog"
            aria-modal="true"
            aria-label="Instant slides input"
            onClick={() => {
              setOnDemandPopupOpen(false);
            }}
          >
            <div
              style={onDemandPopupContentStyle}
              onClick={(event) => event.stopPropagation()}
            >
              <div style={onDemandPopupHeaderStyle}>
                <span style={{ fontSize: 20, fontWeight: 600 }}>Instant slides</span>
                <button
                  type="button"
                  style={onDemandPopupCloseStyle}
                  onClick={() => setOnDemandPopupOpen(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "stretch",
                }}
              >
                <textarea
                  id="on-demand-input"
                  ref={onDemandInputRef}
                  value={onDemandText}
                  onChange={(event) => setOnDemandText(event.target.value)}
                  onKeyDown={async (event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      const success = handleOnDemandSubmit();
                      const completed = typeof success?.then === "function" ? await success : success;
                      if (completed) {
                        setOnDemandPopupOpen(false);
                      }
                    } else if (event.key === "Escape") {
                      setOnDemandPopupOpen(false);
                      setOnDemandError("");
                    }
                  }}
                  placeholder="Lyrics or worship URL"
                  style={onDemandTextareaStyle}
                />
                <button
                  type="button"
                  style={onDemandSubmitButtonStyle}
                  disabled={onDemandLoading}
                  onClick={async () => {
                    const success = handleOnDemandSubmit();
                    const completed = typeof success?.then === "function" ? await success : success;
                    if (completed) {
                      setOnDemandPopupOpen(false);
                    }
                  }}
                  aria-label="Show slides"
                >
                  {onDemandLoading ? <span aria-hidden="true">⏳</span> : <span aria-hidden="true">🎬</span>}
                </button>
              </div>
              {onDemandError && (
                <span style={onDemandErrorStyle} role="alert">
                  {onDemandError}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Lyrics Box */}
        <div id="lyrics-box" ref={slideTouchTargetRef} style={lyricsBoxStyle}>
          {(displayMode === "slide" || isCameraSlidesMode(displayMode)) && (
            <div
              id="lyrics"
              ref={slideLyricsRef}
                tabIndex={0}
                className={
                  isTitleSlide()
                    ? "title-slide"
                    : currentSongIsEnglish
                    ? "english-lyrics"
                    : "non-english-lyrics"
                }
                style={{
                  fontSize: isMobileViewport ? responsiveSlideFontSize : globalFontSize,
                  display: "flex",
                  flexDirection: "column",
                  color: activeTheme.text,
                  gap: isMobileViewport ? (cameraSlidesActive ? 6 : 8) : cameraSlidesActive ? 10 : 12,
                  alignItems: "center",
                  textAlign: "center",
                  fontFamily: activeFont.value,
                  fontWeight: lyricsBold ? 700 : 400,
                  textShadow: cameraSlidesActive ? "0 4px 18px rgba(0, 0, 0, 0.85)" : "none",
                }}
              dangerouslySetInnerHTML={{
                __html: isTitleSlide()
                  ? formatLyricsHtml(getSlideContent())
                  : getSlideContent().toLowerCase().includes("<img")
                  ? getSlideContent()
                  : formatLyricsHtml(getSlideContent()),
              }}
            />
          )}
          {isCameraPresentMode(displayMode) && currentSong && (
            <div style={cameraPresentWrapperStyle} aria-live="polite">
              <div style={cameraPresentCurrentStyle}>
                {presenterMode.current.toLowerCase().includes("<img") ? (
                  <div dangerouslySetInnerHTML={{ __html: presenterMode.current }} />
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: formatLyricsHtml(presenterMode.current) }} />
                )}
              </div>
              <div style={cameraPresentNextStyle}>
                {presenterMode.next.toLowerCase().includes("<img") ? (
                  <div dangerouslySetInnerHTML={{ __html: presenterMode.next }} />
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: formatLyricsHtml(presenterMode.next) }} />
                )}
              </div>
            </div>
          )}
          {displayMode === "book" && currentSong && (
            <div
              className="book-view"
              style={{ ...bookViewStyle, fontFamily: activeFont.value }}
              role="region"
              aria-label="Book view"
            >
              {bookPageHtmls.map((pageHtml, index) => {
                const pageNumber = bookStartIndex + index + 1;
                return (
                  <div key={pageNumber} className="book-page" style={bookPageStyle}>
                    <div className="book-page-content" style={bookPageContentStyle}>
                      <div dangerouslySetInnerHTML={{ __html: pageHtml }} />
                      <span style={bookPageNumberStyle}>{pageNumber}/{bookPages.length}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {displayMode === "entire" && currentSong && (
            <div className="entire-wrapper">
              <div
                id="lyrics"
                ref={entireContentRef}
                tabIndex={0}
              className="entire-song"
              style={{
                fontSize: entireFontSize,
                columnCount: renderedEntireColumns,
                columnGap: renderedEntireColumns > 1 ? flowColumnGap : 0,
                columnWidth: entireColumnsLocked ? "auto" : `${MIN_FLOW_COLUMN_WIDTH}px`,
                columnFill: "balance",
                overflowY: "auto",
                overflowX: "hidden",
                maxHeight: "100%",
                height: "100%",
                color: activeTheme.text,
                fontFamily: activeFont.value,
                fontWeight: lyricsBold ? 600 : 400,
              }}
            >
                {entireContentBlocks.map((block, i) =>
                  block.toLowerCase().includes("<img") ? (
                    <div key={i} className="entire-song-block" dangerouslySetInnerHTML={{ __html: block }} />
                  ) : (
                    <div
                      key={i}
                      className="entire-song-block"
                      dangerouslySetInnerHTML={{ __html: block.replace(/\n/g, "<br>") }}
                    />
                  )
                )}
              </div>
            </div>
          )}
          {displayMode === 'newsletter' && currentSong && (
            <div className="newsletter-wrapper" style={{
              padding: isMobileViewport ? '1rem' : '2rem',
              paddingTop: isMobileViewport ? mobileTopOffset : undefined,
              width: '100%',
              maxWidth: '100%',
              margin: 0,
            }}>
            <div
              className="newsletter-content"
              ref={newsletterContentRef}
              style={{
                  columnCount: newsletterColumnCount,
                  columnGap: newsletterColumnCount > 1 ? `${flowColumnGap}px` : '1rem',
                  columnWidth: entireColumnsLocked ? "auto" : `${MIN_FLOW_COLUMN_WIDTH}px`,
                  columnFill: 'balance',
                  fontSize: newsletterFontSize,
                  textAlign: 'left',
                  maxHeight: '100%',
                  height: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
              }}
            >
                {currentSong.lyrics.map((p, i) =>
                  p.toLowerCase().includes("<img") ? (
                    <div key={i} className="newsletter-block" dangerouslySetInnerHTML={{ __html: p }} />
                  ) : (
                    <p
                      key={i}
                      className="newsletter-block"
                      dangerouslySetInnerHTML={{ __html: p.replace(/\n/g, "<br>") }}
                    />
                  )
                )}
              </div>
            </div>
          )}
          {!!watermarkText && (
            <div
              id="watermark"
              style={{
                color: activeTheme.text,
                opacity: 0.12,
                fontSize: isMobileViewport ? "clamp(12px, 3vw, 18px)" : "clamp(18px, 2.2vw, 24px)",
                ...(displayMode === "presenter"
                  ? { top: "auto", bottom: "8%", transform: "none" }
                  : {}),
              }}
            >
              <span>{watermarkText}</span>
            </div>
          )}
        </div>

        {/* Presenter Mode */}
        <div
          id="presenter-mode-container"
          ref={presenterTouchTargetRef}
          style={{
            display: displayMode === "presenter" && currentSong ? "block" : "none",
            width: "100%",
            height: "calc(100vh - var(--site-header-height, 72px) - 8px)",
            padding: isTabletOrBelow ? 12 : 18,
            paddingTop: isTabletOrBelow ? 8 : 12,
            paddingBottom: isTabletOrBelow ? 6 : 10,
            fontFamily: activeFont.value,
            fontWeight: lyricsBold ? 600 : 400,
            color: activeTheme.text,
            position: "relative",
            textAlign: "center",
            overflow: "hidden",
          }}
        >
          <div
            id="presenter-current"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "100%",
              display: "block",
              whiteSpace: "pre-wrap",
              padding: 0,
              boxSizing: "border-box",
              fontSize: globalFontSize,
              color: activeTheme.text,
              fontFamily: activeFont.value,
              fontWeight: lyricsBold ? 700 : 400,
              textAlign: "center",
              zIndex: 1,
            }}
          >
            {presenterMode.current.toLowerCase().includes("<img") ? (
              <div
                style={{ width: "100%", textAlign: "center" }}
                dangerouslySetInnerHTML={{ __html: presenterMode.current }}
              />
            ) : (
              <div
                style={{ width: "100%", textAlign: "center" }}
                dangerouslySetInnerHTML={{ __html: formatLyricsHtml(presenterMode.current) }}
              />
            )}
          </div>
          <div
            id="presenter-next"
            style={{
              position: "absolute",
              left: "50%",
              bottom: isTabletOrBelow ? 6 : 10,
              transform: "translateX(-50%)",
              width: "100%",
              display: "block",
              color: activeTheme.text,
              opacity: 0.65,
              whiteSpace: "pre-wrap",
              padding: 0,
              boxSizing: "border-box",
              fontSize: Math.floor(globalFontSize * 0.5),
              fontFamily: activeFont.value,
              fontWeight: lyricsBold ? 600 : 400,
              textAlign: "center",
              zIndex: 1,
            }}
          >
            {presenterMode.next.toLowerCase().includes("<img") ? (
              <div
                style={{ width: "100%", textAlign: "center" }}
                dangerouslySetInnerHTML={{ __html: presenterMode.next }}
              />
            ) : (
              <div
                style={{ width: "100%", textAlign: "center" }}
                dangerouslySetInnerHTML={{ __html: formatLyricsHtml(presenterMode.next) }}
              />
            )}
          </div>
          {!!watermarkText && (
            <div
              id="watermark"
              style={{
                position: "absolute",
                right: "clamp(8%, 15vw, 18%)",
                bottom: "10%",
                color: activeTheme.text,
                opacity: 0.12,
                fontSize: isMobileViewport ? "clamp(12px, 3vw, 18px)" : "clamp(18px, 2.2vw, 24px)",
                pointerEvents: "none",
                userSelect: "none",
                zIndex: 0,
                textAlign: "right",
              }}
            >
              <span>{watermarkText}</span>
            </div>
          )}
        </div>

        <div id="hidden-audio-player"></div>

        {/* Video Mode */}
        <div id="video-container" style={videoContainerStyle}>
        {(() => {
          const vidID = extractYouTubeID(selectedYouTubeLink);
          if (selectedYouTubeLink && vidID) {
            return (
              <>
                  <div id="video-mode-player" style={videoPlayerStyle}></div>
                  {cameraVideoActive && (
                    <>
                      <div style={videoMoveHandleStyle} onPointerDown={beginDrag("move")}>
                        O
                      </div>
                      <div style={videoResizeHandleStyle} onPointerDown={beginDrag("resize-se")} />
                    </>
                  )}
                  {cameraVideoActive &&
                    edgeHandles.map((handle) => (
                      <div
                        key={handle.mode}
                        style={{
                          position: "absolute",
                          ...handle.style,
                          backgroundColor: "rgba(255,255,255,0.08)",
                          border: "1px solid rgba(255,255,255,0.35)",
                          pointerEvents: "auto",
                        }}
                        onPointerDown={beginDrag(handle.mode)}
                        role="presentation"
                        aria-hidden="true"
                      />
                    ))}
                  {displayMode === "videoslide" && (
                    <div
                      className="video-slide-overlay"
                      ref={videoSlideTouchTargetRef}
                      onClick={handleVideoSlideClick}
                      role="presentation"
                    >
                      <div className="video-slide-overlay-spacer" />
                      <div className="video-slide-lyrics" aria-live="polite">
                        <div
                          className={`video-slide-lyrics-text ${
                            isTitleSlide()
                              ? "title-slide"
                              : currentSongIsEnglish
                              ? "english-lyrics"
                              : "non-english-lyrics"
                          }`}
                          ref={videoSlideLyricsRef}
                          style={{
                            color: activeTheme.text,
                            fontSize: isMobileViewport ? responsiveSlideFontSize : globalFontSize,
                            fontFamily: activeFont.value,
                            fontWeight: lyricsBold ? 700 : 400,
                            textShadow: "0 4px 16px rgba(0, 0, 0, 0.6)",
                          }}
                          dangerouslySetInnerHTML={{
                            __html: isTitleSlide()
                              ? formatLyricsHtml(getSlideContent())
                              : getSlideContent().toLowerCase().includes("<img")
                              ? getSlideContent()
                              : formatLyricsHtml(getSlideContent()),
                          }}
                        />
                      </div>
                    </div>
                  )}
                </>
              );
            } else {
              return (
                <div style={{ color: activeTheme.text, textAlign: "center", padding: 20 }}>
                  <h2>No YouTube link found for this song.</h2>
                  <p>Select another mode or a different song.</p>
                </div>
              );
            }
          })()}
        </div>

        {/* Music Mode */}        <div
          id="music-container"
          style={{
            display: displayMode === "music" && currentSong ? "flex" : "none",
            fontFamily: activeFont.value,
            fontWeight: lyricsBold ? 600 : 400,
            color: activeTheme.text,
          }}
        >
          <div id="music-embed-left">
            {musescoreLink ? (
              <iframe
                className="music-embed"
                src={extractMusescoreEmbedURL(musescoreLink) || ""}
                frameBorder="0"
                allowFullScreen
                allow="autoplay; fullscreen"
                title="Musescore Left"
              />
            ) : (
              <p>No valid Musescore content found.</p>
            )}
          </div>
            <div
              id="music-content-right"
              style={{
                fontSize: musicFontSize,
                color: activeTheme.text,
                backgroundColor: tileBackgroundColor,
                backgroundImage: activeTheme.gradient || "none",
                border: `1px solid ${tileBorderColor}`,
                fontFamily: activeFont.value,
                fontWeight: lyricsBold ? 600 : 400,
                columnCount: Math.max(1, renderedEntireColumns),
                columnGap: renderedEntireColumns > 1 ? 24 : 12,
              }}
            >
            {showNextSongPreview ? (
              <>
                <h3>{filteredSongs[currentSongIndex + 1].title}</h3>
                <p
                  dangerouslySetInnerHTML={{
                    __html: filteredSongs[currentSongIndex + 1].lyrics.join("<br>"),
                  }}
                />
              </>
            ) : (
              <p>No additional content available.</p>
            )}
          </div>
        </div>

        {/* Unused Music Navigation Buttons */}        <button id="music-prev-btn" className="music-nav-button" style={{ display: "none" }}>
          ◀
        </button>
        <button id="music-next-btn" className="music-nav-button" style={{ display: "none" }}>
          ▶
        </button>

        {typeAheadSearching && searchKeyword && (
          <div style={typeAheadIndicatorStyle} aria-live="polite" aria-atomic="true">
            {searchKeyword}
          </div>
        )}

        {navBarShouldRender && (
          <div style={navButtonBarStyle} className="nav-button-bar">            <div style={navButtonGroupStyle} className="nav-button-group">
              {navSupportsSlides && (
                <button
                  id="slide-prev-song-btn"
                  className="slide-nav-button"
                  style={navButtonBaseStyle}
                  onClick={prevSong}
                  onFocus={handleNavHover}
                  onMouseEnter={handleNavHover}
                  onMouseLeave={handleNavLeave}
                  onBlur={handleNavLeave}
                >
                  <FaStepBackward />
                </button>
              )}
              {navSupportsSlides && (
                <button
                  id="slide-prev-btn"
                  className="slide-nav-button"
                  style={navButtonBaseStyle}
                  onClick={prevContent}
                  onFocus={handleNavHover}
                  onMouseEnter={handleNavHover}
                  onMouseLeave={handleNavLeave}
                  onBlur={handleNavLeave}
                >
                  <FaArrowLeft />
                </button>
              )}
              {playButtonVisible && (
                <button
                  id="slide-bottom-play-btn"
                  style={{
                    ...navButtonBaseStyle,
                  }}
                  onClick={togglePlayPause}
                  onFocus={handleNavHover}
                  onMouseEnter={handleNavHover}
                  onMouseLeave={handleNavLeave}
                  onBlur={handleNavLeave}
                >
                  {isYouTubePlaying ? <FaPause /> : <FaPlay />}
                </button>
              )}
              {navSupportsSlides && (
                <button
                  id="slide-next-btn"
                  className="slide-nav-button"
                  style={navButtonBaseStyle}
                  onClick={nextContent}
                  onFocus={handleNavHover}
                  onMouseEnter={handleNavHover}
                  onMouseLeave={handleNavLeave}
                  onBlur={handleNavLeave}
                >
                  <FaArrowRight />
                </button>
              )}
              {navSupportsSlides && (
                <button
                  id="slide-next-song-btn"
                  className="slide-nav-button"
                  style={navButtonBaseStyle}
                  onClick={nextSong}
                  onFocus={handleNavHover}
                  onMouseEnter={handleNavHover}
                  onMouseLeave={handleNavLeave}
                  onBlur={handleNavLeave}
                >
                  <FaStepForward />
                </button>
              )}
              <button
                type="button"
                aria-label="Hide bottom controls"
                title="Hide bottom controls"
                style={{
                  ...navButtonBaseStyle,
                }}
                onClick={() => setBottomNavHidden(true)}
              >
                <FaTimes />
              </button>
            </div>
            {currentSong && (
              <div style={navTileButtonGroupStyle}>
                <button
                  type="button"
                  aria-label="Back to song tiles"
                  title="Back to song tiles"
                  style={navButtonBaseStyle}
                  onClick={restoreToTiles}
                  onFocus={handleNavHover}
                  onMouseEnter={handleNavHover}
                  onMouseLeave={handleNavLeave}
                  onBlur={handleNavLeave}
                >
                  <FaUndoAlt />
                </button>
                <button
                  type="button"
                  aria-pressed={tilesPanelOpen}
                  aria-label="Show tiles panel"
                  style={tilesPanelToggleButtonStyle}
                  onClick={() => setTilesPanelOpen((prev) => !prev)}
                >
                  <FaTh style={tilesPanelButtonIconStyle} />
                </button>
              </div>
            )}
            <div style={navSettingsRowStyle} className="nav-settings-row">
              <label style={navControlPillStyle} aria-label="Font size">
                F
                <input
                  type="range"
                  min="10"
                  max="150"
                  value={globalFontSize}
                  onChange={(e) => setGlobalFontSize(parseInt(e.target.value, 10))}
                  style={navLineSliderStyle}
                />
                <span>{globalFontSize}</span>
              </label>
              <label style={navControlPillStyle} aria-label="Lines per slide">
                L
                <input
                  type="range"
                  min="0"
                  max="25"
                  value={linesPerSlide}
                  onChange={(e) => setLinesPerSlide(parseInt(e.target.value, 10))}
                  style={navLineSliderStyle}
                />
                <span>{linesPerSlide}</span>
              </label>
              {showAudioSlider && (
                <label style={navControlPillStyle} aria-label="Song duration">
                  D
                  {(() => {
                    const sliderMax = Math.max(audioDuration || 0, audioProgress, 1);
                    const sliderValue = Math.min(audioProgress, sliderMax);
                    return (
                      <input
                        type="range"
                        id="audio-progress-input"
                        min="0"
                        max={sliderMax}
                        value={sliderValue}
                        step="0.1"
                        style={navLineSliderStyle}
                        onInput={(e) => {
                          const value = parseFloat(e.target.value);
                          if (Number.isFinite(value)) {
                            sliderValueRef.current = value;
                            setAudioProgress(value);
                          }
                        }}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          isUserSeekingRef.current = false;
                          handleAudioSeek(value);
                        }}
                        onMouseDown={() => {
                          isUserSeekingRef.current = true;
                        }}
                        onTouchStart={() => {
                          isUserSeekingRef.current = true;
                        }}
                        onMouseUp={() => {
                          if (isUserSeekingRef.current) {
                            isUserSeekingRef.current = false;
                            handleAudioSeek(sliderValueRef.current);
                          }
                        }}
                        onTouchEnd={() => {
                          if (isUserSeekingRef.current) {
                            isUserSeekingRef.current = false;
                            handleAudioSeek(sliderValueRef.current);
                          }
                        }}
                      />
                    );
                  })()}
                  <span style={navSliderTimeStyle}>
                    {formatTime(audioProgress)} / {formatTime(audioDuration)}
                  </span>
                </label>
              )}
            </div>
          </div>
        )}
        {tilesPanelOpen && currentSong && (
          <>
            {!tilesPanelPinned && (
              <div
                style={tilesPanelBackdropStyle}
                onClick={closeTilesPanel}
                aria-hidden="true"
              />
            )}
            <section
              style={tilesPanelStyle}
              aria-label="Song tiles"
              role="dialog"
              aria-modal="true"
            >
              {!tilesPanelMinimized && !isMobileViewport && (
                <div
                  style={tilesPanelResizeHandleStyle}
                  onMouseDown={handleTilesPanelResizeStart}
                  aria-hidden="true"
                />
              )}
              <div style={tilesPanelHeaderWrapperStyle}>
                <div style={tilesPanelHeaderStyle}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button
                      type="button"
                      style={{
                        padding: "4px 10px",
                        borderRadius: 999,
                        border: `1px solid ${tileBorderColor}`,
                        backgroundColor: hexToRgbaString(activeTheme.text, 0.08),
                        color: activeTheme.text,
                        fontSize: 11,
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                        cursor: "pointer",
                      }}
                      onClick={() => setPanelShowAll((prev) => !prev)}
                      aria-pressed={panelShowAll}
                      title={panelShowAll ? "Show selected list" : "Show all songs"}
                    >
                      {panelShowAll ? "Selected" : "All"}
                    </button>
                    <button
                      type="button"
                      style={tilesPanelPinButtonStyle}
                      aria-pressed={tilesPanelPinned}
                      aria-label={tilesPanelPinned ? "Unpin panel" : "Pin panel"}
                      onClick={() => setTilesPanelPinned((prev) => !prev)}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="18"
                        height="18"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M8 3l4 4 4-4"></path>
                        <path d="M12 7v13"></path>
                        <path d="M8 14h8"></path>
                      </svg>
                    </button>
                    <button
                      type="button"
                      style={tilesPanelMinimizeButtonStyle}
                      aria-pressed={tilesPanelMinimized}
                      aria-label={tilesPanelMinimized ? "Restore panel" : "Minimize panel"}
                      onClick={() => setTilesPanelMinimized((prev) => !prev)}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="18"
                        height="18"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <rect x="4" y="6" width="16" height="12" rx="2"></rect>
                        <line x1="8" y1="10" x2="16" y2="10"></line>
                      </svg>
                    </button>
                    <button
                      type="button"
                      style={tilesPanelCloseButtonStyle}
                      onClick={closeTilesPanel}
                      aria-label="Close tiles panel"
                    >
                      ×
                    </button>
                  </div>
                </div>
                {!tilesPanelMinimized && (
                  <>
                    <input
                      type="text"
                      placeholder="Search songs..."
                      value={tilesPanelSearch}
                      onChange={(event) => setTilesPanelSearch(event.target.value)}
                      style={tilesPanelSearchInputStyle}
                    />
                    <div style={{ fontSize: 12, color: hexToRgbaString(activeTheme.text, 0.7) }}>
                      {isCustomCategory
                        ? `Showing ${panelShowAll ? "all songs" : "selected songs"} — use the All/Selected toggle to add more.`
                        : `Showing ${panelShowAll ? "all songs" : "category songs"} — use the All/Selected toggle to switch.`}
                    </div>
                  </>
                )}
              </div>
              {tilesPanelMinimized ? (
                <div style={tilesPanelListWrapperStyle}>
                  <div style={tilesPanelMinimizedGridStyle}>
                    {tilesPanelMinimizedTiles.length ? (
                      tilesPanelMinimizedTiles.map(({ letter, idx }) => (
                        <button
                          key={`${letter}-${idx}`}
                          type="button"
                          style={{
                            ...tilesPanelMinimizedLetterStyle,
                            ...(currentSong &&
                            slugify(currentSong.title) ===
                              slugify(tilesPanelSourceSongs[idx]?.title)
                              ? tilesPanelActiveLetterStyle
                              : {}),
                          }}
                          onClick={() => {
                            const targetSong = tilesPanelSourceSongs[idx];
                            if (!targetSong) return;
                            if (isCustomCategory) {
                              toggleCustomSongSelection(targetSong);
                              return;
                            }
                            loadSongFromPanel(targetSong);
                          }}
                        >
                          {letter}
                        </button>
                      ))
                    ) : (
                      <div style={tilesPanelEmptyStateStyle}>
                        {tilesPanelSearch.trim()
                          ? `No songs match "${tilesPanelSearch.trim()}"`
                          : "No songs available."}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={tilesPanelListWrapperStyle}>
                  <div style={tilesPanelListStyle}>
                    {tilesPanelFilteredSongs.length ? (
                      tilesPanelFilteredSongs.map(({ song, idx }) => {
                        const isActiveSong =
                          currentSong && slugify(currentSong.title) === slugify(song.title);
                          const isSelected = customSelectedSet.has(slugify(song.title));
                          return (
                        <button
                          key={`${song.title}-${idx}`}
                          type="button"
                          style={{
                            ...tilesPanelSongButtonStyle,
                            ...(isActiveSong ? tilesPanelActiveSongStyle : {}),
                            ...(isCustomCategory && isSelected
                              ? {
                                  border: `2px solid ${hexToRgbaString(activeTheme.text, 0.75)}`,
                                  backgroundColor: hexToRgbaString(activeTheme.text, 0.2),
                                }
                              : {}),
                          }}
                          onClick={() => {
                            if (isCustomCategory) {
                              toggleCustomSongSelection(song);
                              return;
                            }
                            loadSongFromPanel(song);
                            if (!tilesPanelMinimized && !tilesPanelPinned) {
                              setTilesPanelOpen(false);
                            }
                          }}
                        >
                          <span style={{ flex: 1 }}>{song.title}</span>
                          {isCustomCategory && (
                            <span
                              style={{
                                width: 18,
                                height: 18,
                                borderRadius: 5,
                                border: `1px solid ${hexToRgbaString(activeTheme.text, 0.6)}`,
                                backgroundColor: isSelected
                                  ? hexToRgbaString(activeTheme.text, 0.85)
                                  : "transparent",
                                color: isSelected
                                  ? activeTheme.background
                                  : hexToRgbaString(activeTheme.text, 0.7),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 11,
                                fontWeight: 700,
                              }}
                              aria-hidden="true"
                            >
                              {isSelected ? "✓" : ""}
                            </span>
                          )}
                        </button>
                      );
                      })
                    ) : (
                      <div style={tilesPanelEmptyStateStyle}>
                        {tilesPanelSearch.trim()
                          ? `No songs match "${tilesPanelSearch.trim()}"`
                          : "No songs available."}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {/* Help Modal */}
      <div id="help-modal" className="modal" style={{ display: helpModalOpen ? "block" : "none" }}>
        <div className="modal-content">
          <span className="close" onClick={() => setHelpModalOpen(false)}>
            &times;
          </span>
          <h2>Keyboard Shortcuts</h2>
          <ul className="shortcuts-list">
            <li>
              <span className="shortcut-key">Arrow Right / Page Down</span>: Next Content
            </li>
            <li>
              <span className="shortcut-key">Arrow Left / Page Up</span>: Previous Content
            </li>
            <li>
              <span className="shortcut-key">Arrow Up</span>: Previous Song
            </li>
            <li>
              <span className="shortcut-key">Arrow Down</span>: Next Song
            </li>
            <li>
              <span className="shortcut-key">Space</span>: Play/Pause
            </li>
            <li>
              <span className="shortcut-key">G</span>: Show All Tiles (Back)
            </li>
            <li>
              <span className="shortcut-key">Control / h</span>: Hide/Show Controls
            </li>
            <li>
              <span className="shortcut-key">C</span>: Hide/Show Controls
            </li>
            <li>
              <span className="shortcut-key">&gt; / .</span>: Increase Playback Rate
            </li>
            <li>
              <span className="shortcut-key">&lt; / ,</span>: Decrease Playback Rate
            </li>
            <li>
              <span className="shortcut-key">1-9</span>: Select YouTube Link
            </li>
            <li>
              <span className="shortcut-key">S</span>: Slide Display Mode
            </li>
            <li>
              <span className="shortcut-key">K</span>: Camera + Slides Display Mode
            </li>
            <li>
              <span className="shortcut-key">R</span>: Camera + Present Display Mode
            </li>
            <li>
              <span className="shortcut-key">J</span>: Camera + Video Display Mode
            </li>
            <li>
              <span className="shortcut-key">F</span>: Entire/Full Display Mode
            </li>
            <li>
              <span className="shortcut-key">V</span>: Video Display Mode
            </li>
            <li>
              <span className="shortcut-key">M</span>: Music Display Mode
            </li>
            <li>
              <span className="shortcut-key">P</span>: Presenter Display Mode
            </li>
            <li>
              <span className="shortcut-key">N</span>: Newsletter Display Mode
            </li>
            <li>
              <span className="shortcut-key">B</span>: Book Display Mode
            </li>
            <li>
              <span className="shortcut-key">= / +</span>: Increase Font Size
            </li>
            <li>
              <span className="shortcut-key">- / _</span>: Decrease Font Size
            </li>
            <li>
              <span className="shortcut-key">{`] / }`}</span>: Increase Lines (Slide/Presenter) or Columns (Full/Newsletter/Music/Book)
            </li>
            <li>
              <span className="shortcut-key">{`[ / {`}</span>: Decrease Lines (Slide/Presenter) or Columns (Full/Newsletter/Music/Book)
            </li>
            <li>
              <span className="shortcut-key">L</span>: Toggle Bold Lyrics
            </li>
            <li>
              <span className="shortcut-key">Y</span>: Copy Share Link
            </li>
            <li>
              <span className="shortcut-key">?</span>: Show Keyboard Shortcuts
            </li>
            <li>
              <span className="shortcut-key">*</span>: Increase Auto-Advance Seconds
            </li>
            <li>
              <span className="shortcut-key">/</span>: Decrease Auto-Advance Seconds
            </li>
            <li>
              <span className="shortcut-key">Shift + Tab</span>: Focus Controls
            </li>
            <li>
              <span className="shortcut-key">T</span>: Focus on Display Mode Dropdown
            </li>
            <li>
              <span className="shortcut-key">O</span>: Focus on Catalog Dropdown
            </li>
            <li>
              <span className="shortcut-key">I</span>: Focus on Song Dropdown
            </li>
            <li>
              <span className="shortcut-key">E</span>: Focus on Search Box
            </li>
            <li>
              <span className="shortcut-key">D</span>: Focus on Slide Content
            </li>
            <li>
              <span className="shortcut-key">Alt / Option</span>: Toggle Song Panel
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default LyricsSlideshow;
